/* Venerabull — the app.

   No dependencies, no build step, one file. It reads the SAME catalogue the
   site reads, so a model cannot exist in one and be missing from the other,
   and the SAME measurement parser, so a figure typed here is understood
   exactly as it is understood there.

   ── HOW IT IS PUT TOGETHER ───────────────────────────────────────────────
   Screens are real stacked layers with push and pop transitions, not innerHTML
   swaps, because the thing that makes an app feel like an app is that you can
   see where you came from. The stack mirrors history, so the Android back
   button and the iOS back-swipe both do the right thing without a library.

   ── WHAT IS DELIBERATELY LEFT OUT ────────────────────────────────────────
   The catalogue carries a star rating, a review count, a stock state and a
   lead time on every model. None of those are true — they are part of the
   presentation set, and the supplier ledger forbids quoting a rating, a
   customer count or a delivery window until it is real. The site still shows
   them and that is flagged; the app does not carry them in. Building the
   honest version now is cheaper than unwinding it later. */

(function (root, doc) {
  'use strict';

  var A = {}, CAT = null, BYID = {}, el = {}, stack = [], busy = false;

  /* ── helpers ─────────────────────────────────────────────────────────── */
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function money(n) {
    return (n == null || isNaN(n)) ? '' : '$' + Number(n).toLocaleString('en-US');
  }
  function asset(name) {
    if (!name) { return ''; }
    var kept = SHOT['img:' + name];
    if (kept) { return kept; }
    var b = (root.VB_APP_CFG && root.VB_APP_CFG.assetBase) || '';
    return b ? b.replace('__FILE__', encodeURIComponent(name)) : name;
  }
  function feet(inch) {
    var f = Math.floor(inch / 12), i = Math.round(inch - f * 12);
    return f + ' ft' + (i ? ' ' + i + ' in' : '');
  }
  function cfg(k) { return (root.VB_APP_CFG || {})[k]; }
  /* Only three of the four values change anything: a consumer and a signed-out
     visitor both get the original register, which is the site's rule. */
  /* Her name is declared in exactly one file in the theme —
     snippets/vb-assistant-config.liquid — and reaches the app through the
     mount like everything else. Nothing here spells it, so renaming her, or
     giving Venerabull a second named voice later, stays an edit to that one
     file. scripts/test/theme-run.js enforces it. */
  function asstName() { return cfg('asst') || 'the assistant'; }

  function acct() {
    var a = cfg('account');
    return (a === 'trade' || a === 'supplier') ? a : 'consumer';
  }

  /* ── the store, on this device ───────────────────────────────────────── */
  var KEY = 'vb.app.v1';
  /* 30 Oct 2026, his direction: a saved value this version cannot read is not
     an empty one. store() used to read an unparseable value, or a storage read
     that threw, as {}, and the next put() wrote that empty document over the
     original: the rooms and shelf were gone. Now:
     - an absent key is a first visit: {} and ordinary saving, as before;
     - a present value that is not a JSON object (malformed, an array, a bare
       value, empty), or a read that throws, HOLDs the page: it works on MEM,
       a document for this page only, and nothing is written over the value;
     - a write that throws holds it too ('unsaved'): what was saved before is
       left as it was, and no later change is claimed as saved.
     The page says so (heldSync). Only the explicit Erase ends a hold, and
     only when the removal itself succeeds (eraseSaved). */
  var HOLD = '', MEM = null;
  function readSaved() {
    var raw, docv;
    try { raw = localStorage.getItem(KEY); } catch (e) { return { kind: 'blocked' }; }
    if (raw == null) { return { kind: 'none' }; }
    try { docv = JSON.parse(raw); } catch (e) { return { kind: 'unreadable' }; }
    if (!docv || typeof docv !== 'object' || Array.isArray(docv)) { return { kind: 'unreadable' }; }
    if (!shapeOk(docv)) { return { kind: 'unreadable' }; }
    return { kind: 'ok', doc: docv };
  }
  /* The fields this version consumes, in the shapes it consumes them (30 Oct
     2026, his review): rooms, compare, recently seen and the conversation as
     lists of records; the shelf as a list (its lines are not judged here,
     because the shelf already shows a line it can't use as "Details
     unavailable" with Remove); a room's site and name as text when set; and
     recent searches as text. Absent or null is an older record's empty list
     and reads as before; a field this version does not know is left alone.
     A field it knows in a shape it cannot use makes the whole value
     unreadable: kept as it is, never repaired or rewritten. */
  function shapeOk(d) {
    var rec = function (x) { return !!x && typeof x === 'object' && !Array.isArray(x); };
    var lists = ['rooms', 'cmp', 'seen', 'chat'].every(function (k) {
      return d[k] == null || (Array.isArray(d[k]) && d[k].every(rec));
    });
    if (!lists || !(d.shelf == null || Array.isArray(d.shelf))) { return false; }
    var rooms = (d.rooms || []).every(function (r) {
      return (!r.site || typeof r.site === 'string') && (!r.name || typeof r.name === 'string');
    });
    var finds = d.finds == null || (Array.isArray(d.finds) && d.finds.every(function (x) { return typeof x === 'string'; }));
    return rooms && finds;
  }
  function hold(why, docv) {
    if (!HOLD) { HOLD = why; MEM = docv || {}; heldSync(); }
    return MEM;
  }
  function store() {
    if (HOLD) { return MEM; }
    var r = readSaved();
    if (r.kind === 'ok') { return r.doc; }
    if (r.kind === 'none') { return {}; }
    return hold(r.kind);
  }
  function put(patch) {
    var s = store();
    Object.keys(patch).forEach(function (k) { s[k] = patch[k]; });
    if (HOLD) { MEM = s; return s; }
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {
      hold('unsaved', s);
      /* Said once, as it happens, through the polite toast; the notice on
         every screen carries it from then on. An action's own toast that
         follows says "not saved" too. */
      if (el && el.toast) { toast('This couldn’t be saved on this device. Changes now last only until you close this page.'); }
    }
    return s;
  }
  /* The explicit Erase, and nothing else, removes the saved value: true only
     when the removal succeeded. Then any hold ends and saving resumes. */
  function eraseSaved() {
    UNDO = null;   /* nothing taken off before an Erase is ever put back after it */
    try { localStorage.removeItem(KEY); } catch (e) { return false; }
    HOLD = ''; MEM = null; heldSync();
    return true;
  }
  /* What the page says while held: what happened, that the saved value is
     left as it is, and what that means for anything done now. No recovery is
     claimed. Shown at the top of every screen (heldSync). */
  var HELD_TEXT = {
    unreadable: 'This device holds a saved record this version of the app can’t read. It is kept as it is, and nothing here will write over it. Until it can be read, what you do now lasts only until you close this page. Erase everything on this device, on the You tab, would delete it for good.',
    blocked: 'This browser isn’t letting the app read what it saved, so nothing here is being saved. What you do now lasts only until you close this page.',
    unsaved: 'A change couldn’t be saved on this device, so nothing you change now is being saved. What was saved before is left as it was. Changes last only until you close this page.'
  };
  var HELD_KEPT = 'Not kept for now: this lasts only until you close this page.';
  /* Every screen in the stack says the same truth about saving, set again
     whenever it changes: when a screen enters (mount), when a hold begins
     (even from a late write that failed) and when Erase ends it (30 Oct 2026,
     his review). The notice sits at the top of each screen's own scrolling
     content, below its bar, in the normal flow: never under the opaque
     screen, never over a control, wrapped and scrollable at any width or text
     size. A screen kept underneath in the stack is corrected too. It is plain
     text, read in order as the screen's first content, not a live region:
     the screens kept underneath are still in the page, and a live region on
     each would be announced once per screen. A hold that begins later is
     announced once, by put(). */
  function heldSync() {
    if (!el || !el.stack) { return; }
    var scrs = el.stack.querySelectorAll('.va-scr'), i;
    for (i = 0; i < scrs.length; i++) { heldScreen(scrs[i]); }
  }
  function heldScreen(sc) {
    var body = sc.querySelector('[data-body]'), n = sc.querySelector('[data-held]'), lines, j;
    if (HOLD && body) {
      if (!n) {
        n = doc.createElement('p');
        n.className = 'va-note va-held';
        n.setAttribute('data-held', '');
        body.insertBefore(n, body.firstChild);
      }
      n.textContent = HELD_TEXT[HOLD] || HELD_TEXT.unreadable;
    } else if (n) { n.remove(); }
    swapKept(sc, HOLD ? 'Kept on this device.' : HELD_KEPT, HOLD ? HELD_KEPT : 'Kept on this device.');
    lines = sc.querySelectorAll('[data-held-line]');
    for (j = 0; j < lines.length; j++) { lines[j].textContent = heldLine(lines[j].getAttribute('data-held-line')); }
  }
  function swapKept(node, from, to) {
    if (!node || !doc.createTreeWalker) { return; }
    var w = doc.createTreeWalker(node, 4), t;
    while ((t = w.nextNode())) {
      if (t.nodeValue.indexOf(from) >= 0) { t.nodeValue = t.nodeValue.split(from).join(to); }
    }
  }
  /* The You tab's lines that say what is kept, from the current state. */
  function heldLine(k) {
    if (k === 'title') { return HOLD ? 'This page only' : 'What is kept here'; }
    if (k === 'store') { return storeLine(); }
    if (k === 'seen') { return seen().length + (HOLD ? ' on this page only, not saved' : ' recorded on this phone'); }
    if (k === 'finds') { return recentFinds().length + (HOLD ? ' on this page only, not saved' : ' kept'); }
    if (k === 'erase') {
      return HOLD === 'unreadable' ? 'The saved record this version can’t read, and this page'
                                   : 'Rooms, shelf, job sites, the conversation';
    }
    return '';
  }
  /* A change made while held is this page's alone, and says so. */
  function heldToast(msg) { toast(HOLD ? msg + ' (this page only, not saved)' : msg); }
  function rooms() { return store().rooms || []; }
  function roomNow() {
    var rs = rooms(), id = store().active, i;
    for (i = 0; i < rs.length; i++) { if (rs[i].id === id) { return rs[i]; } }
    return rs[0] || null;
  }
  function saveRoom(r) {
    var rs = rooms(), hit = false;
    rs = rs.map(function (x) { if (x.id === r.id) { hit = true; return r; } return x; });
    if (!hit) { rs.push(r); }
    put({ rooms: rs, active: r.id });
  }
  function dropRoom(id) {
    var rs = rooms().filter(function (x) { return x.id !== id; });
    put({ rooms: rs });
    if (store().active === id) { put({ active: rs.length ? rs[0].id : '' }); }
  }
  function shelf() { return store().shelf || []; }
  function onShelf(c, m) {
    return shelf().some(function (x) { return !!x && x.c === c && x.m === m; });
  }
  /* A shelf line's room used to be `x.r` — and `r` is the catalogue's field for
     a star rating. Two different things behind one letter is how somebody
     reading quickly writes a bug, and it made scripts/test/ledger-run.js flag a
     room picker as a rating. It is `rm` now, and roomOf() reads the old key too
     so a phone that saved a shelf yesterday keeps its rooms today.

     27 Oct 2026, Nasser's acceptance finding: `rm || r` let the old room win
     whenever the person chose "No room", because the empty choice is falsy and
     the older key was still there. Only a room picker writes `rm` onto a line
     that already exists (new lines are written with it), so once `rm` holds
     text it IS the person's choice, the empty text included, and the old `r`
     is only read for a line nobody has given a room since. The old key is left
     as it was saved: nothing is rewritten until the person chooses. */
  function roomOf(x) {
    if (!x || typeof x !== 'object') { return ''; }
    if (typeof x.rm === 'string') { return x.rm; }
    return x.rm || x.r || '';
  }

  function shelfAt(c, m) {
    var out = null;
    shelf().forEach(function (x) { if (x && x.c === c && x.m === m) { out = x; } });
    return out;
  }
  function toggleShelf(c, m, roomId) {
    var sh = shelf(), i = -1;
    sh.forEach(function (x, k) { if (x && x.c === c && x.m === m) { i = k; } });
    if (i > -1) { sh.splice(i, 1); }
    else { sh.unshift({ c: c, m: m, at: Date.now(), rm: roomId || '', q: 1 }); }
    put({ shelf: sh.slice(0, 150) });
    return i === -1;
  }
  /* A shelf line carries how many and which room it is destined for. An older
     line has neither; it counts as one, unassigned, and nothing breaks. */
  function shelfSet(c, m, patch) {
    var sh = shelf();
    sh.forEach(function (x) {
      if (x && x.c === c && x.m === m) {
        Object.keys(patch).forEach(function (k) { x[k] = patch[k]; });
      }
    });
    put({ shelf: sh });
  }
  function qtyOf(x) { var n = Number(x && x.q); return n > 0 ? Math.floor(n) : 1; }

  /* ── job sites ────────────────────────────────────────────────────────────
     A room belongs to a site or it does not. Trade buyers do not think in
     rooms, they think in addresses with rooms inside them, and the thing they
     have to produce at the end of a walk-through is one sheet per address. */
  function siteNames() {
    var out = [], seen = {};
    rooms().forEach(function (r) {
      var n = (r.site || '').trim();
      if (n && !seen[n.toLowerCase()]) { seen[n.toLowerCase()] = 1; out.push(n); }
    });
    return out.sort(function (a, b) { return a.toLowerCase() < b.toLowerCase() ? -1 : 1; });
  }
  function roomsAt(site) {
    var k = String(site || '').toLowerCase();
    return rooms().filter(function (r) { return (r.site || '').trim().toLowerCase() === k; });
  }
  function specIn(roomId) {
    return shelf().filter(function (x) { return roomOf(x) === roomId; });
  }
  /* A saved line whose category or model is not in the catalogue on this
     device answers null, and so does one whose saved reference is a word
     every object already has ("constructor", "__proto__"), which used to
     reach Object's own properties and throw on a tampered or very old list.
     Own categories only, real item lists only. */
  function itemOf(x) {
    if (!x || !Object.prototype.hasOwnProperty.call(BYID, x.c)) { return null; }
    var c = BYID[x.c]; if (!c || !Array.isArray(c.items)) { return null; }
    var it = null;
    c.items.forEach(function (m) { if (m && m.id === x.m) { it = m; } });
    return it ? { c: c, it: it } : null;
  }

  /* ══════════════════════════════════════════════ what stays on the device ══
     A phone is used in a basement, in a shop with two bars of signal, in a
     garage behind a metal door. The catalogue, the knowledge the assistant
     from, and the pictures already seen are kept in IndexedDB, so the second
     open of this app owes the network nothing and the tenth open in a dead
     spot still works.

     What this CANNOT do, and the reason it is IndexedDB rather than a service
     worker: a service worker has to be served from the site's own origin at or
     above the path it controls, and a Shopify theme can only publish files to
     the CDN. So the app's own shell still needs one connected load. Everything
     after that — every screen, every figure, every answer — is local. When the
     shell is wrapped for the App Store and Play the shell ships inside the
     binary and even that last dependency is gone. That is written here so the
     next person does not spend an evening trying to register a worker that
     Shopify will never scope. */
  var VAULT = (function () {
    var NAME = 'vb.app', SHOP = 'keep', dbp = null;
    var idb = root.indexedDB || root.webkitIndexedDB || null;
    function open() {
      if (dbp) { return dbp; }
      if (!idb) { return (dbp = Promise.reject(new Error('no idb'))); }
      dbp = new Promise(function (ok, no) {
        var rq;
        try { rq = idb.open(NAME, 1); } catch (e) { no(e); return; }
        rq.onupgradeneeded = function () {
          var db = rq.result;
          if (!db.objectStoreNames.contains(SHOP)) { db.createObjectStore(SHOP, { keyPath: 'k' }); }
        };
        rq.onsuccess = function () { ok(rq.result); };
        rq.onerror = function () { no(rq.error); };
        rq.onblocked = function () { no(new Error('blocked')); };
      });
      return dbp;
    }
    function tx(mode, fn) {
      return open().then(function (db) {
        return new Promise(function (ok, no) {
          var t = db.transaction(SHOP, mode), st = t.objectStore(SHOP), out;
          out = fn(st);
          t.oncomplete = function () { ok(out && out.result !== undefined ? out.result : out); };
          t.onerror = function () { no(t.error); };
          t.onabort = function () { no(t.error); };
        });
      });
    }
    return {
      /* Every call resolves. A device with IndexedDB switched off, a private
         window, a quota refusal — none of them are errors the person should
         ever hear about, they are just a slower app. */
      get: function (k) {
        return tx('readonly', function (st) { return st.get(k); })
          .then(function (rec) { return rec ? rec.v : null; })
          .catch(function () { return null; });
      },
      put: function (k, v, bytes) {
        return tx('readwrite', function (st) {
          return st.put({ k: k, v: v, at: Date.now(), n: bytes || 0 });
        }).then(function () { return true; }).catch(function () { return false; });
      },
      keys: function () {
        return tx('readonly', function (st) { return st.getAllKeys ? st.getAllKeys() : null; })
          .then(function (r) { return r || []; }).catch(function () { return []; });
      },
      all: function () {
        return tx('readonly', function (st) { return st.getAll ? st.getAll() : null; })
          .then(function (r) { return r || []; }).catch(function () { return []; });
      },
      drop: function (k) {
        return tx('readwrite', function (st) { return st['delete'](k); })
          .then(function () { return true; }).catch(function () { return false; });
      }
    };
  }());

  /* ── pictures already seen ────────────────────────────────────────────────
     Kept as blobs, handed back as object URLs. asset() consults this first, so
     a picture that has arrived once arrives instantly forever after and needs
     no signal. Capped, and the oldest go first — a phone is not a warehouse. */
  var SHOT = {}, SHOTBYTES = 0, SHOTCAP = 26 * 1024 * 1024, SEEN = {};
  function shotKey(u) { return 'img:' + String(u).split('?')[0].split('/').pop(); }
  function shotsLoad() {
    return VAULT.all().then(function (recs) {
      var imgs = recs.filter(function (r) { return r.k.indexOf('img:') === 0 && r.v; });
      imgs.forEach(function (r) {
        try { SHOT[r.k] = URL.createObjectURL(r.v); SHOTBYTES += (r.n || 0); } catch (e) {}
      });
      return imgs.length;
    });
  }
  function shotKeep(url) {
    if (!url || /^(blob|data):/.test(url)) { return; }
    var k = shotKey(url);
    if (SHOT[k] || SEEN[k]) { return; }
    SEEN[k] = 1;
    if (SHOTBYTES > SHOTCAP) { return; }
    /* The picture is already in the browser's own cache by the time this runs,
       so this fetch costs a cache read, not a download. */
    fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) { throw 0; }
      return r.blob();
    }).then(function (b) {
      if (!b || !b.size || b.size > 3 * 1024 * 1024) { return; }
      SHOTBYTES += b.size;
      try { SHOT[k] = URL.createObjectURL(b); } catch (e) {}
      return VAULT.put(k, b, b.size);
    }).catch(function () {});
  }

  /* The Shop screen is the front door, and a front door with nine of its
     twenty-two pictures missing is not a front door. Lazy loading means the
     tiles below the fold never load and so never get kept — so on a signal,
     when nothing else is happening, fetch the rest. One pass, once. */
  function idleRun(fn) {
    var i = root.requestIdleCallback;
    if (i) { i(fn, { timeout: 4000 }); } else { setTimeout(fn, 2200); }
  }
  function warmTiles() {
    if (!CAT || !LIVE) { return; }
    var names = [], seen = {};
    (CAT.cats || []).forEach(function (c) {
      [c.tile, c.hero].forEach(function (n) {
        if (n && !seen[n] && !SHOT['img:' + n]) { seen[n] = 1; names.push(n); }
      });
    });
    var i = 0;
    (function next() {
      if (i >= names.length || !LIVE) { return; }
      var n = names[i++];
      var b = (root.VB_APP_CFG && root.VB_APP_CFG.assetBase) || '';
      var url = b ? b.replace('__FILE__', encodeURIComponent(n)) : n;
      SEEN['img:' + n] = 0;
      shotKeep(url);
      /* One at a time, spaced out. This runs behind whatever the person is
         actually doing and must never be the reason a tap feels slow. */
      setTimeout(next, 140);
    }());
  }

  /* ── the keyboard ─────────────────────────────────────────────────────────
     On iOS the on-screen keyboard does not resize the page — it slides up over
     it, and a composer pinned to the bottom of the viewport ends up underneath.
     visualViewport is the only thing that reports the real visible height, so
     the app lifts by exactly the amount the keyboard covers and puts it back
     when the keyboard goes. Nothing else in the layout moves. */
  function wireKeyboard() {
    var vv = root.visualViewport;
    if (!vv) { return; }
    var apply = function () {
      var gap = Math.max(0, (root.innerHeight || 0) - vv.height - vv.offsetTop);
      /* Under about 90px it is a toolbar collapsing, not a keyboard. */
      el.root.style.setProperty('--kb', (gap > 90 ? Math.round(gap) : 0) + 'px');
      el.root.classList.toggle('is-kb', gap > 90);
    };
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    apply();
  }

  /* ── whether the network is there ─────────────────────────────────────── */
  var LIVE = true;
  function setLive(v) {
    if (LIVE === v) { return; }
    LIVE = v;
    var strip = doc.getElementById('va-off');
    if (strip) { strip.classList.toggle('on', !v); }
    if (el.root) { el.root.classList.toggle('is-off', !v); }
  }

  /* ── reading what a person typed ─────────────────────────────────────── */
  function readLen(t) {
    var L = root.VB_LANG; if (!L || !L.measures) { return null; }
    var m = L.measures(String(t || '')), i;
    for (i = 0; i < m.length; i++) {
      if (m[i].unit === 'ft') { return Math.round(m[i].value * 12); }
      if (m[i].unit === 'in') { return Math.round(m[i].value); }
    }
    return null;
  }
  /* A slab thickness, as typed (30 Oct 2026, with his two corrections). The
     whole entry must be one thickness: a number and one unit (in, inch,
     inches, ", ft, foot, feet, '), with nothing before or after it, in at
     most 15 significant digits (13 in feet, so twelve times it still fits
     in 15) and at most 6 decimal places. The decimal limit is what lets
     every figure it keeps be shown back in the field and read again
     unchanged: below a millionth JavaScript writes a number as "1e-7",
     which this reader would refuse, and twelve times a figure adds no
     decimal places. The curly marks a phone keyboard types, opening or
     closing, are read as " and '.
     Anything else (a bare number, a fraction, a range, a sign, words, a
     second figure) is not read. The shared parser in vb-lang is not used
     here: it pulls figures out of running text and lists an inch figure
     twice, once in feet. The precision typed is kept; feet become inches
     with only binary noise past the 15th significant digit removed. */
  function readThick(t) {
    var s = String(t == null ? '' : t).replace(/[“”″]/g, '"').replace(/[‘’′]/g, "'");
    var m = /^\s*(\d+(?:\.\d{1,6})?)\s*(in|inch|inches|"|ft|foot|feet|')\s*$/i.exec(s);
    if (!m) { return null; }
    var ft = /^(?:ft|foot|feet|')$/i.test(m[2]);
    if (m[1].replace('.', '').replace(/^0+/, '').length > (ft ? 13 : 15)) { return null; }
    var v = Number((parseFloat(m[1]) * (ft ? 12 : 1)).toPrecision(15));
    return isFinite(v) && v > 0 ? v : null;
  }
  /* Whether a saved slab is the person's own exact figure. True when this
     save read it from what was typed; false when the figure equals an older
     save that was rounded to the inch and never marked exact, because the
     form showed that rounded figure back and re-saving it unchanged does
     not make it a measurement. */
  function slabExactOf(slab, was) {
    return slab != null && !(was && was.slabExact !== true && was.slab === slab);
  }
  /* The slab as the form shows it: a saved figure in inches, or a slab saved
     as text exactly as it was given (30 Oct 2026). Before, any saved slab was
     shown with " in" added, so text read back as "6 in, poured 2019 in". */
  function slabShown(r) {
    var v = r && r.slab;
    return typeof v === 'number' ? (v ? v + ' in' : '') : (v == null ? '' : String(v));
  }
  /* The slab a save would keep, decided in one place: the figure, whether it
     is the person's exact one, and whether something was typed that could
     not be read, which stops the save rather than dropping it. A field left
     exactly as the form filled it in keeps the room's saved slab and its
     standing as it was (`kept`), so an older slab this reader would not
     accept never stops a save of the room it belongs to.
     30 Oct 2026, his review: "left as the form filled it in" is judged on the
     field's value BEFORE any trimming, against slabShown(), for text as well
     as numbers. Trimmed first, or numbers only, a saved slab with spaces
     around it or saved as text looked edited, failed the reader and blocked
     the room's save. Only a slab actually edited is trimmed and read. */
  function roomSlab(text, was) {
    var typed = String(text == null ? '' : text), raw = typed.trim();
    if (was && typed !== '' && typed === slabShown(was)) {
      return { slab: was.slab, exact: was.slabExact === true, unread: false, kept: true };
    }
    var slab = readThick(raw);
    return { slab: slab, exact: slabExactOf(slab, was), unread: raw !== '' && slab == null };
  }
  /* The room a save writes (30 Oct 2026). It is built on top of what the room
     already holds, so a key this form does not show is kept. Every field the
     form shows is set from the form, as before. A slab left exactly as the
     form showed it keeps its saved value and its exact mark untouched, a
     legacy text entry included; any other slab is written with its standing,
     as before. */
  function roomFromForm(v, was, sl) {
    var r = {}, k;
    if (was) { for (k in was) { r[k] = was[k]; } }
    r.id = v.id || ('r' + Date.now());
    r.name = (v.name || '').trim() || 'Your room';
    r.site = (v.site || '').trim();
    r.door = readLen(v.door);
    r.ceiling = readLen(v.ceiling);
    r.supply = v.supply || '';
    r.stairs = v.stairs || '';
    if (!sl.kept) { r.slab = sl.slab; r.slabExact = sl.exact; }
    return r;
  }

  /* ══════════════════════════════════════════════════════════ the fit engine ══
     inches(), AMPS and fitRows() below are a VERBATIM PORT of the site's, lifted
     out of assets/vb-shop.js rather than rewritten, so the app and the site can
     never give one customer two different answers about the same doorway.

     They are duplicated rather than shared because pulling a module out of
     working shop code is a refactor of the live site, and this was built
     overnight while nobody could approve it. scripts/test/app-run.js extracts
     both copies and asserts they are character-identical, so drift fails a test
     rather than reaching a customer.

     THIS IS THE APP'S REASON TO EXIST. Everything else here a website can do.
     This it cannot: the phone is the thing that is actually in the room, in your
     hand, beside the doorway, at the moment the question comes up. */

function inches(v) {
    var s = String(v == null ? '' : v);
    var ft = /(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|')/i.exec(s);
    var ins = /(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")/i.exec(s);
    var t = 0;
    if (ft) { t += parseFloat(ft[1]) * 12; }
    if (ins) { t += parseFloat(ins[1]); }
    return t || null;
  }

  var AMPS = { '120 V standard outlet': 0, '240 V 20 A': 20, '240 V 30 A': 30,
               '240 V 40 A': 40, '240 V 50 A': 50, 'Three phase': 999 };

function fitRows(it, room) {
    var a = it.a || {}, out = [];

    /* the doorway */
    var w = inches(a.Width) || inches(a.Depth) || inches(a['Closed height']);
    var wLab = a.Width ? 'Width' : (a.Depth ? 'Depth' : 'Closed height');
    if (room.door && w) {
      var slack = room.door - w;
      out.push({
        k: slack >= 4 ? 'yes' : (slack >= 0 ? 'tight' : 'no'), w: 'the doorway',
        t: 'Through a ' + room.door + " in doorway",
        s: wLab + ' is ' + a[wLab] + '. ' + (slack >= 4
            ? 'Four inches of room or better.'
            : (slack >= 0
              ? 'It goes through with ' + Math.round(slack) + ' in to spare — crated it will not, so it comes off the pallet at the door.'
              : 'It does not, uncrated or otherwise. It would need to come in another way.'))
      });
    } else if (room.door) {
      out.push({ k: 'ask', w: 'the doorway', t: 'Through a ' + room.door + ' in doorway',
        s: 'We do not carry a width for this piece yet. Ask and we confirm it from the manufacturer drawing before anything ships.' });
    }

    /* the ceiling */
    var ch = inches(a.Ceiling) || inches(a['Bay height']);
    if (room.ceiling && ch) {
      out.push({
        k: room.ceiling >= ch ? 'yes' : 'no', w: 'the ceiling',
        t: 'Under a ' + room.ceiling + ' in ceiling',
        s: 'It wants ' + (a.Ceiling || a['Bay height']) + '. ' +
           (room.ceiling >= ch ? 'Yours clears it.' : 'Yours is ' + Math.round(ch - room.ceiling) + ' in short.')
      });
    }

    /* the power */
    if (room.supply) {
      var need = a.Circuit || a.Supply || '';
      var mine = AMPS[room.supply];
      /* a supply this check does not know goes to the rule below, which asks */
      if (need && /hardwired/i.test(need) && !/(\d+)\s*A/.test(need) &&
          (room.supply === 'Not sure yet' || typeof mine === 'number')) {
        /* A hardwired supply with no breaker rating (1 Nov 2026, his
           direction; 506bf70's accepted behaviour, kept to this one case and
           moved in all three engines with the fixture). It is wired in, so a
           standard outlet is never said to run it, "Not sure yet" included. A
           matching voltage alone does not establish that a circuit carries
           it, because the listing gives no amps, and three phase says neither
           the voltage nor the amps. An established mismatch still fails. */
        var hwWired = 'It is wired in, not plugged in, so an electrician makes the connection, and the ' +
                      'manufacturer\'s installation instructions set the circuit it needs.';
        var hwThree = 'Three phase on its own does not say the voltage or the amps available, so an electrician ' +
                      'confirms it at the panel.';
        var hwKind = 'ask', hwSays = hwWired;
        if (room.supply === 'Not sure yet') {
          /* to confirm, wired in */
        } else if (/three phase/i.test(need)) {
          hwKind = mine === 999 ? 'ask' : 'no';
          hwSays = mine === 999 ? hwThree : 'Three phase is not something a house usually has.';
        } else if (/240/.test(need) && !(mine >= 20)) {
          hwKind = 'no';
          hwSays = 'It needs a 240 V circuit adding.';
        } else if (/240/.test(need)) {
          hwSays = mine === 999 ? hwThree
            : 'Yours is ' + room.supply + '. The listing does not give the amps it needs, so the ' +
              'manufacturer\'s installation instructions decide whether yours carries it.';
        }
        out.push({ k: hwKind, w: 'the supply', t: 'On the supply you have', s: 'It runs on ' + need + '. ' + hwSays });
      } else if (need) {
        /* Every other listed circuit or supply (1 Nov 2026, his direction on
           cdf06ae), answered from the listing's own figures and the supply
           the shopper chose, and nothing else. It clears only when the
           supply is what the listing asks for: a 120 V plug-in on a standard
           outlet, or a 240 V circuit of exactly the amps listed, and a
           matching rating is not approval to install. It fails only where a
           figure the shopper gave conflicts with the listing: another
           voltage, or fewer amps than listed. The rest is to confirm: three
           phase on its own (no voltage, no amps), a three-phase listing on a
           circuit of its voltage (a circuit's figures do not say its phase,
           so the branch phase is confirmed), a listing without amps, a
           larger breaker than listed (the listing sets no largest), a 120 V
           piece on a 240 V circuit, a supply this check does not know, and
           gas, which no electrical supply answers. */
        var lead = (a.Circuit ? 'It needs ' : 'It runs on ') + need + '. ';
        var nv = /(\d+)\s*V/.exec(need), na = /(\d+)\s*A/.exec(need);
        var volts = nv ? parseInt(nv[1], 10) : 0, amps = na ? parseInt(na[1], 10) : 0;
        var plug = /plug-in|standard outlet/i.test(need);
        var sk = 'ask', ss;
        if (/gas/i.test(need)) {
          ss = 'An electrical supply does not settle a gas connection; the manufacturer\'s installation ' +
               'instructions set what it needs.';
        } else if (room.supply === 'Not sure yet') {
          ss = plug && volts === 120 && !amps
            ? 'It plugs into a standard outlet, so check there is one where it goes.'
            : 'Worth checking the panel before you order — an electrician can read it in a minute.';
        } else if (typeof mine !== 'number' || !volts) {
          ss = 'This check cannot compare it with the supply you gave, so an electrician confirms it against the listing.';
        } else if (mine === 999) {
          ss = plug && volts === 120 && !amps
            ? 'It plugs into a standard outlet, so check there is one where it goes.'
            : 'Three phase on its own does not say the voltage or the amps available, so an electrician ' +
              'confirms it at the panel.';
        } else if (/three phase/i.test(need)) {
          if (volts !== (mine === 0 ? 120 : 240)) {
            sk = 'no';
            ss = 'It is listed for ' + volts + ' V three phase and yours is ' + (mine === 0 ? '120' : '240') +
                 ' V, so as listed it needs a ' + volts + ' V three-phase supply.';
          } else if (amps && mine > 0 && mine < amps) {
            sk = 'no'; ss = 'Yours is ' + mine + ' A — it would need upgrading.';
          } else {
            ss = 'The voltage matches, but ' + room.supply + ' does not say its phase, so an electrician confirms ' +
                 'the branch phase at the panel.';
          }
        } else if (volts === 120) {
          if (mine !== 0) {
            ss = plug ? 'Yours is ' + room.supply + ', not a standard outlet, so the outlet where it goes needs confirming.'
                      : 'Yours is ' + room.supply + ', a different voltage, so the circuit needs confirming.';
          } else if (plug && !amps) {
            sk = 'yes'; ss = 'A standard outlet runs it.';
          } else if (amps) {
            ss = 'A standard outlet does not say its amps, so an electrician checks its circuit against the ' +
                 amps + ' A listed.';
          } else {
            ss = 'The listing does not say it plugs into a standard outlet or give its amps, so the ' +
                 'manufacturer\'s sheet sets the circuit it needs.';
          }
        } else if (volts !== 240) {
          sk = 'no';
          ss = 'It is listed for ' + volts + ' V and yours is ' + (mine === 0 ? '120' : '240') + ' V, so as listed ' +
               'it needs a ' + volts + ' V supply.';
        } else if (mine === 0) {
          sk = 'no';
          ss = a.Circuit ? 'A standard outlet will not run it; this is a circuit an electrician adds.'
                         : 'It needs a 240 V circuit adding.';
        } else if (!amps) {
          ss = 'Yours is ' + room.supply + '. The listing does not give the amps it needs, so the ' +
               'manufacturer\'s installation instructions decide whether yours carries it.';
        } else if (mine < amps) {
          sk = 'no'; ss = 'Yours is ' + mine + ' A — it would need upgrading.';
        } else if (mine === amps) {
          sk = 'yes';
          ss = 'Your circuit matches the listed rating. The manufacturer\'s installation instructions still apply.';
        } else {
          ss = 'Yours is ' + mine + ' A, more than the ' + amps + ' A it lists, and the listing does not say a ' +
               'larger breaker is allowed, so the manufacturer\'s installation instructions decide.';
        }
        out.push({ k: sk, w: 'the supply', t: 'On the supply you have', s: lead + ss });
      }
    }

    /* the route in

       Never a confirmed fit (26 Oct 2026). A weight on the sheet is not a plan
       for carrying it: with no weight the answer is unresolved and no handling
       advice is invented; with one, the weight is repeated exactly as the sheet
       states it and how it goes up is a question to settle. */
    if (room.stairs && room.stairs !== 'No stairs') {
      var stated = String(a.Weight == null ? '' : a.Weight).trim() !== '';
      var heavy = /1,500|and over|800-1,500/.test(String(a.Weight || ''));
      out.push({
        k: 'ask', w: 'the route in',
        t: room.stairs === 'A full flight' ? 'Up a flight of stairs' : 'Up a few steps',
        s: !stated
          ? 'Our sheet gives no weight for this piece, so its weight and the plan for handling it on the stairs are unconfirmed.'
          : 'It weighs ' + a.Weight + '. ' + (heavy
             ? 'At that weight stairs are a conversation before the truck is booked, not on the day.'
             : 'How it goes up the stairs, and how many people that takes, is a question to settle with us before the truck is booked.')
      });
    }

    /* the slab

       30 Oct 2026, Nasser's direction: a thickness is never an approval.
       The slab entry is the one our sheet gives, repeated exactly as the
       sheet gives it; it is not a verified manufacturer requirement and
       nothing here says it is. It is compared only when it is one
       thickness: a single positive number with one unit and no other
       figure, sign or word, in at most 15 significant digits (13 in feet,
       so twelve times it still fits in 15 and is never rounded up). A room's
       thickness given as text is read the same way. Thinner than the
       sheet's figure is a "no". Meeting it is still unresolved, because a
       thickness does not show whether a slab is suitable. A room's number
       is the person's exact figure only when the app saved it as one
       (slabExact); any other number is a record that may have been rounded
       to the inch, so it is never said to meet the figure, and it is
       thinner only where an inch of rounding could not change that. A
       piece whose sheet gives no slab gets no slab row: nothing is
       invented for it. */
    if (a.Slab != null) {
      var oneThick = function (v) {
        var m = /^\s*(\d+(?:\.\d+)?)\s*(in|inch|inches|"|ft|foot|feet|')\s*$/i.exec(String(v == null ? '' : v));
        var ft = !!m && /^(?:ft|foot|feet|')$/i.test(m[2]);
        if (!m || m[1].replace('.', '').replace(/^0+/, '').length > (ft ? 13 : 15)) { return null; }
        var n = Number((parseFloat(m[1]) * (ft ? 12 : 1)).toPrecision(15));
        return isFinite(n) && n > 0 ? n : null;
      };
      var listed = String(a.Slab).trim();
      var needSlab = oneThick(listed);
      var exact = typeof room.slab === 'string' || room.slabExact === true;
      var mySlab = typeof room.slab === 'number'
        ? (isFinite(room.slab) && room.slab > 0 ? room.slab : null) : oneThick(room.slab);
      var thinner = !!(mySlab && needSlab && (exact ? mySlab < needSlab : mySlab + 1 <= needSlab));
      out.push({
        k: thinner ? 'no' : 'ask', w: 'the slab',
        t: 'On the slab you have',
        s: (listed ? 'Our sheet gives the slab as ' + listed + '. '
                   : 'Our sheet names a slab for this piece but gives no figure. ') +
           (!mySlab
             ? 'No usable slab thickness is given for this room, so whether the slab is suitable is unconfirmed.'
             : (exact ? 'Yours is ' : 'Yours is recorded as ') + mySlab + ' in' + (!needSlab
               ? ', but our sheet does not give one thickness to compare it with, so whether the slab is suitable is unconfirmed.'
               : (thinner
                 ? ', thinner than that figure.'
                 : (exact
                   ? ', which meets that figure, but a thickness alone does not show whether a slab is suitable, so that is unconfirmed.'
                   : ', which may have been rounded when it was saved, so whether it meets that figure is unconfirmed, and so is whether the slab is suitable.'))))
      });
    }
    return out;
  }

  /* One word for a whole model, from the same rows its page shows.

     THE ORDER MATTERS AND THE FIRST VERSION HAD IT WRONG. It let a single
     unanswerable check outrank every check that passed, so a sauna whose
     circuit clearly cleared a 30 A supply came back as "ask" because the
     catalogue holds no width for it — and the Fit tab read 0 of 30 for every
     category on the shelf. Caught by looking at the screen rather than at the
     code: the numbers were all zero, which no real room produces.

     The honest ranking is worst DETERMINATE answer first. A "no" is a no. A
     tight fit is tight. If something passed and something else is merely
     unknown, the piece fits and there is a figure to confirm — which is what
     the row on the model page says, in those words. Only when nothing at all
     could be determined is the headline "ask".

     26 Oct 2026, Nasser's direction, and it replaces the paragraph above: an
     unresolved check is never summed up as Fits. A "no" still wins outright;
     then anything unresolved; then tight; then fits. A piece whose doorway
     clears but whose stairs or width is unconfirmed is "ask", because "Fits"
     there would be a claim nothing on the sheet supports. */
  var RANK = { no: 4, ask: 3, tight: 2, yes: 1 };
  function verdict(it, r) {
    if (!r) { return null; }
    var rows = fitRows(it, r);
    if (!rows.length) { return null; }
    var best = '';
    rows.forEach(function (x) { var k = RANK[x.k] ? x.k : 'ask'; if (!best || RANK[k] > RANK[best]) { best = k; } });
    return best;
  }
  var VWORD = { yes: 'Fits', tight: 'Tight', no: 'Will not', ask: 'Ask' };

  /* A figure the catalogue says is not confirmed (its tbc; 1 Nov 2026, his
     direction on the Loyly). The engine reads no figure for it, so the model
     page answers a supply given "to confirm" rather than with nothing, in the
     product page's words, and its Specification says "Not confirmed yet".
     fitRows() and verdict() are unchanged. */
  var TBC_SUPPLY = 'We have not confirmed the electrical supply it needs, so whether yours carries it is not ' +
    'something we can say yet. We confirm it with you before an order.';
  function withTbc(it, r, rows) {
    var power = (it.tbc || []).some(function (k) { return /^(Circuit|Supply|Service)$/.test(k); });
    if (!r || !r.supply || !power || rows.some(function (x) { return x.w === 'the supply'; })) { return rows; }
    return rows.concat([{ k: 'ask', w: 'the supply', t: 'On the supply you have', s: TBC_SUPPLY }]);
  }
  function tbcSpec(it) {
    return (it.tbc || []).map(function (k) {
      return '<div><dt>' + esc(k) + '</dt><dd>Not confirmed yet</dd></div>';
    }).join('');
  }

  /* What to LOOK AT first, as opposed to what to CALL a set of checks. Fits,
     then tight, then the ones we cannot answer, then the outright no - because
     a no belongs last in a list and first in a headline. */
  var SHOWRANK = { yes: 0, tight: 1, ask: 2, no: 3 };

  /* ══════════════════════════════════════════════════════ the navigation ══
     A stack of real layers. Pushing puts the new screen over the old one and
     slides it in from the right while the old one drifts left and dims; popping
     reverses it. Both are transforms and run on the compositor, so a push costs
     the same on a mid-range Android as it does on a new iPhone.

     The stack mirrors history, so the Android back button and the iOS edge
     swipe both work with no extra code — going back is a history.back(), and
     hashchange decides whether that was a push or a pop by comparing depth. */

  /* Four destinations and her. The shape is the one every phone shop app has
     converged on, and there is no cleverness to be had in moving it — a person
     picking this up has thirty years of muscle memory for where the cart is. */
  var TABS = [
    { id: 'shop', label: 'Shop', hash: '#/shop',
      path: 'M3 10.2 12 3l9 7.2V20a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 20z' },
    { id: 'fit', label: 'Fit', hash: '#/fit',
      path: 'M4 20.5h16M6.5 20.5V10m4 10.5V4.2m4 16.3v-7m4 7V7.4' },
    { id: 'shelf', label: 'Shelf', hash: '#/shelf',
      path: 'M4 7.5h16M5.6 7.5 6.8 19a1.6 1.6 0 0 0 1.6 1.5h7.2a1.6 1.6 0 0 0 1.6-1.5l1.2-11.5M9.2 7.5V5.4A1.9 1.9 0 0 1 11.1 3.5h1.8a1.9 1.9 0 0 1 1.9 1.9V7.5' },
    { id: 'you', label: 'You', hash: '#/you',
      path: 'M20 21v-1.8a4.2 4.2 0 0 0-4.2-4.2H8.2A4.2 4.2 0 0 0 4 19.2V21M12 11.4a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4z' }
  ];

  function chrome() {
    var n = shelf().length;
    return '<div class="va-stack" data-stack></div>' +
      '<nav class="va-tabs">' + TABS.map(function (x) {
        /* The shelf count rides on its own icon, like every cart on every
           phone. It is a fact about this device, not a nudge. */
        var badge = (x.id === 'shelf')
          ? '<i class="va-tab-n" data-shelf-n' + (n ? '' : ' hidden') + '>' + n + '</i>' : '';
        return '<a class="va-tab" data-tab="' + x.id + '" href="' + x.hash + '">' +
          '<span class="va-tab-i"><svg viewBox="0 0 24 24" aria-hidden="true">' +
          '<path d="' + x.path + '"/></svg>' + badge + '</span>' +
          '<span>' + esc(x.label) + '</span></a>';
      }).join('') +
      /* She is the fifth cell and she is not a destination — she opens over
         whatever you are already looking at, because the question is almost
         always ABOUT what you are already looking at. */
      '<button class="va-tab va-tab-a" data-asst-open aria-label="Ask ' + esc(asstName()) + '">' +
        '<span class="va-tab-i">' + ASKMARK + '</span>' +
        '<span>' + esc(asstName()) + '</span></button>' +
      '</nav>' +
      '<div class="va-off" id="va-off" role="status">No signal. Working from this device.</div>' +
      '<div class="va-sheet" data-ask-sheet hidden></div>' +
      '<div class="va-toast" data-toast aria-live="polite"></div>';
  }

  /* Her mark, from the one file in the theme that is allowed to describe her:
     an elongated diamond, her own travelling light drawn still. Copied here as
     a path rather than rendered from Liquid because the app is one script with
     no server round trip — the shape is hers, the name still is not. */
  var ASKMARK =
    '<svg class="va-ask-mk" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M12 5.2 L21 12 L12 18.8 L3 12 Z" fill="none" stroke="currentColor" ' +
        'stroke-width="1.15" opacity=".5"/>' +
      '<path d="M12 8.6 L16.6 12 L12 15.4 L7.4 12 Z" fill="currentColor" stroke="none"/>' +
    '</svg>';

  function shelfBadge() {
    var b = el.root && el.root.querySelector('[data-shelf-n]');
    if (!b) { return; }
    var n = shelf().length;
    b.textContent = String(n);
    b.hidden = !n;
  }

  /* Build one screen layer. `spec` is what a screen function returns. */
  /* The field is a BUTTON that looks like a field. Tapping it pushes the search
     screen, which owns a real input and focuses it on arrival. Two reasons: a
     live input inside a screen that is also collapsing a large title fights the
     scroll handler for the same pixels, and on iOS a focused input inside a
     transform-animated layer drags the whole keyboard through the animation. */
  function findField(ph) {
    return '<div class="va-findrow">' +
      '<button class="va-find" data-find-open>' +
        '<svg class="va-find-i" viewBox="0 0 24 24" aria-hidden="true">' +
          '<circle cx="11" cy="11" r="6.4"/><path d="m20 20-4.4-4.4"/></svg>' +
        '<span>' + esc(ph || 'Search, or ask a question') + '</span>' +
      '</button>' +
      (canHear()
        ? '<button class="va-find-m" data-find-voice aria-label="Ask out loud">' +
          '<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3"/>' +
          '<path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg></button>'
        : '') +
    '</div>';
  }

  function layer(spec) {
    var s = doc.createElement('div');
    s.className = 'va-scr' + (spec.search ? ' has-find' : '') +
      (spec.under ? ' has-jump' : '');
    s.innerHTML =
      '<div class="va-bar">' +
        '<button class="va-ico" data-back' + (spec.back ? '' : ' hidden') +
          ' aria-label="Back"><svg viewBox="0 0 24 24"><path d="M15 18.5 8.5 12 15 5.5"/></svg></button>' +
        '<div class="va-bt">' + esc(spec.title || '') + '</div>' +
        '<div class="va-bright">' + (spec.act || '') + '</div>' +
      '</div>' +
      (spec.search ? findField(spec.searchPh) : '') +
      (spec.under || '') +
      '<div class="va-body' + (spec.bare ? ' is-bare' : '') + '" data-body>' +
        (spec.large ? '<h1 class="va-lt">' + esc(spec.large) + '</h1>' : '') +
        spec.html +
      '</div>' +
      /* Anything that must sit OUTSIDE the scroll view — the composer — goes
         here, so it stays put while the conversation moves under it. */
      (spec.after || '');
    return s;
  }

  /* The large title fades out and the bar title fades in as you scroll — one
     listener, one custom property, no reflow. */
  function wireScroll(s) {
    var body = s.querySelector('[data-body]');
    var lt = s.querySelector('.va-lt');
    var bar = s.querySelector('.va-bar');
    if (!body) { return; }
    var run = false;
    body.addEventListener('scroll', function () {
      if (run) { return; }
      run = true;
      requestAnimationFrame(function () {
        run = false;
        var y = body.scrollTop;
        var t = lt ? Math.max(0, Math.min(1, (y - 6) / 34)) : (y > 4 ? 1 : 0);
        if (lt) { lt.style.setProperty('--ltop', String(1 - t)); }
        bar.style.setProperty('--barttl', String(t));
        bar.style.setProperty('--barline', String(y > 4 ? 1 : 0));
      });
    }, { passive: true });
    if (!lt) { bar.style.setProperty('--barttl', '1'); }
  }

  /* Images fade up over their own placeholder, so nothing on the page moves
     when one lands and nothing snaps in. */
  function wireImages(s) {
    Array.prototype.forEach.call(s.querySelectorAll('.va-img'), function (box) {
      var img = box.querySelector('img');
      if (!img) { box.classList.add('is-on'); return; }
      if (img.complete && img.naturalWidth) { box.classList.add('is-on'); shotKeep(img.src); return; }
      img.addEventListener('load', function () { box.classList.add('is-on'); shotKeep(img.src); });
      img.addEventListener('error', function () { box.classList.add('is-on'); });
    });
  }

  /* The stack must end up as deep as the route is, and no deeper. Popping from
     a model back to a tab used to leave the layer it had been sitting on in the
     document: invisible, but live, and it made every ':last-child' reading in a
     test lie. One live layer beneath the current one is the most any screen
     needs — that is what a back gesture reveals — so that is the ceiling. */
  /* ── coming back to where you were ───────────────────────────────────────
     Tapping the ninth model in a category and pressing back should return you
     to the ninth model, not to the top of thirty. Every app that gets this
     wrong is exhausting to browse and nobody can say why. The position is
     keyed by route, so two categories remember two places. */
  var SPOT = {};
  function spotKey() { return (location.hash || '#/shop'); }
  function spotSave() {
    var top = stack[stack.length - 1];
    var body = top && top.querySelector('[data-body]');
    if (body && body.scrollTop > 4) { SPOT[LASTHASH] = body.scrollTop; }
    else { delete SPOT[LASTHASH]; }
  }
  function spotRestore(scr, how) {
    if (how !== 'pop') { return; }
    var y = SPOT[spotKey()];
    if (!y) { return; }
    var body = scr.querySelector('[data-body]');
    if (!body) { return; }
    /* Set it before the layer is painted, not after, or the eye catches the
       jump. The images below the fold have reserved boxes already, so nothing
       reflows underneath afterwards. */
    body.scrollTop = y;
  }
  var LASTHASH = '#/shop';

  /* A short, quiet tick under the thumb where the platform offers one. Android
     does; iOS Safari does not, and no amount of wishing changes that, so this
     is an enhancement and nothing depends on it. */
  function tap(ms) {
    try { if (root.navigator && root.navigator.vibrate) { root.navigator.vibrate(ms || 8); } }
    catch (e) {}
  }

  function trim(keep) {
    var n = Math.max(1, Math.min(keep, 2));
    while (stack.length > n) { var old = stack.shift(); if (old) { old.remove(); } }
  }

  /* The dots are driven by where the track actually is, not by a click
     handler, so a flick and a tap agree with each other. */
  function wireGallery(scr) {
    var t = scr.querySelector('[data-gal-track]');
    var d = scr.querySelector('[data-gal-dots]');
    if (!t || !d) { return; }
    var dots = d.querySelectorAll('i');
    t.addEventListener('scroll', function () {
      var i = Math.round(t.scrollLeft / Math.max(1, t.clientWidth));
      Array.prototype.forEach.call(dots, function (x, k) { x.classList.toggle('on', k === i); });
    }, { passive: true });
  }

  function mount(spec, how) {
    /* Where focus was, before anything moves (focusLost below): the route
       being left remembers its control for a later return, and a repaint of
       the same route finds the same control again. */
    var was = doc.activeElement, lost = focusLost(was), from = stack[stack.length - 1];
    var ref = lost && lost !== from ? null : focusRef(lost, was), same = spotKey() === LASTHASH;
    if (from) { if (ref) { FOCUS[LASTHASH] = ref; } else { delete FOCUS[LASTHASH]; } }
    spotSave();
    var next = layer(spec);
    var prev = stack[stack.length - 1];
    var keep = (spec.depth == null ? 1 : spec.depth) + 1;
    el.stack.appendChild(next);
    wireScroll(next); wireImages(next);
    wireGallery(next);
    heldScreen(next);
    undoScreen(next);
    spotRestore(next, how);
    LASTHASH = spotKey();

    if (!prev || how === 'replace') {
      if (prev) { prev.remove(); stack.pop(); }
      next.classList.add('is-fade-in');
      stack.push(next);
      trim(keep);
    } else if (how === 'pop') {
      next.classList.add('is-pop-in');
      prev.classList.add('is-pop-out');
      busy = true;
      after(prev, function () { prev.remove(); busy = false; trim(keep); });
      stack.pop(); stack.push(next);
    } else {
      next.classList.add('is-push-in');
      prev.classList.add('is-push-out');
      busy = true;
      after(next, function () { busy = false; trim(keep); });
      stack.push(next);
    }
    setTab(spec.tab);
    screensLive(next);
    /* A search screen that arrives without the keyboard is a search screen you
       have to tap twice. The wait is one frame past the push animation's start,
       so the field is on screen before iOS raises the keyboard against it. */
    if (spec.focus) {
      var at = doc.activeElement;
      setTimeout(function () {
        /* Only for this screen while it is still the page: not under a panel
           opened since, not after a later move, and not over a control the
           person has gone to meanwhile. */
        if (SHEET.open || next !== stack[stack.length - 1] ||
            (doc.activeElement !== at && sheetUsable(doc.activeElement))) { return; }
        var f = next.querySelector('[data-find-in]');
        if (f) { try { f.focus(); } catch (e) {} }
        else if (lost) { focusLand(next); }
      }, 90);
    } else if (lost) {
      /* Focus was on a control of a screen that has just gone (or gone
         underneath), or in a sheet that this move closed. Back: the control
         this route was left from. A repaint of the same route: the same
         control again, on the same item wherever it now sits; if that item
         is gone, its neighbour's row to read from (focusNear). Otherwise, or
         when there is none of those: the new screen's title. */
      if (how === 'pop') { focusTo(focusFind(next, FOCUS[spotKey()]), next, true); }
      else if (how === 'replace' && same) { focusTo(focusFind(next, ref), next, false); }
      else { focusLand(next); }
    }
  }
  /* ── only the current screen is the page (30 Oct 2026, his direction) ────
     The stack keeps the screen underneath for the slide and the back gesture,
     and the leaving screen until its slide ends. Those stay painted exactly as
     before but are inert: no Tab stop, no screen-reader cursor, no tap. Only
     the top screen, with the tab bar and any open sheet around it, is the
     page. Focus already elsewhere (the tab bar, an open sheet, nothing) is
     never moved by a screen change. */
  var FOCUS = {};
  function screensLive(top) {
    Array.prototype.forEach.call(el.stack.children, function (s) {
      if (!s.classList || !s.classList.contains('va-scr')) { return; }
      if (s === top) { s.removeAttribute('inert'); s.removeAttribute('aria-hidden'); }
      else { s.setAttribute('inert', ''); s.setAttribute('aria-hidden', 'true'); }
    });
  }
  /* The screen (or the closing sheet) that held focus, or null: an open sheet
     keeps its focus, and so do the tab bar and the page itself. */
  function focusLost(n) {
    if (!n || !n.closest || !el.stack || n === doc.body) { return null; }
    var s = n.closest('.va-scr');
    if (s && s.parentNode === el.stack) { return s; }
    var sh = n.closest('[data-ask-sheet]');
    return sh && !SHEET.open ? sh : null;
  }
  /* A control is known again by what it is, not by the element: its tag, its
     link, its field name and which data- attributes it carries (focusSig),
     and which item it acts on (focusId). Then which of those it was, counting
     from the top of its screen.

     30 Oct 2026, his direction: the kind alone was not enough. A saved line's
     Remove carries no identity of its own, so when a line arrived on top or
     one was removed, focus went to the same place in the list: another
     line's control, and after a Remove the next line's Remove, one keypress
     from taking off a piece nobody chose. So a control is also known by the
     item it belongs to: the values of its own data- attributes (a compare
     Remove's "category|model", a room's id), and inside a saved-list row,
     that row's saved reference. Never its visible text, never the row's place
     in the list (data-sl, the drawing's stamp, generated ids).

     A saved reference is only the category and model, and the shelf keeps
     two lines of the same piece with different rooms or counts. Those lines
     have no identity that tells them apart, so neither is restored: focus on
     one goes to the screen's title, and the reference it was captured with
     stays marked as one of twins, so removing one twin can never send the old
     Remove's focus to the other. A control with no item keeps the
     kind-and-order matching it always had. */
  var FOCUS_ROW = '[data-sl-ref]';
  function focusSig(n) {
    var d = [], k;
    for (k = 0; k < n.attributes.length; k++) {
      if (n.attributes[k].name.indexOf('data-') === 0) { d.push(n.attributes[k].name); }
    }
    return [n.tagName, n.getAttribute('href') || '', n.getAttribute('name') || '', d.sort().join(' ')].join('|');
  }
  /* The saved-list rows of a screen and the identity of each: "row " and its
     reference when that reference is on one row only, "row? " and it when
     the reference is shared (twins, amb). */
  function focusRows(scr) {
    var els = Array.prototype.slice.call(scr.querySelectorAll(FOCUS_ROW));
    var seen = Object.create(null), ids = [], amb = [], k, r;
    for (k = 0; k < els.length; k++) {
      r = els[k].getAttribute('data-sl-ref') || '';
      seen[r] = (seen[r] || 0) + 1;
    }
    for (k = 0; k < els.length; k++) {
      r = els[k].getAttribute('data-sl-ref') || '';
      amb.push(seen[r] > 1);
      ids.push((seen[r] > 1 ? 'row? ' : 'row ') + r);
    }
    return { els: els, ids: ids, amb: amb };
  }
  /* The item a control acts on, or '' for a control that has none. A row
     that holds focus itself (focusNear) is known by its identity alone: its
     own data-sl and stamp are its place and its drawing. */
  function focusId(rows, n) {
    var d = [], k, a, row = n.closest ? n.closest(FOCUS_ROW) : null;
    for (k = 0; row !== n && k < n.attributes.length; k++) {
      a = n.attributes[k];
      if (a.name.indexOf('data-') === 0 && a.value) { d.push(a.name + '=' + a.value); }
    }
    d.sort();
    k = row ? rows.els.indexOf(row) : -1;
    if (k >= 0) { d.push(rows.ids[k]); }
    return d.length ? JSON.stringify(d) : '';
  }
  function focusRef(scr, n) {
    if (!scr || !n || n === scr || !scr.contains(n)) { return null; }
    var rows = focusRows(scr), sig = focusSig(n), id = focusId(rows, n);
    var all = scr.querySelectorAll(n.tagName), i = 0, k, ref, row;
    for (k = 0; k < all.length && all[k] !== n; k++) {
      if (focusSig(all[k]) === sig && focusId(rows, all[k]) === id) { i++; }
    }
    ref = { tag: n.tagName, sig: sig, i: i };
    if (id) {
      ref.id = id;
      /* A twin is captured as a twin and never restored (focusFind). Else
         its own row, then the row after it and the row before it, twins
         left out: where to go if this control is not there when the list
         comes back. */
      row = n.closest(FOCUS_ROW); k = row ? rows.els.indexOf(row) : -1;
      if (k >= 0 && rows.amb[k]) { ref.amb = true; }
      else if (k >= 0) {
        ref.near = [k, k + 1, k - 1].filter(function (j) {
          return j >= 0 && j < rows.els.length && !rows.amb[j];
        }).map(function (j) { return rows.ids[j]; });
      }
    }
    return ref;
  }
  /* The same control in the new screen, wherever its item has moved to.
     Never a disabled or hidden one. A control with no item: past the end of
     a shorter list, the last of its kind, as before. A twin's control: none,
     so the caller lands on the screen's title. A control whose item is gone,
     or has no usable control of that kind now: focusNear. */
  function focusFind(scr, ref) {
    if (!scr || !ref || ref.amb) { return null; }
    var rows = focusRows(scr), id = ref.id || '', all = scr.querySelectorAll(ref.tag), hit = [], k;
    for (k = 0; k < all.length; k++) {
      if (focusSig(all[k]) === ref.sig && focusId(rows, all[k]) === id &&
          !all[k].disabled && !all[k].closest('[hidden]')) { hit.push(all[k]); }
    }
    if (hit.length) { return hit[Math.min(ref.i, hit.length - 1)]; }
    return id ? focusNear(rows, ref.near) : null;
  }
  /* Somewhere to read from, never something to press: the item's own row if
     it is still there, else the row that took its place, else the one before
     it. A row takes focus by script only (not a Tab stop) and does nothing on
     Enter or Space. With none of them (a compare column, the last line
     removed), null, and the caller lands on the screen's title; also when
     the item's own line has gained a twin since, because which of the two is
     the item is not known. Never another item's control, so never another
     item's Remove. */
  function focusNear(rows, near) {
    var k, at, n;
    if (near && near.length && rows.ids.indexOf('row? ' + near[0].slice(4)) >= 0) { return null; }
    for (k = 0; near && k < near.length; k++) {
      at = rows.ids.indexOf(near[k]);
      n = at >= 0 ? rows.els[at] : null;
      if (!n || n.closest('[hidden]')) { continue; }
      if (!n.hasAttribute('tabindex')) { n.setAttribute('tabindex', '-1'); }
      return n;
    }
    return null;
  }
  /* During a slide the screen is moving, so nothing is scrolled to reach it;
     a repaint in place may scroll its own content to show the control. */
  function focusTo(n, scr, still) {
    if (n) {
      try { if (still) { n.focus({ preventScroll: true }); } else { n.focus(); } } catch (e) {}
      if (doc.activeElement === n) { return; }
    }
    focusLand(scr);
  }
  /* The new screen's own title, so a screen reader says where you now are:
     the large title, the bar title, or the first heading of an untitled one.
     Reachable by script only, not a Tab stop. */
  function focusLand(scr) {
    var c = [scr.querySelector('.va-lt'), scr.querySelector('.va-bt'),
             scr.querySelector('[data-body] h1, [data-body] h2')], h = null, k;
    for (k = 0; k < c.length && !h; k++) { if (c[k] && c[k].textContent.trim()) { h = c[k]; } }
    if (!h) { return; }
    if (!h.hasAttribute('tabindex')) { h.setAttribute('tabindex', '-1'); }
    try { h.focus({ preventScroll: true }); } catch (e) {}
  }
  function after(node, fn) {
    var done = false;
    function go() { if (done) { return; } done = true; fn(); }
    node.addEventListener('animationend', go, { once: true });
    setTimeout(go, 520);
  }
  function setTab(id) {
    Array.prototype.forEach.call(el.root.querySelectorAll('.va-tab'), function (a) {
      a.classList.toggle('on', a.getAttribute('data-tab') === id);
    });
  }
  /* navigator.share is the right gesture on a phone — it opens the sheet the
     person already knows and lands the text in Messages, Mail or whatever they
     actually use. The clipboard is the fallback, and there is a third fallback
     below it because a phone with neither still has to be able to hand this to
     someone.

     26 Oct 2026: the share call used to swallow every rejection, so a share
     that genuinely failed ended the tap with nothing at all: no clipboard, no
     text, no word. Now a deliberate cancel (AbortError) stays quiet, and any
     other rejection, or a share that throws before it starts, falls through to
     the clipboard and from there to the text itself. "Copied" is said only
     after the clipboard says it worked. The sheet is built at the tap, from
     the rooms, models and quantities as they stand then, and a result that
     comes back after the person has moved on (another screen, a changed room
     or quantity, a second tap) is dropped: it is neither announced nor allowed
     to take over the screen they are on now. */
  var SHEETGEN = 0;
  function copySheet(slug) {
    var names = siteNames(), name = null;
    names.forEach(function (n) { if (slugOf(n) === slug) { name = n; } });
    if (!name) { return; }
    sheetBoxClose(false);
    var text = sheetText(name);
    var gen = ++SHEETGEN, at = location.hash, scr = stack[stack.length - 1];
    function live() {
      return gen === SHEETGEN && location.hash === at && stack[stack.length - 1] === scr &&
        sheetText(name) === text;
    }
    var nav = root.navigator || {}, p;
    if (typeof nav.share !== 'function') { sheetClip(text, live, false); return; }
    try { p = nav.share({ title: name, text: text }); }
    catch (e) { sheetClip(text, live, true); return; }
    if (!p || typeof p.then !== 'function') { return; }
    p.then(null, function (e) {
      if (e && e.name === 'AbortError') { return; }
      if (live()) { sheetClip(text, live, true); }
    });
  }
  function sheetClip(text, live, shareFailed) {
    var cb = root.navigator && root.navigator.clipboard, p = null;
    if (cb && typeof cb.writeText === 'function') {
      try { p = cb.writeText(text); } catch (e) { p = null; }
    }
    /* A writeText that hands back no promise has not told us anything, and
       "copied" is a claim, so that counts as a refusal. */
    if (!p || typeof p.then !== 'function') { sheetBox(text, shareFailed); return; }
    p.then(function () {
      if (live()) { toast(shareFailed ? 'Could not share it, so the sheet is copied' : 'Sheet copied'); }
    }, function () {
      if (live()) { sheetBox(text, shareFailed); }
    });
  }
  /* The last resort is the text itself, in place under the button that asked
     for it: read-only, so a stray key cannot change a figure before it is
     handed over; already selected, so copying is one gesture; reachable from
     the keyboard, with Escape or Done putting focus back on the button. It is
     no longer a pushed screen: that screen had no address of its own, so its
     back button went past the site screen entirely. */
  function sheetBox(text, shareFailed) {
    var scr = stack[stack.length - 1];
    var btn = scr && scr.querySelector('[data-sheet-copy]');
    var acts = btn && btn.closest('.va-acts');
    var rows = Math.max(6, Math.min(16, text.split('\n').length + 1));
    var h = '<div class="va-jobsheet" data-jobsheet-box>' +
      '<p class="va-p" id="va-jobsheet-how">' +
      (shareFailed ? 'Sharing did not work here, and this browser would not copy the sheet for you. '
                   : 'This browser would not copy the sheet for you. ') +
      'It is selected below: copy it with Command-C or Ctrl-C, or touch and hold it on a phone.</p>' +
      '<textarea class="va-pre va-jobsheet-ta" data-jobsheet-text readonly rows="' + rows +
      '" aria-label="The sheet" aria-describedby="va-jobsheet-how" spellcheck="false">' + esc(text) +
      '</textarea><div class="va-acts"><button class="va-btn va-b2" type="button" data-jobsheet-done>' +
      'Done</button></div></div>';
    if (acts) { acts.insertAdjacentHTML('afterend', h); }
    else { mount({ tab: 'you', title: 'The sheet', back: true, depth: 1, html: h }, 'push'); }
    var top = stack[stack.length - 1], ta = top && top.querySelector('[data-jobsheet-text]');
    if (!ta) { return; }
    /* Focus without the browser's own minimal scroll, which on a phone left the
       field behind the tab bar; then bring the whole box to the middle. */
    try { ta.focus({ preventScroll: true }); } catch (e) { try { ta.focus(); } catch (e2) {} }
    try { ta.select(); ta.setSelectionRange(0, ta.value.length); } catch (e) {}
    var box = ta.closest('[data-jobsheet-box]');
    try { if (box && box.scrollIntoView) { box.scrollIntoView({ block: 'center' }); } } catch (e) {}
  }
  function sheetBoxClose(back) {
    var scr = stack[stack.length - 1], box = scr && scr.querySelector('[data-jobsheet-box]');
    if (!box) { return false; }
    var btn = scr.querySelector('[data-sheet-copy]');
    box.remove();
    if (back && btn) { try { btn.focus(); } catch (e) {} }
    return true;
  }
  /* 29 Oct 2026: the sheet as a file, so whoever does the job can keep the
     exact equipment list and its fit reasons with the job, or attach it
     wherever they choose. The file IS the copy text, character for character:
     built at the tap from sheetText and the rooms, models and quantities as
     they stand then, so it carries every quantity, the Rooms and Units lines,
     every figure not given and every unknown, each reason, what is not placed
     yet and the equipment-only line, and nothing else. It is made here as
     UTF-8 plain text and handed to the browser's own download: nothing is
     sent anywhere, and drawing the screen saves nothing.

     After the hand-over nothing is said. Only the browser's own download
     control knows whether the file was kept or the person cancelled, so it is
     the one that tells them; "saved" from here would be a claim this code
     cannot check. What is said is a refusal: a browser that cannot save a
     file this way (the Copy button beside it still works), or an address no
     longer on this device. A second tap within a second is the same tap.

     The name is the address in plain ASCII letters and digits, accents
     folded, everything else a single hyphen, at most 60 characters:
     "job-sheet-12-elm-street.txt". An address with nothing left after that
     (only punctuation, or written wholly in another script) is
     "job-sheet.txt"; the address, as written, is still the file's first
     line. A blank name never reaches here: siteNames() keeps no blank site. */
  var SHEETSAVING = false;
  function sheetFile(name) {
    var s = String(name == null ? '' : name);
    try { s = s.normalize('NFKD'); } catch (e) {}
    s = s.replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/, '');
    return 'job-sheet' + (s ? '-' + s : '') + '.txt';
  }
  function saveSheet(slug) {
    var names = siteNames(), name = null;
    names.forEach(function (n) { if (slugOf(n) === slug) { name = n; } });
    if (!name) { toast('That address is no longer on this device, so there is no sheet to download'); return; }
    if (SHEETSAVING) { return; }
    var text = sheetText(name), U = root.URL, a = null, url = null;
    try {
      a = doc.createElement('a');
      if (typeof root.Blob !== 'function' || !U || typeof U.createObjectURL !== 'function' ||
          !('download' in a)) { throw new Error('no file download here'); }
      url = U.createObjectURL(new root.Blob([text], { type: 'text/plain;charset=utf-8' }));
      a.href = url;
      a.download = sheetFile(name);
      a.hidden = true;
      doc.body.appendChild(a);
      a.click();
    } catch (e) {
      if (url) { try { U.revokeObjectURL(url); } catch (e2) {} }
      if (a && a.parentNode) { a.parentNode.removeChild(a); }
      toast('This browser would not save the sheet as a file. Copy it instead.');
      return;
    }
    a.parentNode.removeChild(a);
    SHEETSAVING = true;
    setTimeout(function () { SHEETSAVING = false; }, 1000);
    /* Revoked later, not at once: some browsers are still reading the file
       when click() returns. */
    setTimeout(function () { try { U.revokeObjectURL(url); } catch (e) {} }, 40000);
  }
  /* 30 Oct 2026: the sheet on paper, for the contractor handing one
     address's equipment and fit reasons to someone on site. What prints is
     the copy text itself, built at the tap by sheetText for this one address:
     every room and its figures, each placed quantity, Rooms, Units and the
     equipment figure, each piece's reasons, every "Not given" and unknown,
     the Not placed yet lines kept apart from the units, and the
     equipment-only line; no other address, and no phone. It is written into
     one plain report (the address as its heading, the rest as wrapping text)
     placed straight in <body>, and only while it is there does the print
     stylesheet hide everything else in the page: earlier screens still in
     the stack, the tab bar, buttons, drawers and overlays. Saved names go in
     as text, never as markup.

     The browser's print dialog does the rest, and nothing is said after it:
     printing, saving a PDF and cancelling look the same from here, so a
     word of success would be a guess. The report leaves when the browser
     says printing is over (afterprint) or when the person moves to another
     screen, whichever comes first; never on a timer, so it cannot vanish
     while a dialog is still open. Focus goes back to where it was only when
     printing ends on this screen: after a screen change, route() has already
     moved on, and the old button can still be in the page (sliding out, or
     kept in the stack), so focus stays where the new screen put it. A report left waiting (a browser that
     never says afterprint) is replaced by the next one. A second tap in the
     same second is the same tap. */
  var SHEETPRINTING = false, sheetPrintEnd = null;
  function printSheet(slug) {
    var names = siteNames(), name = null;
    names.forEach(function (n) { if (slugOf(n) === slug) { name = n; } });
    if (!name) { toast('That address is no longer on this device, so there is no sheet to print'); return; }
    if (SHEETPRINTING) { return; }
    var html = doc && doc.documentElement;
    if (typeof root.print !== 'function' || !doc || !doc.body || !html || !html.classList) {
      toast('This browser would not open printing here. Copy or download the sheet instead.');
      return;
    }
    if (sheetPrintEnd) { sheetPrintEnd(); }
    var lines = sheetText(name).split('\n');
    var box = doc.createElement('section'), head = doc.createElement('h1'), rest = doc.createElement('pre');
    box.className = 'va-print-sheet';
    head.textContent = lines[0];
    rest.textContent = lines.slice(1).join('\n');
    box.appendChild(head);
    box.appendChild(rest);
    var was = doc.activeElement;
    function end(restore) {
      if (sheetPrintEnd !== end) { return; }
      sheetPrintEnd = null;
      root.removeEventListener('afterprint', printed);
      root.removeEventListener('hashchange', end);
      if (box.parentNode) { box.parentNode.removeChild(box); }
      html.classList.remove('va-printing');
      if (restore !== true) { return; }
      try {
        if (was && was.isConnected && was !== doc.activeElement) { was.focus({ preventScroll: true }); }
      } catch (e) {}
    }
    function printed() { end(true); }
    sheetPrintEnd = end;
    doc.body.appendChild(box);
    html.classList.add('va-printing');
    root.addEventListener('afterprint', printed);
    root.addEventListener('hashchange', end);
    SHEETPRINTING = true;
    try { root.print(); }
    catch (e) {
      end(true);
      SHEETPRINTING = false;
      toast('This browser would not open printing here. Copy or download the sheet instead.');
      return;
    }
    /* Started after print() returns: where the dialog holds the page, a
       second click queued behind it lands inside this second. */
    setTimeout(function () { SHEETPRINTING = false; }, 1000);
  }

  function toast(msg) {
    el.toast.textContent = msg;
    el.toast.classList.add('on');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { el.toast.classList.remove('on'); }, 1700);
  }

  /* ══════════════════════════════════════════════════════════════ screens ══ */

  function gateName(g) {
    return g === 'home' ? 'The things that stay'
         : g === 'nothome' ? 'Everywhere else' : 'Venerabull Trade';
  }
  /* Every picture sits in a box with a fixed aspect ratio, so nothing on the
     page moves when one arrives — the commonest way a web app gives itself
     away. If the file is missing the box keeps its own quiet ground rather than
     showing a browser's broken-image glyph, which is the second commonest. */
  function imgBox(cls, src, alt, fallback) {
    /* Take the parent BEFORE removing the node: after this.remove() the img is
       detached and this.parentNode is null. Cost me eighteen page errors. */
    var give = "var b=this.parentNode;this.remove();if(b){b.classList.add('is-on','is-gone');}";
    var on = fallback
      ? "this.dataset.try=(this.dataset.try||'')+'1';" +
        "if(this.dataset.try==='1'){this.src='" + esc(fallback) + "';}" +
        "else{" + give + "}"
      : give;
    return '<span class="va-img ' + cls + '"><img src="' + esc(src) + '" alt="' + esc(alt || '') +
      '" loading="lazy" decoding="async" onerror="' + on + '"></span>';
  }

  function skeletonShop() {
    var t = '';
    for (var i = 0; i < 6; i++) { t += '<div class="va-sk va-sk-t"></div>'; }
    return '<div class="va-sk va-sk-l" style="width:60%"></div>' +
           '<div class="va-sk va-sk-l" style="width:42%;height:11px"></div>' +
           '<div class="va-grid" style="margin-top:22px">' + t + '</div>';
  }

  function scrShop() {
    if (!CAT) {
      return { tab: 'shop', title: 'Venerabull', large: 'Everything we carry.',
               search: true, html: skeletonShop() };
    }
    var h = rail() + seenRail();
    h += '<p class="va-p">Large equipment that does not travel by parcel van. ' +
         'Tell the app your room once and every model answers against it.</p>';
    ['home', 'nothome', 'trade'].forEach(function (g) {
      var cs = CAT.cats.filter(function (c) { return c.gate === g; });
      if (!cs.length) { return; }
      h += '<h2 class="va-h2">' + esc(gateName(g)) + '</h2><div class="va-grid">';
      cs.forEach(function (c) {
        h += '<a class="va-ct va-press" href="#/c/' + esc(c.id) + '">' +
          imgBox('', asset(c.tile || c.hero), '', asset(c.hero)) +
          '<span class="va-ct-b"><b>' + esc(c.t) + '</b><i>' +
          c.items.length + ' models</i></span></a>';
      });
      h += '</div>';
    });

    /* Every category again, as twenty-two lines you can thumb through in one
       flick. The grid is how the shop looks; this is how you get somewhere. */
    h += '<h2 class="va-h2">Shop by category</h2><div class="va-idx">' +
      CAT.cats.map(function (c) { return idxRow(c); }).join('') + '</div>';

    h += '<p class="va-set">A presentation set. Venerabull is in its first phase and is signing ' +
         'its first manufacturers — the models here are our own, shown so a maker can see the ' +
         'page their line would get. As lines are signed, their real products replace these.</p>';
    return { tab: 'shop', title: 'Venerabull', large: 'Everything we carry.', search: true,
             act: '<img class="va-mark" src="' + asset(MARK) + '" alt="">', html: h };
  }

  /* ── the rail ─────────────────────────────────────────────────────────────
     A row of chips under the search field. Every one of them NARROWS what is
     already here — a gate, your room, your shelf. None of them is a promotion,
     a deal, or a department we were paid to put in front of you, because there
     is no such thing here and there is not going to be. */
  function rail() {
    var r = roomNow(), n = shelf().length, sn = siteNames();
    var chips = [];
    if (r) {
      chips.push('<a class="va-chip is-key" href="#/fit"><svg viewBox="0 0 24 24" ' +
        'class="va-chip-i"><path d="M4 20.5h16M6.5 20.5V10m4 10.5V4.2m4 16.3v-7m4 7V7.4"/></svg>' +
        'Fits ' + esc(r.name || 'your room') + '</a>');
    } else {
      chips.push('<button class="va-chip is-key" data-room-new>Add your room</button>');
    }
    if (n) { chips.push('<a class="va-chip" href="#/shelf">Put aside <i>' + n + '</i></a>'); }
    if (sn.length) { chips.push('<a class="va-chip" href="#/site/' + esc(slugOf(sn[0])) +
      '">' + esc(sn[0]) + '</a>'); }
    ['home', 'nothome', 'trade'].forEach(function (g) {
      chips.push('<a class="va-chip" href="#/gate/' + g + '">' + esc(gateName(g)) + '</a>');
    });
    return '<div class="va-chips va-rail">' + chips.join('') + '</div>';
  }

  /* ── where you left off ───────────────────────────────────────────────────
     What THIS phone actually opened, newest first. Not "recommended for you",
     not "customers also viewed" — we have no customers yet and inventing some
     is the exact thing the ledger forbids. A record of your own last five
     taps is both true and, on a phone you pick up twice a day, more useful. */
  function seen() { return store().seen || []; }
  function noteSeen(c, m) {
    var sn = seen().filter(function (x) { return !(x.c === c && x.m === m); });
    sn.unshift({ c: c, m: m, at: Date.now() });
    put({ seen: sn.slice(0, 20) });
  }
  function seenRail() {
    var sn = seen(), r = roomNow(), cards = [];
    sn.forEach(function (x) {
      var f = itemOf(x); if (!f || cards.length >= 10) { return; }
      var v = verdict(f.it, r);
      cards.push('<a class="va-seen va-press" href="#/m/' + esc(x.c) + '/' + esc(x.m) + '">' +
        imgBox('va-seen-s', asset(f.it.img), f.it.n) +
        '<b>' + esc(f.it.n) + '</b>' +
        '<i>' + money(f.it.p) + (v ? ' · <span class="va-vt is-' + v + '">' + vWord(v) +
          '</span>' : '') + '</i></a>');
    });
    if (cards.length < 2) { return ''; }
    return '<div class="va-fsec"><h2 class="va-fh">Where you left off</h2>' +
      '<button class="va-mini" data-seen-clear>Clear</button></div>' +
      '<div class="va-hscroll">' + cards.join('') + '</div>';
  }

  /* One gate, on its own, from the rail. */
  function scrGate(g) {
    if (!CAT || !gateName(g)) { return scrLost(); }
    var cs = CAT.cats.filter(function (c) { return c.gate === g; });
    if (!cs.length) { return scrLost(); }
    var n = 0;
    cs.forEach(function (c) { n += c.items.length; });
    return { tab: 'shop', title: gateName(g), back: true, depth: 1, large: gateName(g), html:
      '<p class="va-p">' + cs.length + ' categories, ' + n + ' models.</p>' +
      '<div class="va-idx">' + cs.map(function (c) { return idxRow(c); }).join('') + '</div>' };
  }

  /* ══════════════════════════════════════════════════════════ narrowing it ══
     Thirty to thirty-six models in a category is more than a person can hold in
     their head, and every shop solves it with filters. What is worth taking
     from the big ones is the SHAPE — a row of chips at the top, tapping one
     narrows, tapping it again undoes it. What is not worth taking is what they
     filter BY: "most purchased", "4 stars and up", "top brand". We have no
     purchases and no stars, and inventing either is the one thing the ledger
     forbids outright.

     So we filter by the things that are actually true about a machine: the
     specifications the catalogue already carries per category, and whether it
     clears the room you saved. Those are the two questions somebody standing in
     a garage is actually asking. */
  var FILT = { cat: null, sel: {}, fit: false, sort: '' };

  function filtReset(id) {
    if (FILT.cat !== id) { FILT = { cat: id, sel: {}, fit: false, sort: '' }; }
  }
  function filtOn() {
    return FILT.fit || !!FILT.sort || Object.keys(FILT.sel).some(function (k) { return FILT.sel[k]; });
  }
  function filtApply(c, r) {
    var out = c.items.slice();
    Object.keys(FILT.sel).forEach(function (k) {
      var v = FILT.sel[k];
      if (!v) { return; }
      out = out.filter(function (it) { return (it.a || {})[k] === v; });
    });
    if (FILT.fit && r) {
      out = out.filter(function (it) { var d = verdict(it, r); return d === 'yes' || d === 'tight'; });
    }
    if (FILT.sort === 'up') { out.sort(function (a, b) { return (a.p || 0) - (b.p || 0); }); }
    else if (FILT.sort === 'down') { out.sort(function (a, b) { return (b.p || 0) - (a.p || 0); }); }
    return out;
  }

  function scrCategory(id) {
    var c = BYID[id];
    if (!c) { return scrLost(); }
    filtReset(id);
    var r = roomNow();
    var shown = filtApply(c, r);

    var h = '<p class="va-p">' + esc(c.line) + '</p>';

    /* The chips. The room chip first, because it is the one that matters, then
       price order, then one chip per specification the catalogue holds for this
       category. A chip that would leave nothing behind is not drawn at all —
       a filter that empties the screen is a trap, not a tool. */
    var chips = [];
    if (r) {
      chips.push('<button class="va-chip' + (FILT.fit ? ' on' : '') + '" data-f-fit>' +
        'Fits ' + esc(r.name || 'your room') + '</button>');
    }
    chips.push('<button class="va-chip' + (FILT.sort ? ' on' : '') + '" data-f-sort>' +
      (FILT.sort === 'up' ? 'Price, low first'
       : FILT.sort === 'down' ? 'Price, high first' : 'Price') + '</button>');
    (c.f || []).forEach(function (f) {
      var key = f[0], cur = FILT.sel[key];
      chips.push('<button class="va-chip' + (cur ? ' on' : '') + '" data-f-open="' + esc(key) + '">' +
        esc(cur || key) + '<svg class="va-chip-c" viewBox="0 0 24 24">' +
        '<path d="M6 9.5 12 15.5 18 9.5"/></svg></button>');
    });
    h += '<div class="va-chips va-rail" data-frow>' + chips.join('') + '</div>';

    h += '<div class="va-fbar">' +
      '<span>' + shown.length + (shown.length === 1 ? ' model' : ' models') +
      (shown.length !== c.items.length ? ' of ' + c.items.length : '') + '</span>' +
      (filtOn() ? '<button class="va-mini" data-f-clear>Clear</button>' : '') + '</div>';

    if (!shown.length) {
      h += '<div class="va-card"><p class="va-sm">Nothing in ' + esc(c.t) +
        ' matches all of that. Take one of the chips off and it will.</p>' +
        '<button class="va-btn va-b2 va-wide" style="margin-top:14px" data-f-clear>' +
        'Clear the filters</button></div>';
    } else {
      h += '<div class="va-rows" data-flist>' +
        shown.map(function (it) { return row(c, it, r); }).join('') + '</div>';
    }
    return { tab: 'shop', title: c.t, large: c.t, back: true, search: true, html: h };
  }

  /* The value picker for one specification, as a sheet from the bottom —
     the same gesture as her, because it is the same kind of thing: something
     that comes up over the screen and goes away again. */
  function filtSheet(key) {
    var c = BYID[FILT.cat]; if (!c) { return; }
    var f = null;
    (c.f || []).forEach(function (x) { if (x[0] === key) { f = x; } });
    if (!f) { return; }
    var cur = FILT.sel[key];
    if (ASK.on) { ASK.on = false; hush(); if (VOICE.on) { voiceStop(); } }
    var sh = sheetBegin('filter');
    if (!sh) { return; }
    sh.innerHTML = '<div class="va-sheet-bd" data-filt-close></div>' +
      '<div class="va-sheet-w is-short" role="dialog" aria-modal="true" aria-label="' +
        esc(key) + '">' +
      '<div class="va-sheet-h">' +
        '<button class="va-ico" data-filt-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24"><path d="M6 9.5 12 15.5 18 9.5"/></svg></button>' +
        '<span class="va-sheet-t"><b>' + esc(key) + '</b></span>' +
        (cur ? '<button class="va-mini" data-f-pick="' + esc(key) + '|">Any</button>'
             : '<span class="va-sheet-sp"></span>') +
      '</div>' +
      '<div class="va-sheet-b"><div class="va-opts">' +
        f[1].map(function (v) {
          var n = c.items.filter(function (it) { return (it.a || {})[key] === v; }).length;
          return '<button class="va-opt' + (v === cur ? ' on' : '') + '" data-f-pick="' +
            esc(key) + '|' + esc(v) + '"' + (n ? '' : ' disabled') + '>' +
            '<b>' + esc(v) + '</b><i>' + n + '</i></button>';
        }).join('') + '</div><div class="va-mo-pad"></div></div></div>';
    sheetShow(sh);
  }
  function filtClose() {
    if (ASK.on) { askClose(); return; }
    sheetEnd();
  }

  function row(c, it, r) {
    var v = verdict(it, r);
    return '<a class="va-row va-press" href="#/m/' + esc(c.id) + '/' + esc(it.id) + '">' +
      imgBox('', asset(it.img), '') +
      '<span class="va-row-b"><b>' + esc(it.n) + '</b><i>' + esc(it.b) + '</i>' +
        '<span class="va-row-f"><span class="va-row-p">' + money(it.p) + '</span>' +
        (v ? '<span class="va-v is-' + v + '">' + VWORD[v] + '</span>' : '') +
        '</span></span></a>';
  }

  function scrModel(cid, mid) {
    var c = BYID[cid]; if (!c) { return scrLost(); }
    var it = null, i;
    for (i = 0; i < c.items.length; i++) { if (c.items[i].id === mid) { it = c.items[i]; } }
    if (!it) { return scrLost(); }
    var r = roomNow(), kept = onShelf(cid, mid);
    noteSeen(cid, mid);

    var h = gallery(it) +
      '<p class="va-k" style="margin-top:0">' + esc(c.t) + '</p>' +
      '<h1 class="va-h1">' + esc(it.n) + '</h1>' +
      '<p class="va-p">' + esc(it.b) + '</p>' +
      '<p class="va-price">' + money(it.p) + '</p>' +
      '<div class="va-acts">' +
        '<button class="va-btn va-b2" data-keep="' + esc(cid) + '|' + esc(mid) + '">' +
          (kept ? 'On your shelf' : (r ? 'Put aside for ' + esc(r.name || 'this room') : 'Put aside')) +
        '</button>' +
        '<a class="va-btn va-b1" href="tel:' + esc(cfg('tel') || '') + '">Ask a person</a>' +
      '</div>';

    /* Once it is on the shelf, how many and for which room. This is the part
       that turns a wishlist into a specification: on a walk-through you are
       counting units per room, not saving one of each thing you like. */
    if (kept) { h += stepper(cid, mid); }

    h += '<label class="va-cmpk"><input type="checkbox" data-cmp="' + esc(cid) + '|' + esc(mid) +
      '"' + (onCmp(cid, mid) ? ' checked' : '') + '><span>Compare this against another</span>' +
      (cmp().length > 1 ? '<a class="va-mini" href="#/cmp">See ' + cmp().length + '</a>' : '') +
      '</label>';

    h += '<h2 class="va-h2" id="va-fit">Will it fit?</h2>';
    if (!r) {
      h += '<div class="va-card"><p class="va-sm">Tell the app your room once — the doorway, ' +
        'the ceiling, the circuit — and this answers for every model instead of you working ' +
        'it out each time.</p>' +
        '<a class="va-btn va-b1 va-wide" style="margin-top:15px" href="#/fit">Add your room</a></div>';
    } else {
      var rows = withTbc(it, r, fitRows(it, r));
      if (!rows.length) {
        h += '<div class="va-card"><p class="va-sm">Nothing in ' + esc(r.name || 'your room') +
          ' bears on this one. Add a doorway or a ceiling and it will.</p></div>';
      } else {
        h += '<div class="va-fit">' + rows.map(function (x) {
          return '<div class="va-fitrow is-' + x.k + '"><b>' + esc(x.t) + '</b><span>' +
            esc(x.s) + '</span></div>';
        }).join('') + '</div>';
      }
    }

    h += '<h2 class="va-h2" id="va-spec">Specification</h2><dl class="va-spec">' +
      Object.keys(it.a || {}).map(function (k) {
        return '<div><dt>' + esc(k) + '</dt><dd>' + esc(it.a[k]) + '</dd></div>';
      }).join('') + tbcSpec(it) + '</dl>';

    /* ── ask about THIS one ─────────────────────────────────────────────────
       Straight off the page, with the questions already written, because the
       question a person has while looking at a machine is about the machine
       they are looking at. She opens with it already in hand.

       This is where a shop normally puts customer reviews. We have no
       customers, so there are none, and there is not going to be an invented
       one — not a star, not a count, not a "customers say" paragraph. What we
       have instead is somebody who knows the specifications and will say when
       she does not know. That is a smaller promise and a true one. */
    h += '<h2 class="va-h2" id="va-ask">Looking for something specific?</h2>' +
      '<div class="va-card">' +
        '<button class="va-find va-wide" data-ask-here>' +
          ASKMARK + '<span>Ask ' + esc(asstName()) + ' about this one</span></button>' +
        '<div class="va-asks" style="margin-top:12px">' +
          ['Will this fit through my door?', 'What circuit does this need?',
           'How does this get delivered?'].map(function (q) {
            return '<button class="va-ask" data-ask-here="' + esc(q) + '">' + esc(q) + '</button>';
          }).join('') + '</div>' +
        '<p class="va-sm" style="margin-top:13px">No ratings and no reviews here, on purpose. ' +
        'Venerabull has not sold this to anybody yet, so there is nothing honest to put in ' +
        'their place.</p>' +
      '</div>';

    /* ── the rest of the category, measured the same way ────────────────── */
    var sibs = c.items.filter(function (x) { return x.id !== it.id; });
    if (r) {
      /* RANK answers "which single word describes this?" - which is right for
         rolling a set of checks up into a headline and wrong for an ordered
         list. (Its first version ranked ask lowest.) Sorting on it put every model we CANNOT
         answer for at the front of a rail whose own heading promises the ones
         that clear your room first. Two different questions; this is the
         second one, so it gets its own weight. */
      sibs.sort(function (a, b) {
        var wa = SHOWRANK[verdict(a, r) || 'ask'], wb = SHOWRANK[verdict(b, r) || 'ask'];
        return wa - wb || Math.abs((a.p || 0) - (it.p || 0)) - Math.abs((b.p || 0) - (it.p || 0));
      });
    } else {
      sibs.sort(function (a, b) {
        return Math.abs((a.p || 0) - (it.p || 0)) - Math.abs((b.p || 0) - (it.p || 0));
      });
    }
    sibs = sibs.slice(0, 6);
    if (sibs.length) {
      h += '<h2 class="va-h2" id="va-near">Others in ' + esc(c.t) + '</h2>' +
        (r ? '<p class="va-sm" style="margin:-6px 0 12px">Ordered by what clears ' +
             esc(r.name || 'your room') + ' first.</p>' : '') +
        '<div class="va-hscroll">' + sibs.map(function (x) {
          var v = verdict(x, r);
          return '<a class="va-seen va-press" href="#/m/' + esc(c.id) + '/' + esc(x.id) + '">' +
            imgBox('va-seen-s', asset(x.img), x.n) +
            '<b>' + esc(x.n) + '</b><i>' + money(x.p) +
            (v ? ' · <span class="va-vt is-' + v + '">' + vWord(v) + '</span>' : '') +
            '</i></a>';
        }).join('') + '</div>';
    }

    h += '<p class="va-set">Part of a presentation set, not stock. Venerabull is signing its ' +
         'first manufacturers; real models replace these as lines are signed.</p>';

    /* The jump bar. Amazon pins Top / Details / Explore / Reviews to the top of
       a long product page and it is the right idea — a specification page is
       long by nature and scrolling past the same three sections every time is
       a tax. Ours names the four things that are actually on the page. */
    var jump = '<div class="va-jump" data-jump>' +
      [['top', 'Top'], ['va-fit', 'Fit'], ['va-spec', 'Spec'], ['va-ask', 'Ask'],
       ['va-near', 'Others']].map(function (j) {
        return '<button class="va-jb" data-jump-to="' + j[0] + '">' + j[1] + '</button>';
      }).join('') + '</div>';

    return { tab: 'shop', title: it.n, back: true, html: h, under: jump };
  }

  /* ── the gallery ──────────────────────────────────────────────────────────
     Eight of 765 models carry more than one photograph, and where they do it is
     worth showing properly: a swipe, snapped, with dots. Where they do not, one
     picture and no dots — an empty carousel with a single slide is a promise of
     more that is not kept. */
  function gallery(it) {
    var shots = [it.img].concat(it.g || []).filter(Boolean);
    if (shots.length < 2) { return imgBox('va-hero', asset(it.img), it.n); }
    return '<div class="va-gal" data-gal>' +
      '<div class="va-gal-t" data-gal-track>' + shots.map(function (f, i) {
        return imgBox('va-gal-s', asset(f), it.n + (i ? ' — view ' + (i + 1) : ''));
      }).join('') + '</div>' +
      '<div class="va-gal-d" data-gal-dots>' + shots.map(function (f, i) {
        return '<i' + (i ? '' : ' class="on"') + '></i>';
      }).join('') + '</div></div>';
  }

  /* ── how many, and where it goes ─────────────────────────────────────── */
  function stepper(cid, mid) {
    var x = shelfAt(cid, mid) || {}, rs = rooms(), q = qtyOf(x);
    var h = '<div class="va-step">' +
      '<div class="va-step-q">' +
        '<button class="va-qb" data-q="' + esc(cid) + '|' + esc(mid) + '|-1" aria-label="One fewer">−</button>' +
        '<b data-q-out>' + q + '</b>' +
        '<button class="va-qb" data-q="' + esc(cid) + '|' + esc(mid) + '|1" aria-label="One more">+</button>' +
      '</div>';
    if (rs.length) {
      h += '<label class="va-step-r"><span>Room</span><select data-assign="' +
        esc(cid) + '|' + esc(mid) + '">' +
        '<option value=""' + (roomOf(x) ? '' : ' selected') + '>Not assigned</option>' +
        rs.map(function (rr) {
          return '<option value="' + esc(rr.id) + '"' + (rr.id === roomOf(x) ? ' selected' : '') + '>' +
            esc(rr.name || 'Room') + (rr.site ? ' · ' + esc(rr.site) : '') + '</option>';
        }).join('') + '</select></label>';
    }
    return h + '</div>';
  }

  /* ══════════════════════════════════════════════════════ the job sheet ══
     One address, its rooms, and what is specified in each — with every model
     answered against the room it is actually going into rather than against
     whichever room happened to be selected. This is the screen a contractor
     hands to somebody, so it has to be readable off a phone held sideways in a
     driveway and it has to be copyable as plain text.

     WHAT IT IS NOT. It is not a quote. There is no freight on it, no tax, no
     lead time and no terms, because none of those are true yet and the ledger
     forbids implying they are. It says so on its face. */
  /* Each site-sheet render takes the next number, and its heading ids carry
     it: going back from a model page can leave an earlier layer of the same
     sheet in the page beside a fresh one, and ids shared between the two
     would name a group by the other layer's heading. */
  var sheetRenders = 0;
  function scrSite(slug) {
    var names = siteNames(), name = null;
    names.forEach(function (n) { if (slugOf(n) === slug) { name = n; } });
    if (!name) { return scrLost(); }
    var rs = roomsAt(name), h = '', t = sheetTally();

    /* 29 Oct 2026, the sheet's structure (web-run GAPS items 2 and 3): the
       site is the page's h1, each room an h2, each placed piece an h3, each
       group of its reasons an h4, so an installer can jump room to room,
       piece to piece and to a piece's Needs attention, Unknown or Passed.
       A piece and its reasons sit together in one group named by the
       piece's heading. The piece heading wraps the row's link whole, so the
       link's name and target are exactly as they were. A piece the catalogue
       cannot name is still an h3, with its saved reference and no reasons.
       Ids come from this render's number, the address's slug and the room
       and piece positions, never from saved text, so each render's ids are
       its own even when an earlier render of the same sheet is still in
       the page. */
    var sid = 'vs' + (++sheetRenders) + '-' + slugOf(name);
    rs.forEach(function (r, ri) {
      var mine = specIn(r.id);
      h += '<div class="va-site-r"><h2 class="va-h2">' + esc(r.name || 'Room') + '</h2>' +
        '<p class="va-sm">' + (esc(roomFigs(r).join(' · ')) || 'No figures set') + '</p>';
      if (!mine.length) {
        h += '<p class="va-sm va-dim">Nothing specified here yet.</p></div>';
        return;
      }
      h += '<div class="va-rows">' + mine.map(function (x, pi) {
        var f = itemOf(x), q = qtyOf(x), line = sheetCount(t, x, f), pid = sid + '-' + ri + '-' + pi;
        if (!f) { return '<h3 class="va-ph" id="' + pid + '">' + gapRow(x, q, '', 'span') + '</h3>'; }
        var a = fitAssess(f.it, r);
        return '<div class="va-piece" role="group" aria-labelledby="' + pid + '">' +
          '<h3 class="va-ph" id="' + pid + '">' +
          '<a class="va-lrow va-press" href="#/m/' + esc(x.c) + '/' + esc(x.m) + '">' +
          '<b>' + (q > 1 ? q + ' × ' : '') + esc(f.it.n) + '</b>' +
          '<i>' + esc(f.c.t) + ' · ' + (line == null ? 'Price unknown' : money(line)) +
          ' · <span data-fit-head><span class="va-vt' + (a.v ? ' is-' + a.v : '') + '">' + esc(a.word) +
          '</span>' + (a.part ? ' · ' + esc(a.part) : '') + '</span></i></a></h3>' + fitWhy(a, 4) + '</div>';
      }).join('') + '</div></div>';
    });

    var short = sheetShort(t), loose = sheetLoose();
    h += '<dl class="va-spec va-site-t">' +
      dline('Rooms', String(rs.length)) +
      dline('Units', String(t.units)) +
      (!t.n ? dline('Equipment', 'Nothing placed yet') : !short ? dline('Equipment', money(t.total))
        : dline(t.known ? 'Equipment subtotal' : 'Equipment', t.known ? money(t.total) + ' — incomplete' : 'Unknown') +
          dline('Not counted', short)) + '</dl>' +
      (short ? '<p class="va-note" data-sheet-gap>Every piece stays on your list ' +
        'exactly as you saved it. Details and prices come from the catalogue on this device: once it ' +
        'has updated, which happens when the app is open with a connection, reopen this sheet. A piece ' +
        'that still has no details may have changed in the catalogue, so confirm it with us before ' +
        'relying on this sheet.</p>' : '') +
      (loose.rows.length ? '<div class="va-site-r"><h2 class="va-h2">Not placed yet</h2>' +
        '<p class="va-sm" data-sheet-loose>' + esc(loose.say) + '</p><div class="va-rows">' +
        loose.rows.map(function (o) {
          var w = o.why.charAt(0).toUpperCase() + o.why.slice(1);
          if (!o.f) { return gapRow(o.x, o.q, w); }
          return '<a class="va-lrow va-press" href="#/m/' + esc(o.x.c) + '/' + esc(o.x.m) + '">' +
            '<b>' + (o.q > 1 ? o.q + ' × ' : '') + esc(o.f.it.n) + '</b><i>' + esc(o.f.c.t) + ' · ' +
            (o.line == null ? 'Price unknown' : money(o.line)) + ' · ' + esc(w) + '</i></a>';
        }).join('') + '</div></div>' : '') +
      '<div class="va-acts"><button class="va-btn va-b2" data-sheet-copy="' + esc(slugOf(name)) +
      '">Copy the sheet</button><button class="va-btn va-b2" type="button" data-sheet-save="' +
      esc(slugOf(name)) + '">Download the sheet</button><button class="va-btn va-b2" type="button" ' +
      'data-sheet-print="' + esc(slugOf(name)) + '">Print the sheet</button></div>' +
      '<p class="va-note">Equipment only. No freight, no tax, no lead time — none of those ' +
      'are settled yet, and a sheet that implied they were would be worse than no sheet. ' +
      'Kept on this device.</p>';

    return { tab: 'you', title: name, back: true, large: name, html: h };
  }
  /* One state, one word. This used to answer "Cannot say" where VWORD answered
     "Ask", so the same model read one way on a card and another on a rail six
     pixels below it. "Ask" wins because it fits a 10.5px pill at every width
     and the fuller explanation already lives in the sentence underneath it on
     the model screen. Reads from VWORD now rather than repeating it, so there
     is exactly one place to change a verdict word. */
  function vWord(v) { return VWORD[v] || VWORD.ask; }
  function slugOf(n) {
    return String(n || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
  /* Plain text, because it has to survive being pasted into a message, an
     email, or a supplier's order form without carrying any markup with it.

     26 Oct 2026, Nasser's direction: the sheet is for the person RECEIVING
     it. It used to carry the equipment and one word per piece, [Ask] or
     [Will not], and left out the route in and every reason, so a plan could
     be handed over without saying the route is a full flight or what is
     still to be resolved. Now each room line carries the route in when it
     was given and names the figures that were not, and under each piece go
     the engine's own rows that need attention (will not, tight, ask): the
     room-specific label and the sentence the model screen shows, as written.
     Nothing is decided here. The rows are fitRows(), the word is verdict(),
     and a piece no check reached says it has not been checked, so silence
     never reads as a fit. Quantities, the total and the equipment-only line
     are as they were. Anything typed into a room is folded onto its own
     line, so a name cannot add lines to the text or reorder it.
     27 Oct 2026: the reasons under each piece now come from fitAssess(),
     below, which the site screen reads too: every row, grouped, and every
     check not made. */
  /* 27 Oct 2026, Nasser's direction: a saved line the catalogue on this
     device could not answer for used to vanish from the screen and from the
     sheet, and a missing price counted as $0, so a buyer or an installer
     could be handed fewer pieces under a total that looked complete. Now
     every saved line stays in its room with its quantity. One the catalogue
     cannot name shows only what it was saved with (and the category's own
     title when that much is known) and says its price and fit are unknown;
     no name, price or availability is made up. A price that is missing, not
     a number, or not above zero is unknown, never zero. A sum with anything
     unknown in it is a subtotal, says so, and says how many lines it leaves
     out. Nothing here changes what is saved. The sheet's last line is the
     brand alone: the number it used to carry is not yet a verified line. */
  function priceOf(it) {
    var p = it && it.p;
    return (typeof p === 'number' && isFinite(p) && p > 0) ? p : null;
  }
  function sheetTally() { return { total: 0, known: 0, n: 0, units: 0, gone: 0, bare: 0, gq: 0 }; }
  function sheetCount(t, x, f) {
    var q = qtyOf(x), p = f ? priceOf(f.it) : null;
    t.n++; t.units += q;
    if (p == null) { t.gq += q; if (f) { t.bare++; } else { t.gone++; } return null; }
    t.known++; t.total += p * q;
    return p * q;
  }
  function sheetShort(t) {
    var miss = t.gone + t.bare, why = [];
    if (!miss) { return ''; }
    if (t.gone) { why.push(t.gone + ' with details unavailable'); }
    if (t.bare) { why.push(t.bare + ' with no price'); }
    return miss + ' of ' + t.n + (t.n === 1 ? ' line' : ' lines') +
      (t.gq > miss ? ' (' + t.gq + ' units)' : '') + ': ' + why.join(', ');
  }
  function gapRef(x) {
    function cut(s) {
      s = sheetLine(s).replace(/\s+/g, ' ').trim();
      return s.length > 60 ? s.slice(0, 59) + '…' : s;
    }
    var c = x && Object.prototype.hasOwnProperty.call(BYID, x.c) ? BYID[x.c] : null;
    var t = c && typeof c.t === 'string' ? cut(c.t) : '';
    var m = cut(x && x.m), ref = t ? m : [cut(x && x.c), m].filter(Boolean).join(' / ');
    return (t ? t + ', ' : '') + (ref ? 'saved as “' + ref + '”' : 'no saved reference');
  }
  /* `el` is "span" only where the row sits inside a piece heading (a placed
     piece on the site sheet); everywhere else it stays a div, as before. */
  function gapRow(x, q, tag, el) {
    var e = el === 'span' ? 'span' : 'div';
    return '<' + e + ' class="va-lrow" data-sheet-gap-row><b>' + (q > 1 ? q + ' × ' : '') +
      'Details unavailable</b><i>' + esc(gapRef(x)) + ' · Price unknown · Fit unknown' +
      (tag ? ' · ' + esc(tag) : '') + '</i></' + e + '>';
  }
  /* 27 Oct 2026, Nasser's direction: the saved lines in no room (none was
     chosen, or the room has since been deleted) showed on the site screen
     with a unit price and no count, and the copied sheet left them out. One
     selection now feeds both, in saved order, every row kept apart (two
     identical lines stay two), each with its count and its line price
     (unknown when the price is). It keeps its own count, because these are
     not placed at this address: nothing here enters the site's units or
     equipment figure. A line whose room exists at another address is placed
     there and is not selected. */
  function sheetLoose() {
    var rs = rooms(), lt = sheetTally(), out = [];
    shelf().forEach(function (x) {
      if (!x) { return; }
      var rm = roomOf(x);
      if (rm && rs.some(function (r) { return r.id === rm; })) { return; }
      var f = itemOf(x);
      out.push({ x: x, f: f, q: qtyOf(x), line: sheetCount(lt, x, f),
                 why: rm ? 'its room was deleted' : 'no room chosen' });
    });
    return { rows: out, t: lt, say: !out.length ? '' :
      lt.n + (lt.n === 1 ? ' line, ' : ' lines, ') + lt.units + (lt.units === 1 ? ' unit' : ' units') +
      ', in no room: not placed at this address, and not in the units or equipment above.' };
  }
  /* 27 Oct 2026, Nasser's direction: one assessment per placed piece, read
     by the site screen and the copied sheet alike, so a builder is told the
     same thing in both. Before, the screen showed one word and no reasons,
     the copy listed only the rows needing attention, a piece no check
     reached read [Ask], and a Fits or a Tight with checks still open read
     as settled.

     Nothing is decided here. The word is verdict(), exactly, or "Not
     checked" when the engine had nothing to judge. The reasons are the
     engine's own rows from fitRows(), in its words, grouped as a builder
     acts on them: needs attention (will not, tight), unknown (ask), passed
     (fits); within a group, the engine's order: the doorway, the ceiling,
     the supply, the route in, the slab. Each check the engine did not make is an
     unknown too, saying what it would decide. "Not given" when the room
     lacks the figure and the engine would check THIS piece against it,
     found by asking the engine about a room holding only that figure (a
     room never shown or judged), so a missing ceiling is not raised for a
     piece with no ceiling figure. "Not on our sheet" when the room gives a
     ceiling, a supply or a slab and the catalogue holds nothing for the piece to
     check it against. The doorway never lands there, because the engine
     says itself when a width is missing, and "No stairs" leaves nothing to
     carry. A Fits or a Tight with anything unknown says "partly checked".
     Pure: the piece's attributes and the room's figures in, strings out. */
  function fitAssess(it, r) {
    var CHECKS = [
      { w: 'the doorway', on: 'door', probe: { door: 1 },
        room: 'No doorway width is given for this room, so whether it goes through the door is not checked.' },
      { w: 'the ceiling', on: 'ceiling', probe: { ceiling: 1 },
        room: 'No ceiling height is given for this room, so whether it stands under the ceiling is not checked.',
        piece: 'Our sheet gives no ceiling figure for this piece, so it is not checked against this room\'s ceiling.' },
      { w: 'the supply', on: 'supply', probe: { supply: 'Not sure yet' },
        room: 'No supply is given for this room, so whether the power here runs it is not checked.',
        piece: 'Our sheet gives no power requirement for this piece, so it is not checked against this room\'s supply.' },
      { w: 'the route in', on: 'stairs', probe: { stairs: 'A full flight' },
        room: 'This room does not say whether there are stairs on the way in, so how it is carried in is not checked.' },
      /* 30 Oct 2026: the engine itself answers the slab for every piece whose
         sheet gives one, room figure or not, so "Not given" never arises here;
         the room sentence is a guard. What this adds is "Not on our sheet":
         a slab the room gives is never dropped silently from a piece. */
      { w: 'the slab', on: 'slab', probe: { slab: 1 },
        room: 'No slab thickness is given for this room, so whether the slab is suitable is not checked.',
        piece: 'Our sheet gives no slab figure for this piece, so it is not checked against this room\'s slab.' }];
    var GROUP = { no: 0, tight: 0, ask: 1, yes: 2 }, LABEL = ['Needs attention', 'Unknown', 'Passed'];
    var room = r || {}, rows = fitRows(it, room), v = verdict(it, room), found = [];
    var has = function (list, w) { return list.some(function (x) { return x.w === w; }); };
    rows.forEach(function (x, i) {
      var k = RANK[x.k] ? x.k : 'ask', at = CHECKS.length + i;
      CHECKS.forEach(function (c, j) { if (c.w === x.w) { at = j; } });
      found.push({ g: GROUP[k], o: at, i: i, k: k, mark: vWord(k), t: x.t, s: x.s });
    });
    CHECKS.forEach(function (c, j) {
      if (has(rows, c.w)) { return; }
      var miss = !room[c.on];
      if (miss ? !has(fitRows(it, c.probe), c.w) : !c.piece) { return; }
      found.push({ g: 1, o: j, i: rows.length + j, k: null, mark: miss ? 'Not given' : 'Not on our sheet',
                   t: c.w.charAt(0).toUpperCase() + c.w.slice(1), s: miss ? c.room : c.piece });
    });
    found.sort(function (a, b) { return (a.g - b.g) || (a.o - b.o) || (a.i - b.i); });
    var why = found.map(function (x) {
      var t = sheetLine(x.t), s = sheetLine(x.s);
      return { g: LABEL[x.g], k: x.k, mark: x.mark, t: t, s: s,
               line: LABEL[x.g] + ': [' + x.mark + '] ' + t + ': ' + s };
    });
    var word = v ? vWord(v) : 'Not checked';
    var part = (v === 'yes' || v === 'tight') && why.some(function (x) { return x.g === LABEL[1]; })
      ? 'partly checked' : '';
    return { v: v, word: word, part: part, head: word + (part ? ' · ' + part : ''), why: why };
  }
  /* The same reasons on the screen, in the model page's own fit-row styles:
     each group headed once, each row its mark, its label and its sentence.
     29 Oct 2026: each group is one wrapper, its heading first, so the gap
     between groups is the wrapper's and a heading sits with its own rows,
     never nearer the group before. `level` is the heading level under the
     piece's heading; the site sheet (its only caller) passes 4. */
  function fitWhy(a, level) {
    var hx = 'h' + (level === 5 || level === 6 ? level : 4), groups = [], cur = null;
    a.why.forEach(function (x) {
      if (!cur || x.g !== cur.g) { cur = { g: x.g, h: '' }; groups.push(cur); }
      cur.h += '<div class="va-fitrow is-' + (x.k || 'ask') + '" data-fit-why="' + esc(x.g) + '"><b>' +
        esc(x.mark + ' · ' + x.t) + '</b><span>' + esc(x.s) + '</span></div>';
    });
    return groups.length ? '<div class="va-fit" data-fit>' + groups.map(function (g) {
      return '<div class="va-fitg"><' + hx + ' class="va-fgh">' + esc(g.g) + '</' + hx + '>' + g.h + '</div>';
    }).join('') + '</div>' : '';
  }
  /* A room's figures in the copied sheet's order, the route in included
     ("No stairs" too, which no check would otherwise say), for the screen
     and the copy alike. */
  function roomFigs(r) {
    return [r.door ? r.door + ' in door' : '', r.ceiling ? feet(r.ceiling) + ' ceiling' : '',
            r.stairs ? 'route in: ' + r.stairs : '', r.supply || '',
            r.slab ? r.slab + ' in slab' : ''].filter(Boolean).map(sheetLine);
  }
  function sheetLine(s) {
    return String(s == null ? '' : s)
      .replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029\u200e\u200f\u202a-\u202e\u2066-\u2069]+/g, ' ');
  }
  function sheetText(name) {
    var rs = roomsAt(name), out = [sheetLine(name).trim(), ''], t = sheetTally();
    rs.forEach(function (r) {
      out.push(sheetLine(r.name).trim() || 'Room');
      var figs = roomFigs(r).join(', ');
      if (figs) { out.push('  ' + sheetLine(figs)); }
      var gone = [r.door ? '' : 'doorway', r.ceiling ? '' : 'ceiling', r.stairs ? '' : 'route in',
                  r.supply ? '' : 'supply'].filter(Boolean);
      if (gone.length) { out.push('  Not given: ' + gone.join(', ')); }
      var mine = specIn(r.id);
      if (!mine.length) { out.push('  (nothing specified)'); }
      mine.forEach(function (x) {
        var f = itemOf(x), q = qtyOf(x), line = sheetCount(t, x, f);
        if (!f) {
          out.push('  ' + q + ' x Details unavailable (' + gapRef(x) + ')  Price unknown  [Fit unknown]');
          out.push('    Its details were not available when this sheet was made: confirm the piece, ' +
            'its price and its fit before relying on this line.');
          return;
        }
        var a = fitAssess(f.it, r);
        out.push('  ' + q + ' x ' + sheetLine(f.it.n) + '  ' + (line == null ? 'Price unknown' : money(line)) +
          '  [' + a.head + ']');
        a.why.forEach(function (w) { out.push('    ' + w.line); });
      });
      out.push('');
    });
    var short = sheetShort(t), loose = sheetLoose();
    /* 29 Oct 2026: the screen's Rooms and Units lines, copied too, so whoever
       receives the sheet can reconcile the whole job at a glance — and so the
       "not in the units … above" of the Not placed yet section points at a
       units figure that is there. The same figures as the screen, from the
       same reading: the rooms at this address, and the units the tally above
       counted line by line (every placed line, priced or not; nothing in no
       room and nothing at another address). */
    out.push('Rooms ' + rs.length);
    out.push('Units ' + t.units);
    out.push(!t.n ? 'Nothing placed yet' : !short ? 'Equipment total ' + money(t.total)
      : t.known ? 'Equipment subtotal ' + money(t.total) + ' — incomplete' : 'Equipment total unknown');
    if (short) { out.push('  Not counted: ' + short + '.'); }
    if (loose.rows.length) {
      out.push('', 'Not placed yet', '  ' + loose.say);
      loose.rows.forEach(function (o) {
        out.push('  ' + o.q + ' x ' + (o.f ? sheetLine(o.f.it.n) : 'Details unavailable (' + gapRef(o.x) + ')') +
          '  ' + (o.line == null ? 'Price unknown' : money(o.line)) + (o.f ? '' : '  [Fit unknown]') +
          '  (' + o.why + ')');
      });
      out.push('');
    }
    out.push('Equipment only — no freight, tax or lead time.');
    out.push('Venerabull');
    return out.join('\n');
  }

  function scrLost() {
    return { tab: 'shop', title: '', back: true, html:
      '<div class="va-empty"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/>' +
      '<path d="M9 9.5a3 3 0 0 1 5.8 1c0 2-3 2.5-3 2.5M12 17h.01"/></svg>' +
      '<h1 class="va-h1">That is not here.</h1>' +
      '<p class="va-p">The link may be older than the catalogue.</p>' +
      '<a class="va-btn va-b1" href="#/shop">Everything we carry</a></div>' };
  }
  /* The mark is struck in three metals and the site already uses all three.
     The app picks the one that matches the register rather than carrying the
     bronze one into a graphite room. */
  var MARKS = { trade: 'venerabull-symbol-steel.png',
                supplier: 'venerabull-symbol-patina.png' };
  function markFile() { return MARKS[acct()] || 'venerabull-symbol-bronze.png'; }
  var MARK = markFile();

  /* ══════════════════════════════════════════════════════════════════ find ══
     The SAME engine the site's search field runs — assets/vb-find.js, against
     assets/vb-search-index.json. Not a second search that ranks things
     differently: the app and the website give one person one answer for one
     query, or they are two shops arguing in front of a customer.

     The index is 282 KB (42 KB on the wire) and goes into the vault like
     everything else, so the search field works in a basement. That is not a
     small thing: search is the first thing every other shopping app loses
     when the signal does.

     WHAT IS NOT SHOWN. Position 5 of an item row was a star rating, 4.9 on the
     first one. It was invented and on the ledger's never-claim list; it is null
     on every row now and the catalogue bench fails if it ever is not. The app
     prints a name, a category and a price. */
  var IX = null, IXWARM = false, FQ = '', FT = null;

  function findReady() {
    if (IX) { return Promise.resolve(IX); }
    if (!root.VB_FIND) { return Promise.resolve(null); }
    var url = cfg('index');
    if (!url) { return Promise.resolve(null); }
    /* A kept index is only good for the catalogue it was built from. It used
       to be kept forever, so a phone that had searched once went on finding
       the old names and the old figures after the catalogue changed under it.
       Now it carries the catalogue's version, and a kept copy with no version,
       or with a different one from the catalogue on screen, is a miss. The
       network decides; only when the network cannot be reached does an old
       copy beat no search at all. */
    return VAULT.get('index').then(function (kept) {
      var stale = !kept || !kept.v || (CAT && CAT.v && kept.v !== CAT.v);
      if (!stale) { return kept; }
      return fetch(url, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) { throw new Error('ix ' + r.status); }
        return r.json();
      }).then(function (k) { VAULT.put('index', k, 0); return k; })
        .catch(function () { return kept || null; });
    }).then(function (j) {
      if (!j) { return null; }
      IX = j;
      /* warm() pre-stems every keyword string in the index. It costs about a
         tenth of a second once, and without it the first keystroke pays for all
         of it while a finger is waiting. */
      if (!IXWARM) { try { root.VB_FIND.warm(IX); IXWARM = true; } catch (e) {} }
      return IX;
    }).catch(function () { return null; });
  }

  var RECENT_K = 'finds';
  function recentFinds() { return store()[RECENT_K] || []; }
  function rememberFind(q) {
    q = String(q || '').trim();
    if (q.length < 2) { return; }
    var r = recentFinds().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    r.unshift(q);
    put({ finds: r.slice(0, 8) });
  }

  function scrFind(q) {
    FQ = q ? decodeURIComponent(q) : '';
    var recent = recentFinds();
    var h = '<div class="va-fx">' +
      '<svg class="va-fx-i" viewBox="0 0 24 24" aria-hidden="true">' +
        '<circle cx="11" cy="11" r="6.4"/><path d="m20 20-4.4-4.4"/></svg>' +
      '<input data-find-in type="search" enterkeyhint="search" autocomplete="off" ' +
        'autocorrect="off" autocapitalize="off" spellcheck="false" ' +
        'placeholder="Search, or ask a question" value="' + esc(FQ) + '">' +
      '<button class="va-fx-x" data-find-clear aria-label="Clear"' + (FQ ? '' : ' hidden') + '>' +
        '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg></button>' +
      (canHear() ? '<button class="va-fx-m" data-find-voice aria-label="Ask out loud">' +
        '<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3"/>' +
        '<path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg></button>' : '') +
      '</div>' +
      '<div class="va-fres" data-fres>' +
        (FQ ? '' : findIdle(recent)) +
      '</div>';
    return { tab: 'shop', title: '', back: true, depth: 1, bare: true, html: h, focus: true };
  }

  /* Nothing typed yet: what this phone actually searched for, and the way in
     to everything. No trending, no popular, no suggested-for-you — none of
     those would be true, and a fabricated suggestion is worse than a blank. */
  function findIdle(recent) {
    var h = '';
    if (recent.length) {
      /* Rows, not chips. Eight past searches as chips is a paragraph of
         wrapped text; as rows it is a list you can run a thumb down, and each
         one can carry its own cross. */
      h += '<div class="va-fsec"><h2 class="va-fh">Recent</h2>' +
        '<button class="va-mini" data-finds-clear>Clear all</button></div>' +
        '<div class="va-recents">' + recent.map(function (q) {
          return '<div class="va-recent">' +
            '<button class="va-recent-q" data-find-set="' + esc(q) + '">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1"/>' +
              '<path d="M3.2 4.6v3.6h3.6M12 7.6V12l3 1.8"/></svg>' +
              '<span>' + esc(q) + '</span></button>' +
            '<button class="va-recent-x" data-find-drop="' + esc(q) + '" aria-label="Remove">' +
              '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div>';
        }).join('') + '</div>';
    }
    if (CAT) {
      h += '<h2 class="va-fh" style="margin-top:' + (recent.length ? '24px' : '4px') + '">' +
        'Everything we carry</h2><div class="va-idx">' +
        CAT.cats.map(function (c) { return idxRow(c); }).join('') + '</div>';
    }
    return h;
  }

  function findRender(q) {
    var box = el.root.querySelector('[data-fres]');
    if (!box) { return; }
    q = String(q || '').trim();
    if (!q) { box.innerHTML = findIdle(recentFinds()); return; }
    if (!IX) { box.innerHTML = '<div class="va-sk va-sk-r"></div>'; return; }

    var out = [];
    try { out = root.VB_FIND.predict(IX, q, 24) || []; } catch (e) { out = []; }

    /* A question is not a search. If what was typed looks like something a
       person would ASK, the top of the results says so and hands it to her
       rather than pretending a keyword match answered it. */
    var asky = looksAsked(q);
    var h = '';
    if (asky) {
      h += '<button class="va-askcard va-press" data-find-ask="' + esc(q) + '">' +
        '<span class="va-askcard-m">' + ASKMARK + '</span>' +
        '<span class="va-askcard-b"><b>Ask ' + esc(asstName()) + '</b>' +
        '<i>' + esc(q) + '</i></span>' +
        '<svg class="va-askcard-c" viewBox="0 0 24 24"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg>' +
      '</button>';
    }

    var cats = out.filter(function (x) { return x.kind === 'cat'; }).slice(0, 4);
    var items = out.filter(function (x) { return x.kind === 'item'; }).slice(0, 14);
    var help = out.filter(function (x) { return x.kind === 'help'; }).slice(0, 3);

    if (cats.length) {
      h += '<h2 class="va-fh">Categories</h2><div class="va-chips va-chips-w">' +
        cats.map(function (x) {
          return '<a class="va-chip" href="#/c/' + esc(x.row[0]) + '">' + esc(x.row[1]) +
            ' <i>' + x.row[4] + '</i></a>';
        }).join('') + '</div>';
    }
    if (items.length) {
      var r = roomNow();
      h += '<h2 class="va-fh">Models</h2><div class="va-rows">' + items.map(function (x) {
        var c = BYID[x.row[2]];
        var it = null;
        if (c) { c.items.forEach(function (m) { if (m.id === x.row[0]) { it = m; } }); }
        if (!c || !it) { return ''; }
        return row(c, it, r);
      }).join('') + '</div>';
    }
    if (help.length) {
      h += '<h2 class="va-fh">She can answer this</h2><div class="va-rows">' +
        help.map(function (x) {
          return '<button class="va-lrow va-press" data-find-ask="' + esc(x.row[0].replace(/-/g, ' ')) + '">' +
            '<b>' + esc(cap(x.row[0].replace(/-/g, ' '))) + '</b>' +
            '<i>Ask ' + esc(asstName()) + ' about this</i></button>';
        }).join('') + '</div>';
    }
    if (!h) {
      h = '<div class="va-empty"><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.4"/>' +
        '<path d="m20 20-4.4-4.4"/></svg><h1 class="va-h1">Nothing matches that.</h1>' +
        '<p class="va-p">We carry twenty-two categories of large equipment and nothing else — ' +
        'no parts, no small goods. If it should be here, she will tell you straight.</p>' +
        '<button class="va-btn va-b1" data-find-ask="' + esc(q) + '">Ask ' +
        esc(asstName()) + '</button></div>';
    }
    box.innerHTML = h;
    wireImages(box);
  }

  /* ── the dense index ──────────────────────────────────────────────────────
     The tile grid is how a shop should LOOK. It is not how you get somewhere
     when you already know where you are going: twenty-two pictures is eleven
     screens of scrolling. This is the same twenty-two as a list you can read in
     one thumb-flick, with the count on each so you know what you are opening. */
  function idxRow(c) {
    var tile = c.tile || c.hero;
    return '<a class="va-ix va-press" href="#/c/' + esc(c.id) + '">' +
      imgBox('va-ix-s', asset(tile), '', c.hero ? asset(c.hero) : '') +
      '<span class="va-ix-b"><b>' + esc(c.t) + '</b>' +
      '<i>' + c.items.length + ' models · ' + esc(gateName(c.gate)) + '</i></span>' +
      '<svg class="va-ix-c" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="m9 5.5 6.5 6.5L9 18.5"/></svg></a>';
  }

  function cap(t) { return String(t || '').charAt(0).toUpperCase() + String(t || '').slice(1); }

  /* Cheap and deliberately conservative: a question mark, or an opener a
     person only uses when they are asking rather than looking for a thing. */
  var ASKWORDS = /^(what|whats|what's|how|why|when|where|can|could|do|does|did|is|are|will|would|should|who|which|am|if|need|i need|my)\b/i;
  function looksAsked(q) {
    if (/\?/.test(q)) { return true; }
    if (q.split(/\s+/).length < 3) { return false; }
    return ASKWORDS.test(q.trim());
  }

  /* ── the Fit tab: the room, and what clears it ───────────────────────── */

  var SUPPLY = ['120 V standard outlet', '240 V 20 A', '240 V 30 A', '240 V 40 A',
                '240 V 50 A', 'Three phase', 'Not sure yet'];

  /* The engine has asked about the route in since the day it was written, and
     until now no screen ever offered the question - so room.stairs was read on
     every model and was empty on every room, and the one check that knows what
     a flight of stairs does to an eight-hundred-pound delivery never fired
     once. Not a new feature: a field that was missing. Absent and "No stairs"
     mean the same thing to the engine, which is why both are offered. */
  var STAIRS = ['No stairs', 'A few steps', 'A full flight'];

  function scrFit() {
    var r = roomNow(), rs = rooms();
    if (!r) {
      return { tab: 'fit', title: 'Fit', search: true, large: 'Tell it the room once.', html:
        '<p class="va-p">The doorway, the ceiling, the circuit, the slab. Every model in the ' +
        'catalogue then answers against your numbers instead of you working it out each ' +
        'time — and it keeps working with no signal, which is where these questions ' +
        'usually come up.</p>' +
        '<button class="va-btn va-b1 va-wide" data-room-new>Add a room</button>' +
        '<p class="va-note">Kept on this device. Nothing is sent anywhere.</p>' };
    }

    var h = '';
    if (rs.length > 1) {
      h += '<div class="va-chips">' + rs.map(function (x) {
        return '<button class="va-chip' + (x.id === r.id ? ' on' : '') + '" data-room-pick="' +
          esc(x.id) + '">' + esc(x.name || 'Room') + '</button>';
      }).join('') + '</div>';
    }
    h += '<dl class="va-spec">' +
      dline('Doorway', r.door ? r.door + ' in' : null) +
      dline('Ceiling', r.ceiling ? feet(r.ceiling) : null) +
      /* The route in is collected on the room sheet and it decides a check, so
         it belongs in the readout with the other four. It was missing here for
         the same reason the field itself was missing: nothing ever asked. */
      dline('Route in', r.stairs) +
      dline('Supply', r.supply) +
      dline('Slab', r.slab ? r.slab + ' in' : null) + '</dl>' +
      '<div class="va-acts">' +
        '<button class="va-btn va-b2" data-room-edit="' + esc(r.id) + '">Edit</button>' +
        '<button class="va-btn va-b2" data-room-new>Another room</button></div>';

    if (!CAT) { h += '<h2 class="va-h2">What clears it</h2><div class="va-sk va-sk-r"></div>'; }
    else {
      h += '<h2 class="va-h2">What clears it</h2><div class="va-fitcats">';
      var tot = { yes: 0, tight: 0, no: 0, ask: 0 }, all = 0, tally = [];
      CAT.cats.forEach(function (c) {
        /* blank: models this room gives nothing to check against: no row at
           all. A row that exists is an answer, even an 'ask' one: up a full
           flight with nothing else entered, a piece whose weight is on the
           sheet has only its stairs row, unresolved while the handling plan
           is open, and that is a question to settle, not a missing figure
           (26 Oct). Counted only for the words beside the bar. */
        var t = { yes: 0, tight: 0, no: 0, ask: 0, blank: 0 };
        c.items.forEach(function (it) {
          var v = verdict(it, r) || 'ask'; t[v]++; tot[v]++; all++;
          if (v === 'ask' && !fitRows(it, r).length) { t.blank++; }
        });
        tally.push({ c: c, t: t });
      });
      /* The heading says "what clears it", so the things that clear it go first.
         Catalogue order buried the good categories under nine that cannot
         answer at all. */
      tally.sort(function (a, b) {
        var d = (b.t.yes - a.t.yes) || (b.t.tight - a.t.tight) || (a.t.ask - b.t.ask);
        return d || (a.c.t < b.c.t ? -1 : 1);
      });
      tally.forEach(function (e) {
        var c = e.c, t = e.t;
        /* A category where every model is unanswerable is not a category where
           nothing fits. "0/36" says the second thing, so it does not go there.
           Nor is one where every model is unsettled a category with no
           figures: any aisle with a row to show, even all 'ask', is not
           settled yet; "no figures yet" is kept for an aisle with no row at
           all, which invents nothing. */
        var count = (t.blank === c.items.length)
          ? '<span class="va-fitc-c is-quiet">no figures yet</span>'
          : (t.ask === c.items.length)
            ? '<span class="va-fitc-c is-quiet">not settled yet</span>'
            : '<span class="va-fitc-c">' + t.yes + '/' + c.items.length + '</span>';
        h += '<a class="va-fitc va-press" href="#/c/' + esc(c.id) + '">' +
          '<span class="va-fitc-n">' + esc(c.t) + '</span><span class="va-fitc-b">' +
          ['yes', 'tight', 'no', 'ask'].map(function (k) {
            return t[k] ? '<i class="is-' + k + '" style="flex:' + t[k] + '"></i>' : '';
          }).join('') +
          '</span>' + count + '</a>';
      });
      h += '</div><p class="va-note"><b>' + tot.yes + '</b> of ' + all +
        ' models clear ' + esc(r.name || 'this room') + ' outright. ' + tot.tight +
        (tot.tight === 1 ? ' is' : ' are') + ' tight, ' + tot.no + ' will not, and ' + tot.ask +
        (tot.ask === 1 ? ' is' : ' are') + ' not settled yet: a figure we do not hold, or a question to settle with us.</p>';
    }
    return { tab: 'fit', title: r.name || 'Your room', search: true,
             large: r.name || 'Your room', html: h };
  }
  function dline(k, v) {
    return '<div><dt>' + esc(k) + '</dt><dd>' + (v ? esc(v) : '<em>not set</em>') + '</dd></div>';
  }

  function scrRoom(id) {
    var r = null;
    rooms().forEach(function (x) { if (x.id === id) { r = x; } });
    var isNew = !r;
    r = r || { id: 'r' + Date.now(), name: '' };
    return { tab: 'fit', title: isNew ? 'Add a room' : 'Edit room', back: true,
      large: isNew ? 'What are you working with?' : 'Edit the room', html:
      '<p class="va-p">Say it however you say it — 8 ft 6, 32 inch, 30 amp. It reads all of them.</p>' +
      '<form class="va-form" data-room-form>' +
        '<input type="hidden" name="id" value="' + esc(r.id) + '">' +
        fld('name', 'What to call it', 'Garage, basement, the shop', r.name || '') +
        siteF(r.site || '') +
        fld('door', 'Doorway width', '32 in', r.door ? r.door + ' in' : '') +
        fld('ceiling', 'Ceiling height', '8 ft 6', r.ceiling ? feet(r.ceiling) : '') +
        selF('stairs', 'The route in', STAIRS, r.stairs || '') +
        selF('supply', 'Electrical supply', SUPPLY, r.supply || '') +
        fld('slab', 'Concrete slab', '4 in', slabShown(r)) +
        '<button class="va-btn va-b1 va-wide" type="submit">Save the room</button>' +
        (isNew ? '' : '<button class="va-btn va-b2 va-wide" type="button" data-room-drop="' +
          esc(r.id) + '">Remove this room</button>') +
      '</form>' +
      '<p class="va-note">Kept on this device. Nothing is sent anywhere, and it works with ' +
      'no signal — which is usually where you are standing when it matters.</p>' };
  }
  function fld(n, label, ph, v) {
    return '<label class="va-f"><span>' + esc(label) + '</span>' +
      '<input name="' + n + '" value="' + esc(v) + '" placeholder="' + esc(ph) +
      '" autocomplete="off" autocapitalize="off" spellcheck="false" data-read>' +
      '<p class="va-read" data-read-out></p></label>';
  }
  /* Optional, and it says so. A homeowner has one room and no job site; a
     contractor has four addresses and twelve. The same field serves both
     because it is a free text box with the ones already used offered
     underneath, not a list to be managed somewhere first. */
  function siteF(v) {
    var names = siteNames();
    return '<label class="va-f"><span>Job site <em>optional</em></span>' +
      '<input name="site" value="' + esc(v) + '" list="va-sites" ' +
      'placeholder="An address, a client, a build" autocomplete="off"></label>' +
      (names.length ? '<datalist id="va-sites">' + names.map(function (n) {
        return '<option value="' + esc(n) + '"></option>';
      }).join('') + '</datalist>' : '');
  }
  function selF(n, label, opts, v) {
    return '<label class="va-f"><span>' + esc(label) + '</span><select name="' + n + '">' +
      '<option value="">Not set</option>' + opts.map(function (o) {
        return '<option' + (o === v ? ' selected' : '') + '>' + esc(o) + '</option>';
      }).join('') + '</select></label>';
  }

  /* ═════════════════════════════════════════════════════════════════ shelf ══
     The shape every phone shopping app uses for a cart, because it is the right
     shape: a running total at the top, then a row per line with a quantity
     stepper and the actions for that line.

     WHAT IS NOT ON IT, and every one of these is a deliberate refusal. No
     countdown. No "only 3 left". No free-delivery progress bar. No instalment
     plan — Venerabull has no financing and saying "or four payments of" would
     be a lie with a number attached to it. No lead time, because none is
     settled. The total says what the equipment costs and then stops talking. */
  function scrShelf() {
    var sh = shelf(), r = roomNow(), h = '';
    if (!sh.length) {
      return { tab: 'shelf', title: 'Shelf', search: true, large: 'Nothing put aside yet.', html:
        '<p class="va-p">Put a model aside from its page and it waits here — on this device, ' +
        'with or without a signal. Give it a room and a count and it becomes a specification ' +
        'rather than a wishlist.</p>' +
        '<a class="va-btn va-b1 va-wide" href="#/shop">Everything we carry</a>' +
        '<p class="va-note">Kept on this device. Nothing is sent anywhere.</p>' };
    }

    /* Each room picker is named for its piece, and a Compare box that cannot
       be ticked points at the line saying why. The explanation's id is made
       only of this drawing's number and the row's place, never of a saved
       reference, so it is unique on the page and nothing saved reaches it. */
    var t = sheetTally(), lines = [], draw = scrShelf.draws = (scrShelf.draws || 0) + 1;
    var stamp = shelfStamp(sh);
    sh.forEach(function (x, i) {
      var f = itemOf(x), q = qtyOf(x), line = sheetCount(t, x, f);
      var rm = null, why = 'va-slw-' + draw + '-' + i;
      rooms().forEach(function (y) { if (y.id === roomOf(x)) { rm = y; } });
      var tick = !!x && onCmp(x.c, x.m);
      var acts =
        '<div class="va-sl-a">' +
          '<div class="va-step-q va-step-s">' +
            '<button class="va-qb" data-sl-q="-1" aria-label="One fewer">−</button>' +
            '<b>' + q + '</b>' +
            '<button class="va-qb" data-sl-q="1" aria-label="One more">+</button>' +
          '</div>' +
          (rooms().length
            ? '<select class="va-sl-r" data-sl-room aria-label="' +
              esc('Room for ' + (f ? f.it.n : 'Details unavailable (' + gapRef(x) + ')')) + '">' +
              '<option value=""' + (roomOf(x) ? '' : ' selected') + '>No room</option>' +
              rooms().map(function (y) {
                return '<option value="' + esc(y.id) + '"' + (y.id === roomOf(x) ? ' selected' : '') +
                  '>' + esc(y.name || 'Room') + '</option>';
              }).join('') + '</select>'
            : '') +
          (f
            ? '<label class="va-tick' + (tick ? ' on' : '') + '">' +
              '<input type="checkbox" data-sl-cmp' + (tick ? ' checked' : '') + '><span>Compare</span></label>'
            : '<label class="va-tick' + (tick ? ' on' : '') + '">' +
              '<input type="checkbox" disabled' + (tick ? ' checked' : '') + ' aria-describedby="' + why +
              '"><span>Compare</span></label>') +
          '<button class="va-mini" data-sl-drop>Remove</button>' +
        '</div>';
      var at = ' data-sl="' + i + '" data-sl-ref="' + esc(shelfRef(x)) + '" data-sl-v="' + esc(stamp) + '"';
      if (!f) {
        lines.push(
          '<div class="va-sl"' + at + ' data-shelf-gap-row>' +
            '<span class="va-sl-s" aria-hidden="true"><span class="va-img is-on is-gone"></span></span>' +
            '<div class="va-sl-b">' +
              '<b class="va-sl-n">Details unavailable</b>' +
              '<p class="va-sl-c">' + esc(gapRef(x)) + '</p>' +
              '<p class="va-sl-p">Price unknown</p>' +
              '<p class="va-sl-c">Fit unknown' + (rm ? ' in ' + esc(rm.name || 'that room') : '') + '</p>' +
              acts +
              '<p class="va-sl-c" data-sl-cmp-why id="' + why + '">' + (tick
                ? 'Still ticked to compare. It lines up again once its details are back.'
                : 'Can’t be compared until its details are back.') + '</p>' +
            '</div>' +
          '</div>');
        return;
      }
      var vr = rm ? verdict(f.it, rm) : verdict(f.it, r), p = priceOf(f.it);
      lines.push(
        '<div class="va-sl"' + at + '>' +
          '<a class="va-sl-s" href="#/m/' + esc(x.c) + '/' + esc(x.m) + '">' +
            imgBox('', asset(f.it.img), f.it.n) + '</a>' +
          '<div class="va-sl-b">' +
            '<a class="va-sl-n" href="#/m/' + esc(x.c) + '/' + esc(x.m) + '">' +
              esc(f.it.n) + '</a>' +
            '<p class="va-sl-c">' + esc(f.c.t) + '</p>' +
            '<p class="va-sl-p">' + (line == null ? 'Price unknown'
              : money(line) + (q > 1 ? ' <span>' + money(p) + ' each</span>' : '')) + '</p>' +
            (vr ? '<span class="va-v is-' + vr + '">' + vWord(vr) +
                  (rm ? ' in ' + esc(rm.name || 'that room') : '') + '</span>' : '') +
            acts +
          '</div>' +
        '</div>');
    });

    var short = sheetShort(t), cmpOk = 0, cmpGone = 0;
    cmp().forEach(function (y) { if (itemOf(y)) { cmpOk++; } else { cmpGone++; } });
    h += '<dl class="va-spec va-sl-t">' +
      dline('Lines', String(t.n)) +
      dline('Units', String(t.units)) +
      (!short ? dline('Equipment', money(t.total))
        : dline(t.known ? 'Equipment subtotal' : 'Equipment', t.known ? money(t.total) + ' — incomplete' : 'Unknown') +
          dline('Not counted', short)) + '</dl>';
    if (short) {
      h += '<p class="va-note" data-shelf-gap style="margin-top:14px">Every piece stays here exactly as ' +
        'you saved it, with its count and room. Details and prices come from the catalogue on this ' +
        'device: once it has updated, which happens when the app is open with a connection, they ' +
        'return here. Remove a piece only if you no longer want it.</p>';
    }
    h += '<p class="va-note" style="margin-top:14px">Equipment only. No freight, no tax, no lead ' +
         'time — none of those are settled yet. Kept on this device.</p>';
    h += '<div class="va-rows" style="margin-top:22px">' + lines.join('') + '</div>';
    h += '<div class="va-acts">' +
      '<a class="va-btn va-b1" href="tel:' + esc(cfg('tel') || '') + '">Talk it through</a>' +
      (cmpOk > 1 ? '<a class="va-btn va-b2" href="#/cmp">Compare ' + cmpOk +
        '</a>' : '') + '</div>' + cmpGap(cmpGone);

    return { tab: 'shelf', title: 'Shelf', search: true, large: 'Put aside.',
             act: '<button class="va-mini" data-shelf-clear>Clear</button>', html: h };
  }

  /* 27 Oct 2026, Nasser's direction: the Shelf keeps what the job sheet
     keeps. A saved line the catalogue on this device could not answer for
     used to vanish from this list and a missing price counted as $0, so the
     list showed fewer pieces under a total that looked complete and the
     person could not change or remove what the sheet now preserves. Every
     saved line is a row now, with its count, its room and its own Remove.
     One the catalogue cannot name shows only what it was saved with
     (gapRef), price and fit unknown, a plain tile and no link: nothing is
     made up and nothing points at a page that is not there. It cannot be
     ticked to compare without details, and a tick it already has is kept,
     not cleared. The sum is the job sheet's own (sheetCount, sheetShort).
     Drawing the list never changes what is saved, so when the catalogue
     updates the same line is its model row again, with the count and room
     the person gave it.

     Each row's actions find their line by the row's place in the saved list
     AND its exact saved reference, written into the page as data and read
     back as data, never split on a character. A reference holding "|",
     quotes or markup cannot reach a neighbour, and two identical lines are
     still two lines.

     27 Oct 2026, his reviewer's finding: place and reference were not enough
     to say a row was drawn from the list as it is now. Two lines of the same
     piece with different rooms or counts share a reference, so when another
     tab removed the first, this tab's stale first row matched the second
     line's place and reference and its Remove took off the line the person
     had not pressed. A piece put aside elsewhere shifts every place down one
     (new lines go on the top) with the same result. So each drawing stamps
     its rows with a fingerprint of the exact saved list it was drawn from
     (shelfStamp), and an action whose stamp differs from the list as saved
     now edits nothing: the listener says the list had changed and redraws,
     and the fresh rows act normally. Any change to the saved list counts,
     even one to another line, because the row on screen no longer shows
     what is saved. Every action redraws, so the next press on the same
     screen carries the new stamp. */
  function shelfRef(x) {
    try { return JSON.stringify([x && x.c, x && x.m]) || ''; } catch (e) { return ''; }
  }
  /* The length of the exact serialized list and two independent 32-bit
     mixes of it: digits, letters and dots only, so it is safe in markup, and
     any edit to any line changes it. Never empty, so a row without one is
     refused. */
  function shelfStamp(list) {
    var s = '', h1, h2, k, ch;
    try { s = JSON.stringify(list == null ? [] : list) || ''; } catch (e) { s = ''; }
    h1 = 0xdeadbeef ^ s.length; h2 = 0x41c6ce57 ^ s.length;
    for (k = 0; k < s.length; k++) {
      ch = s.charCodeAt(k);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 'v' + s.length + '.' + (h1 >>> 0).toString(36) + '.' + (h2 >>> 0).toString(36);
  }
  function shelfFind(row) {
    var i = Number(row && row.getAttribute('data-sl')), ref = row && row.getAttribute('data-sl-ref');
    var v = row && row.getAttribute('data-sl-v'), sh = shelf();
    if (!v || v !== shelfStamp(sh)) { return null; }
    if (!ref || !(i >= 0 && i < sh.length && Math.floor(i) === i) || shelfRef(sh[i]) !== ref) { return null; }
    return { sh: sh, i: i, x: sh[i] };
  }
  function shelfAct(row, kind, val) {
    var at = shelfFind(row);
    if (!at) { return null; }
    if (kind === 'drop') { at.sh.splice(at.i, 1); }
    else if (!at.x || typeof at.x !== 'object') { return null; }
    else if (kind === 'q') { at.x.q = Math.max(1, Math.min(99, qtyOf(at.x) + (Number(val) < 0 ? -1 : 1))); }
    else if (kind === 'rm') { at.x.rm = String(val == null ? '' : val); }
    else { return null; }
    put({ shelf: at.sh });
    return at;
  }
  function shelfRedraw() {
    var top = stack[stack.length - 1];
    var y = top && top.querySelector ? ((top.querySelector('[data-body]') || {}).scrollTop || 0) : 0;
    mount(scrShelf(), 'replace');
    var nb = (stack[stack.length - 1] || {}).querySelector
      ? stack[stack.length - 1].querySelector('[data-body]') : null;
    if (nb) { nb.scrollTop = y; }
  }

  /* ── putting back what was just taken off (30 Oct 2026, his direction) ──
     A Remove on the Shelf, taking a piece off from its own page, and Clear
     used to throw away the line's count, its room and the compare picks at
     once. The last of those can now be put back exactly as it was, from a
     line at the top of the screen it happened on and of the Shelf. One at a
     time: a newer one replaces it. It lives in this page's memory only (no
     history is saved and the saved record's format is unchanged), with no
     time limit.

     It is put back only over exactly the record the removal left (undoCtx):
     every saved field the same (rooms, the active room, the list, and for
     Clear the compare picks), except 'seen', which every model page writes,
     and for one line the compare picks, so a newer pick choice stays. And
     never after an Erase on this page, or an erase, clear or incompatible
     record this page sees another tab make (undoWatch): those end it for
     good, even if a record that looks the same comes back. A later edit
     here or in another tab, a record erased, replaced or unreadable since:
     nothing is written and the person is told so. The prior list is put
     back whole, never merged, so it can never make a second copy of a line.
     A put-back made while the page isn't saving says "not saved", like
     every other change (put, heldToast).

     30 Oct 2026, his review: comparing the list and the field names alone
     let an erase in another tab, followed there by a new record with a
     different room but the same field names and an empty list, take the old
     lines. The rooms and the rest of the record are compared now, and an
     erase seen happening ends the put-back for good. A write made in
     another tab between this check and the put-back's own write is not
     seen: there is no lock across tabs. */
  var UNDO = null;
  function undoJ(d, k) { try { return JSON.stringify(d ? d[k] : undefined); } catch (e) { return '!'; } }
  function undoV(s) { return s === undefined ? undefined : JSON.parse(s); }
  function undoCtx(d, kind) {
    var o = {};
    Object.keys(d).sort().forEach(function (k) {
      if (k !== 'seen' && (kind === 'clear' || k !== 'cmp')) { o[k] = d[k]; }
    });
    try { return JSON.stringify(o); } catch (e) { return '!'; }
  }
  function undoDoc() {
    if (HOLD) { return MEM; }
    var r = readSaved();
    return r.kind === 'ok' ? r.doc : null;
  }
  /* Before a removal: the exact saved list and picks, and in words what
     goes. i is the line's place in that list, used only to find the row
     drawn from the list it restores. */
  function undoTake(kind, x, i) {
    var d = store(), text, n, k, f, rm = null, q;
    if (kind === 'clear') {
      n = (d.shelf || []).length; k = (d.cmp || []).length;
      text = 'Shelf cleared: ' + n + (n === 1 ? ' line' : ' lines') +
        (k ? ' and ' + k + (k === 1 ? ' compare pick' : ' compare picks') : '') + '.';
    } else {
      f = itemOf(x); q = qtyOf(x);
      rooms().forEach(function (y) { if (y.id === roomOf(x)) { rm = y; } });
      text = 'Taken off the shelf: ' + (f ? f.it.n : 'Details unavailable (' + gapRef(x) + ')') +
        (q > 1 ? ', ' + q + ' of them' : '') + (rm ? ', for ' + (rm.name || 'a room') : '') + '.';
    }
    return { kind: kind, i: i, text: text, at: spotKey(), shelf: undoJ(d, 'shelf'), cmp: undoJ(d, 'cmp') };
  }
  /* The line a Put aside toggle or data-drop would take off: the last match. */
  function undoLine(c, m) {
    var sh = shelf(), i = -1;
    sh.forEach(function (x, j) { if (x && x.c === c && x.m === m) { i = j; } });
    return i > -1 ? undoTake('line', sh[i], i) : null;
  }
  /* After it: what it left, the only thing a put-back may restore over. */
  function undoSet(u) {
    u.left = undoCtx(store(), u.kind);
    UNDO = u;
  }
  function undoOk(u) {
    var d = u && u.left ? undoDoc() : null;
    return !!d && undoCtx(d, u.kind) === u.left;
  }
  /* Another tab's change to the saved record, as this page sees it: an
     erase or clear of it, or a record it can no longer be put back over,
     ends the pending put-back for good, and its line goes. The record the
     event carries is judged as well as the one stored now (30 Oct 2026, his
     review): events queue, so another tab can write a record that doesn't
     fit and then one that does before the first event arrives, and the one
     that didn't fit still ends it. A record in the event that can't be read
     or isn't in a shape this version uses ends it too. */
  function undoFits(raw, u) {
    var d;
    try { d = JSON.parse(raw); } catch (e) { return false; }
    return !!d && typeof d === 'object' && !Array.isArray(d) && shapeOk(d) && undoCtx(d, u.kind) === u.left;
  }
  function undoWatch(e) {
    var top;
    if (!UNDO || !e || (e.key !== null && e.key !== KEY)) { return; }
    if (e.key === null || e.newValue == null || !undoFits(e.newValue, UNDO) || !undoOk(UNDO)) { UNDO = null; }
    top = stack[stack.length - 1];
    if (top) { undoScreen(top); }
  }
  /* The put-back itself: the removal it reverses, or null when nothing was
     written (and the person has been told). */
  function undoRun() {
    var u = UNDO, patch;
    UNDO = null;
    if (!undoOk(u)) {
      toast('This can’t be put back now: your shelf has changed since. Nothing was changed.');
      return null;
    }
    patch = { shelf: undoV(u.shelf) };
    if (u.kind === 'clear') { patch.cmp = undoV(u.cmp); }
    put(patch);
    heldToast(u.kind === 'clear' ? 'Your shelf is back as it was' : 'Put back on the shelf');
    return u;
  }
  /* The line that offers it: on the screen the removal happened on and on
     the Shelf, at the top of the content under any saving notice. Plain text
     and one button whose name says what comes back; announced once by the
     removal's own polite toast, not a live region of its own. Gone once it
     can no longer be put back.

     30 Oct 2026, his review: a check that changes nothing (another tab's
     'seen' write, a compare change a line's put-back allows) leaves the line
     exactly as it is, the same elements, so a focused Undo stays focused.
     When the line does go and focus was in it, focus goes to the screen's
     title, never to another control; focus anywhere else is not moved. */
  function undoScreen(sc) {
    var body = sc.querySelector('[data-body]'), n = sc.querySelector('[data-undo]'), h, had;
    var at = spotKey(), show;
    if (UNDO && !undoOk(UNDO)) { UNDO = null; }
    show = !!UNDO && !!body && (UNDO.at === at || at === '#/shelf');
    if (show && n && n.vbUndo === UNDO) { return; }
    had = !!n && n.contains(doc.activeElement);
    if (n) { n.remove(); }
    if (show) {
      n = doc.createElement('div');
      n.className = 'va-undo';
      n.setAttribute('data-undo', '');
      n.vbUndo = UNDO;
      n.innerHTML = '<p class="va-note">' + esc(UNDO.text) + '</p>' +
        '<button class="va-mini" data-undo-go aria-label="' + esc('Undo. ' + UNDO.text) + '">Undo</button>';
      h = sc.querySelector('[data-held]');
      body.insertBefore(n, h ? h.nextSibling : body.firstChild);
    }
    if (had) { focusLand(sc); }
  }
  /* After a put-back on the Shelf, the restored row as a place to read from
     (its place is exact: this drawing is of the list just restored). */
  function undoFocus(u) {
    var top = stack[stack.length - 1], r;
    if (!u || u.kind !== 'line' || spotKey() !== '#/shelf' || !top) { return; }
    r = top.querySelector('[data-sl="' + u.i + '"]');
    if (r) { r.setAttribute('tabindex', '-1'); focusTo(r, top, false); }
  }
  function cmpGap(n) {
    return n ? '<p class="va-note" data-cmp-gap>' + (n === 1
      ? 'One piece ticked to compare has no details on this device yet. It stays ticked ' +
        'and lines up again once its details are back.'
      : n + ' pieces ticked to compare have no details on this device yet. They stay ticked ' +
        'and line up again once their details are back.') + '</p>' : '';
  }

  /* ═══════════════════════════════════════════════════════════════ compare ══
     Every shop has "compare with similar items" and every one of them compares
     the wrong things — price, rating, how many people bought it. The comparison
     that decides anything here is the one against YOUR doorway, so the verdict
     row sits at the top and the specifications that differ are pulled above the
     ones that do not. Rows where all the models agree are worth nothing on a
     small screen and they go last. */
  function cmp() { return store().cmp || []; }
  function onCmp(c, m) { return cmp().some(function (x) { return x.c === c && x.m === m; }); }
  function toggleCmp(c, m) {
    var k = cmp(), i = -1;
    k.forEach(function (x, n) { if (x.c === c && x.m === m) { i = n; } });
    if (i > -1) { k.splice(i, 1); }
    else {
      if (k.length >= 4) { toast('Four at a time is the most a phone can show'); return false; }
      k.push({ c: c, m: m });
    }
    put({ cmp: k });
    return i === -1;
  }

  function scrCmp() {
    var k = cmp(), r = roomNow();
    var got = [];
    k.forEach(function (x) { var f = itemOf(x); if (f) { got.push(f); } });
    if (got.length < 2) {
      return { tab: 'shelf', title: 'Compare', back: true, depth: 1, large: 'Pick two.', html:
        '<p class="va-p">Tick <b>Compare</b> on two to four models — on the shelf, or on any ' +
        'model page — and they line up here, specification by specification, each one answered ' +
        'against your room.</p>' + cmpGap(k.length - got.length) +
        '<a class="va-btn va-b1 va-wide" href="#/shelf">Your shelf</a>' };
    }

    /* Every specification any of them carries, in the order the catalogue
       lists them, with the ones they disagree about first. */
    var keys = [], seenK = {};
    got.forEach(function (f) {
      Object.keys(f.it.a || {}).forEach(function (kk) {
        if (!seenK[kk]) { seenK[kk] = 1; keys.push(kk); }
      });
    });
    function differs(kk) {
      var first = null, diff = false;
      got.forEach(function (f) {
        var v = (f.it.a || {})[kk] || '';
        if (first === null) { first = v; } else if (v !== first) { diff = true; }
      });
      return diff;
    }
    keys.sort(function (a, b) { return (differs(b) ? 1 : 0) - (differs(a) ? 1 : 0); });

    var w = got.length;
    var h = '<div class="va-cmp" style="--n:' + w + '">' +
      '<div class="va-cmp-row is-head">' + got.map(function (f) {
        return '<div class="va-cmp-c">' +
          imgBox('va-cmp-s', asset(f.it.img), f.it.n) +
          '<a class="va-cmp-n" href="#/m/' + esc(f.c.id) + '/' + esc(f.it.id) + '">' +
            esc(f.it.n) + '</a>' +
          '<span class="va-cmp-p">' + money(f.it.p) + '</span>' +
          '<button class="va-mini" data-cmp-drop="' + esc(f.c.id) + '|' + esc(f.it.id) +
            '">Remove</button></div>';
      }).join('') + '</div>';

    if (r) {
      h += '<div class="va-cmp-k">In ' + esc(r.name || 'your room') + '</div>' +
        '<div class="va-cmp-row">' + got.map(function (f) {
          var v = verdict(f.it, r);
          return '<div class="va-cmp-c">' + (v
            ? '<span class="va-v is-' + v + '">' + vWord(v) + '</span>'
            : '<span class="va-cmp-x">—</span>') + '</div>';
        }).join('') + '</div>';
      /* And the specific reason, per check, so the pill is never the whole
         answer. This is the row people screenshot and send to a contractor. */
      var checks = [], cs = {};
      got.forEach(function (f) {
        fitRows(f.it, r).forEach(function (x) { if (!cs[x.t]) { cs[x.t] = 1; checks.push(x.t); } });
      });
      checks.forEach(function (ct) {
        h += '<div class="va-cmp-k">' + esc(ct) + '</div><div class="va-cmp-row">' +
          got.map(function (f) {
            var hitr = null;
            fitRows(f.it, r).forEach(function (x) { if (x.t === ct) { hitr = x; } });
            return '<div class="va-cmp-c">' + (hitr
              ? '<span class="va-cmp-s2 is-' + hitr.k + '">' + esc(hitr.s) + '</span>'
              : '<span class="va-cmp-x">—</span>') + '</div>';
          }).join('') + '</div>';
      });
    } else {
      h += '<div class="va-cmp-k">In your room</div><div class="va-cmp-row">' +
        got.map(function () {
          return '<div class="va-cmp-c"><a class="va-mini" href="#/fit">Add a room</a></div>';
        }).join('') + '</div>';
    }

    keys.forEach(function (kk) {
      h += '<div class="va-cmp-k' + (differs(kk) ? ' is-diff' : '') + '">' + esc(kk) + '</div>' +
        '<div class="va-cmp-row">' + got.map(function (f) {
          var v = (f.it.a || {})[kk];
          return '<div class="va-cmp-c"><span class="va-cmp-v">' +
            (v ? esc(v) : '<span class="va-cmp-x">—</span>') + '</span></div>';
        }).join('') + '</div>';
    });
    h += '</div><p class="va-note">Rows they disagree about are first. Where a figure is missing ' +
         'it is missing — we do not hold it yet, and a dash is the honest mark for that.</p>' +
         cmpGap(k.length - got.length);

    return { tab: 'shelf', title: 'Compare', back: true, depth: 1, large: 'Side by side.',
             act: '<button class="va-mini" data-cmp-clear>Clear</button>', html: h };
  }

  /* ── You ─────────────────────────────────────────────────────────────── */
  function scrYou() {
    var kind = cfg('account') || 'guest', nm = cfg('name') || '';
    var sh = shelf(), rs = rooms(), h = '';

    h += '<p class="va-reg">' + esc(kindLabel(kind)) + '</p>';

    /* The shortcut row. Four things a person comes back to this screen for,
       each with the number that tells them whether it is worth the tap. */
    h += '<div class="va-chips va-rail">' +
      '<a class="va-chip" href="#/shelf">Shelf' + (sh.length ? ' <i>' + sh.length + '</i>' : '') + '</a>' +
      '<a class="va-chip" href="#/fit">Rooms' + (rs.length ? ' <i>' + rs.length + '</i>' : '') + '</a>' +
      (siteNames().length ? '<a class="va-chip" href="#/site/' + esc(slugOf(siteNames()[0])) +
        '">Job sites <i>' + siteNames().length + '</i></a>' : '') +
      '<a class="va-chip" href="tel:' + esc(cfg('tel') || '') + '">Call us</a>' +
    '</div>';

    h += seenRail();

    var sn = siteNames();
    if (sn.length) {
      h += '<h2 class="va-h2">Job sites</h2><div class="va-rows">' + sn.map(function (n) {
        var rr = roomsAt(n), units = 0;
        rr.forEach(function (x) { specIn(x.id).forEach(function (y) { units += qtyOf(y); }); });
        return '<a class="va-lrow va-press" href="#/site/' + esc(slugOf(n)) + '">' +
          '<b>' + esc(n) + '</b><i>' + rr.length + (rr.length === 1 ? ' room' : ' rooms') +
          ' · ' + units + (units === 1 ? ' unit' : ' units') + '</i></a>';
      }).join('') + '</div>';
    }

    h += '<h2 class="va-h2">Rooms</h2>';
    if (!rs.length) {
      h += '<div class="va-card"><p class="va-sm">No room saved yet.</p>' +
        '<button class="va-btn va-b2 va-wide" style="margin-top:14px" data-room-new>Add a room</button></div>';
    } else {
      h += '<div class="va-rows">' + rs.map(function (x) {
        return '<button class="va-lrow va-press" data-room-open="' + esc(x.id) + '" style="width:100%">' +
          '<b>' + esc(x.name || 'Room') + (x.site ? ' <em>' + esc(x.site) + '</em>' : '') + '</b><i>' +
          [x.door ? x.door + ' in door' : '', x.ceiling ? feet(x.ceiling) + ' ceiling' : '',
           x.supply || ''].filter(Boolean).join(' · ') + '</i></button>';
      }).join('') + '</div>';
    }

    /* ── settings, grouped ────────────────────────────────────────────────
       The big apps put the account screen's settings in labelled groups and
       that is right — a flat list of everything is unreadable by the twentieth
       row. Ours has three groups because there are only three things to say.

       WHAT IS NOT IN OURS. No wallet, because we hold no card. No addresses,
       because we ship nothing yet. No ad preferences, no recommendations to
       tune, no browsing history to manage on a server — the browsing history
       is on this phone and the button below deletes it. A privacy section with
       thirty rows in it is thirty rows of things being done with your data.
       Ours is short for the honest reason. */
    h += '<h2 class="va-h2">This device</h2><div class="va-list">' +
      '<button class="va-lr" data-store-info>' +
        '<b data-held-line="title">' + heldLine('title') + '</b><i data-held-line="store">' + storeLine() + '</i>' +
        '<svg viewBox="0 0 24 24"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg></button>' +
      (seen().length ? '<button class="va-lr" data-seen-clear>' +
        '<b>Clear what you have looked at</b><i data-held-line="seen">' + heldLine('seen') + '</i>' +
        '<svg viewBox="0 0 24 24"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg></button>' : '') +
      (recentFinds().length ? '<button class="va-lr" data-finds-clear>' +
        '<b>Clear recent searches</b><i data-held-line="finds">' + heldLine('finds') + '</i>' +
        '<svg viewBox="0 0 24 24"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg></button>' : '') +
      '<button class="va-lr is-warn" data-wipe>' +
        '<b>Erase everything on this device</b><i data-held-line="erase">' + heldLine('erase') + '</i>' +
        '<svg viewBox="0 0 24 24"><path d="m9 5.5 6.5 6.5L9 18.5"/></svg></button>' +
      '</div>';

    if (canSpeak()) {
      h += '<h2 class="va-h2">Voice</h2><div class="va-card">' +
        '<button class="va-tog' + (speakBack() ? ' on' : '') + '" data-speak role="switch" ' +
        'aria-pressed="' + (speakBack() ? 'true' : 'false') + '">' +
        '<span>Read answers back</span><i></i></button>' +
        '<p class="va-sm" style="margin-top:12px">Only when you have asked out loud. ' +
        (canHear()
          ? 'The microphone opens when you press it and closes when your sentence ends — ' +
            'there is no wake word and nothing is listening otherwise.'
          : 'This phone does not offer dictation, so the microphone is not shown.') +
        '</p></div>';
    }

    h += installCard();

    h += '<h2 class="va-h2">If you need a person</h2><div class="va-card">' +
      '<p class="va-sm">Venerabull is small enough that the phone is answered by someone who ' +
      'can decide something. Nothing here is designed to keep you off it.</p>' +
      '<a class="va-btn va-b1 va-wide" style="margin-top:15px" href="tel:' +
        esc(cfg('tel') || '') + '">Call 1 (800) 982-7007</a>' +
      '<p class="va-sm" style="margin-top:11px">Monday to Friday, 8am–8pm ET</p></div>';

    h += '<h2 class="va-h2">Data</h2><div class="va-card">' +
      '<p class="va-sm">Everything this app knows is on this phone: the rooms you saved, what ' +
      'you put aside, your job sites, what you have looked at, and the conversation. None of it ' +
      'is uploaded, none of it is shared, and there is no account needed to use any of it. The ' +
      'only thing the app fetches is our own catalogue and what ' + esc(asstName()) +
      ' answers from.</p>' +
      '<p class="va-sm" style="margin-top:10px">Erase it from the row above and it is gone — ' +
      'there is no copy anywhere else to ask us to delete.</p></div>';

    if (kind !== 'guest') {
      h += '<div class="va-acts"><a class="va-btn va-b2 va-wide" href="/account/logout">' +
        'Sign out</a></div>';
    }

    return { tab: 'you', title: nm || 'You', search: true, searchPh: 'Search everything',
             large: nm ? nm + '.' : 'You.', html: h };
  }
  /* What is actually on the phone, counted rather than described. */
  /* One is not a plural. The job-sites row three sections up gets this right
     and this one did not, so a person with one room read "1 rooms" on the only
     screen in the app whose whole job is telling them what it is holding. */
  function count(n, one, many) { return n + ' ' + (n === 1 ? one : (many || one + 's')); }

  function storeLine() {
    var st = store(), bits = [];
    if ((st.rooms || []).length) { bits.push(count((st.rooms || []).length, 'room')); }
    if ((st.shelf || []).length) { bits.push((st.shelf || []).length + ' put aside'); }
    if (siteNames().length) { bits.push(count(siteNames().length, 'job site')); }
    if ((st.chat || []).length) { bits.push(count((st.chat || []).length, 'message')); }
    /* Held: this page's own counts, not saved, and no size: the size of the
       value kept on the device says nothing about this page (30 Oct 2026). */
    if (HOLD) { return (bits.length ? bits.join(' · ') : 'Nothing yet') + ' · not saved'; }
    var kb = 0;
    try { kb = Math.round((localStorage.getItem(KEY) || '').length / 1024); } catch (e) {}
    bits.push((kb || 1) + ' KB');
    return bits.join(' · ');
  }

  function kindLabel(k) {
    return k === 'trade' ? 'Venerabull Trade' : k === 'supplier' ? 'Venerabull Supplier'
         : k === 'consumer' ? 'Your account' : 'Not signed in';
  }

  /* ═════════════════════════════════════════════════════════ the assistant ══
     The same brain the site runs — assets/vb-assistant-brain.js, which touches
     no DOM at all and exposes load() and ask(). Not a copy of her answers: her.

     WHAT THE APP GIVES HER THAT THE SITE CANNOT. On the site she has to ask for
     your doorway, because a browser has no idea. Here the room is already
     saved, so the figures go in with the question and she answers against your
     actual numbers the first time. That is the whole difference between an
     assistant and a form. */

  var MO = { ready: false, busy: false };

  function moLog() { return store().chat || []; }
  function moPush(who, html) {
    var l = moLog(); l.push({ w: who, h: html, at: Date.now() });
    put({ chat: l.slice(-40) });
  }

  function moContext() {
    var r = roomNow();
    if (!r) { return ''; }
    var bits = [];
    if (r.door) { bits.push(r.door + ' in doorway'); }
    if (r.ceiling) { bits.push(feet(r.ceiling) + ' ceiling'); }
    if (r.supply && r.supply !== 'Not sure yet') { bits.push(r.supply); }
    if (r.slab) { bits.push(r.slab + ' in slab'); }
    return bits.join(', ');
  }

  var STARTERS = [
    'Will a sauna fit through a 32 in door?',
    'My garage slab is 4 in. Is that enough for a lift?',
    'What circuit does a deck oven need?',
    'What does room of choice delivery mean?'
  ];

  /* ═══════════════════════════════════════════════ putting it on the phone ══
     Installed, this stops being a web page: full screen, own icon, own launch
     image, no address bar, and it opens straight into the room the person
     saved. It is also the state in which the offline layer earns its keep,
     because an installed app is opened in places a browser tab is not.

     Two platforms, two mechanisms, and the app only ever offers the one that
     exists on the handset it is running on:
       iOS      no API for this at all — Safari's own Share sheet is the only
                way, so the app can do nothing but say where the button is, and
                only in Safari, and only once.
       Android  Chrome fires beforeinstallprompt, which we hold onto and fire
                back when the person asks for it.
     Already installed: neither is offered. Nothing is more irritating than an
     app asking you to install the app you are inside. */
  var INSTALL = { evt: null };

  function standalone() {
    try {
      return !!(root.navigator && root.navigator.standalone) ||
             !!(root.matchMedia && root.matchMedia('(display-mode: standalone)').matches);
    } catch (e) { return false; }
  }
  function isIOS() {
    var ua = (root.navigator && root.navigator.userAgent) || '';
    /* iPadOS reports itself as a Mac; the touch points give it away. */
    return /iPad|iPhone|iPod/.test(ua) ||
      (/Macintosh/.test(ua) && root.navigator && root.navigator.maxTouchPoints > 1);
  }
  function isSafari() {
    var ua = (root.navigator && root.navigator.userAgent) || '';
    return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/.test(ua);
  }
  function installCard() {
    if (standalone() || store().hideInstall) { return ''; }
    if (INSTALL.evt) {
      return '<h2 class="va-h2">Put it on your phone</h2><div class="va-card">' +
        '<p class="va-sm">Installed, it opens full screen with its own icon, and it opens ' +
        'in places a browser will not — a basement, a job site, a lift bay.</p>' +
        '<button class="va-btn va-b1 va-wide" style="margin-top:15px" data-install>Install</button>' +
        '<button class="va-mini" style="margin-top:6px" data-install-no>Not now</button></div>';
    }
    if (isIOS() && isSafari()) {
      return '<h2 class="va-h2">Put it on your phone</h2><div class="va-card">' +
        '<p class="va-sm">Tap the share button at the bottom of Safari, then ' +
        '<b>Add to Home Screen</b>. It opens full screen after that, with its own icon, ' +
        'and it keeps working where the signal does not.</p>' +
        '<button class="va-mini" style="margin-top:10px" data-install-no>Got it</button></div>';
    }
    return '';
  }
  function wireInstall() {
    root.addEventListener('beforeinstallprompt', function (e) {
      /* Hold it rather than let Chrome decide the moment. The right moment is
         after somebody has saved a room, not three seconds after landing. */
      e.preventDefault();
      INSTALL.evt = e;
    });
    root.addEventListener('appinstalled', function () {
      INSTALL.evt = null;
      put({ hideInstall: 1 });
      toast('On your home screen');
    });
  }

  /* ══════════════════════════════════════════════════════════════════ voice ══
     The reason voice belongs in this app and not on the website: the question
     "will this fit" is asked with a tape measure in one hand and a phone in the
     other, often wearing gloves, often in a place with bad light. Typing is the
     wrong instrument for that moment.

     WHAT IS NOT HERE, AND WHY. There is no wake word. A wake word
     means the microphone is open the whole time the app is, which is a battery
     cost the person did not agree to and a privacy claim I am not willing to
     make on Venerabull's behalf. The microphone opens when a finger opens it
     and closes when the sentence ends. If we ever ship a wrapped native build
     with a proper on-device wake engine, that is the conversation to have then,
     with the person told plainly what is listening.

     Dictation on both platforms sends audio to the platform's own service, so
     it needs a signal even though the rest of the app does not. When there is
     none, the button says so rather than failing quietly. Speaking BACK is
     local on both, so that half keeps working in a basement. */
  var SR = root.SpeechRecognition || root.webkitSpeechRecognition || null;
  var VOICE = { rec: null, on: false, sent: false };

  function canHear() { return !!SR; }
  function canSpeak() { return !!(root.speechSynthesis && root.SpeechSynthesisUtterance); }
  function speakBack() { return store().speak !== false; }

  function voiceToggle(mode) {
    VOICE.mode = mode || 'ask';
    if (VOICE.on) { voiceStop(); return; }
    if (!canHear()) { toast('This phone does not do dictation'); return; }
    if (!LIVE) { toast('Dictation needs a signal. Typing does not.'); return; }
    var box = el.root.querySelector(VOICE.mode === 'find' ? '[data-find-in]' : '[data-mo-in]');
    try { VOICE.rec = new SR(); } catch (e) { toast('Could not open the microphone'); return; }
    VOICE.rec.lang = 'en-US';
    VOICE.rec.interimResults = true;
    VOICE.rec.continuous = false;
    VOICE.rec.maxAlternatives = 1;
    VOICE.sent = false;

    VOICE.rec.onstart = function () { VOICE.on = true; voicePaint(); };
    VOICE.rec.onresult = function (ev) {
      var txt = '', fin = false, i;
      for (i = ev.resultIndex; i < ev.results.length; i++) {
        txt += ev.results[i][0].transcript;
        if (ev.results[i].isFinal) { fin = true; }
      }
      if (box) {
        box.value = txt;
        box.dispatchEvent(new Event('input', { bubbles: true }));
      }
      /* Send on the final result rather than on end, so the answer starts
         forming while the person is still lowering the phone. */
      if (fin && txt.trim() && !VOICE.sent) {
        VOICE.sent = true;
        if (VOICE.mode === 'find') {
          /* Dictated into the search field, it stays there: a person gets to
             SEE what was heard before the app acts on it. */
          rememberFind(txt.trim());
          var xb = el.root.querySelector('[data-find-clear]');
          if (xb) { xb.hidden = false; }
          findReady().then(function () { findRender(txt.trim()); });
          return;
        }
        VOICE.heard = true;
        if (box) { box.value = ''; box.style.height = ''; }
        moAsk(txt.trim());
      }
    };
    VOICE.rec.onerror = function (ev) {
      var k = ev && ev.error;
      toast(k === 'not-allowed' || k === 'service-not-allowed'
              ? 'The microphone is not allowed for this app'
          : k === 'no-speech' ? 'Did not catch that'
          : k === 'network'   ? 'Dictation needs a signal. Typing does not.'
          : 'Dictation stopped');
    };
    VOICE.rec.onend = function () { VOICE.on = false; voicePaint(); };
    try { VOICE.rec.start(); } catch (e) { VOICE.on = false; voicePaint(); }
  }
  function voiceStop() {
    VOICE.on = false;
    try { if (VOICE.rec) { VOICE.rec.stop(); } } catch (e) {}
    voicePaint();
  }
  function voicePaint() {
    var b = el.root.querySelector('[data-mic]');
    if (b) {
      b.classList.toggle('on', VOICE.on);
      b.setAttribute('aria-label', VOICE.on ? 'Stop listening' : 'Ask out loud');
      b.setAttribute('aria-pressed', VOICE.on ? 'true' : 'false');
    }
  }
  /* Read back the answer, not the markup. Nothing that would sound like a
     robot reading a web page: the links and the bold go, the sentences stay. */
  function say(html) {
    if (!canSpeak() || !speakBack()) { return; }
    var tmp = doc.createElement('div');
    tmp.innerHTML = html;
    var text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) { return; }
    try {
      root.speechSynthesis.cancel();
      var u = new root.SpeechSynthesisUtterance(text.slice(0, 700));
      u.rate = 1.02; u.pitch = 1;
      root.speechSynthesis.speak(u);
    } catch (e) {}
  }
  function hush() { try { if (canSpeak()) { root.speechSynthesis.cancel(); } } catch (e) {} }

  /* ════════════════════════════════════════════════════════ she is a sheet ══
     She used to be a fourth tab, which meant leaving the model you were
     standing on to ask about it — and then describing it to her from memory.
     She opens OVER the screen instead, carries what is on that screen into the
     question, and closes back onto it. That is the difference between an
     assistant and a help page.

     The sheet is not a screen in the stack. It has no history entry, so the
     back gesture dismisses it rather than navigating away, which is what a
     sheet is for. */
  var ASK = { on: false, ctx: null };

  function askCtx() {
    /* Whatever the top screen is about, in words she already understands. */
    var seg = (location.hash || '').replace(/^#\/?/, '').split('/');
    if (seg[0] === 'm') {
      var c = BYID[seg[1]], it = null;
      if (c) { c.items.forEach(function (m) { if (m.id === seg[2]) { it = m; } }); }
      if (it) { return { kind: 'model', label: it.n, hint: it.n + ' (' + c.t + ')' }; }
    }
    if (seg[0] === 'c' && BYID[seg[1]]) {
      return { kind: 'cat', label: BYID[seg[1]].t, hint: BYID[seg[1]].t };
    }
    return null;
  }

  function askOpen(q) {
    var sh = sheetBegin('ask');
    if (!sh) { return; }
    ASK.ctx = askCtx();
    ASK.on = true;
    sh.innerHTML = askHtml();
    sheetShow(sh);
    askScroll();
    /* The question goes to this opening's panel only: not after it closed,
       not into a later opening (whose context may differ), not into a filter. */
    var g = SHEET.gen;
    if (q) { setTimeout(function () { if (SHEET.gen === g && SHEET.open && ASK.on) { moAsk(q); } }, 220); }
  }

  function askClose() {
    var sh = el.root.querySelector('[data-ask-sheet]');
    if (!sh || !ASK.on) { return; }
    ASK.on = false;
    hush(); if (VOICE.on) { voiceStop(); }
    sheetEnd();
  }

  /* ── the Ask and filter panels as one modal (30 Oct 2026, his direction) ──
     Both live in the one [data-ask-sheet]. While either is open:
     - focus is inside it: on a phone or tablet the panel itself, so no
       keyboard rises over her starters or a filter's values; with a mouse
       and keyboard, the question field; in a filter, the chosen value or
       else the first one there is;
     - Tab and Shift-Tab go round its usable controls, counted afresh on each
       key, so controls that come and go are followed, and with none the
       panel itself keeps focus;
     - the screens behind are inert. The tab bar stays tappable, as it was
       designed to (the panel stops short of it so you can leave for the
       shelf without dismissing her), but is out of the Tab round and hidden
       from screen readers. The toast and the offline line stay outside, so
       what they say is still heard.
     Closing gives focus back to what opened it; if that is gone, to the same
     control repainted on this screen; else to this screen's title. A close
     that comes with a route change does the same first, and mount() then
     takes focus on from there. Each opening has its own number, so a late
     frame or a late close callback from an earlier one never shows, hides or
     empties a newer panel. */
  var SHEET = { gen: 0, open: false, kind: '', opener: null, ref: null, at: '', tap: null };
  function sheetUsable(n) {
    if (!n || n === doc.body || !n.isConnected || n.disabled) { return false; }
    return !(n.closest && n.closest('[inert], [hidden]'));
  }
  function sheetBegin(kind) {
    var sh = el.root.querySelector('[data-ask-sheet]');
    if (!sh) { return null; }
    if (!SHEET.open) {
      var a = doc.activeElement, top = stack[stack.length - 1];
      var o = sheetUsable(a) && !sh.contains(a) ? a
            : (sheetUsable(SHEET.tap) && !sh.contains(SHEET.tap) ? SHEET.tap : null);
      SHEET.opener = o; SHEET.at = spotKey();
      SHEET.ref = o && top && top.contains(o) ? focusRef(top, o) : null;
    }
    SHEET.gen++; SHEET.open = true; SHEET.kind = kind;
    sh.hidden = false;
    sh.removeAttribute('inert'); sh.removeAttribute('aria-hidden');
    sheetBehind(true);
    el.root.classList.add('is-sheet');
    return sh;
  }
  /* One frame between "in the document" and "add the class", or the browser
     has nothing to animate from; then focus goes in. */
  function sheetShow(sh) {
    var g = SHEET.gen;
    requestAnimationFrame(function () { if (SHEET.gen === g && SHEET.open) { sh.classList.add('on'); } });
    var w = sh.querySelector('[role="dialog"]'), t = null, k, opts;
    if (!w) { return; }
    if (SHEET.kind === 'filter') {
      opts = sheetControls(w).filter(function (n) { return n.classList.contains('va-opt'); });
      for (k = 0; k < opts.length && !t; k++) { if (opts[k].classList.contains('on')) { t = opts[k]; } }
      t = t || opts[0] || null;
    } else {
      var fine = false;
      try { fine = !!(root.matchMedia && root.matchMedia('(hover: hover) and (pointer: fine)').matches); } catch (e) {}
      t = fine ? w.querySelector('[data-mo-in]') : null;
      if (t && !sheetUsable(t)) { t = null; }
    }
    if (t) { try { t.focus({ preventScroll: true }); } catch (e) {} }
    if (!t || doc.activeElement !== t) { sheetFocus(w); }
  }
  function sheetFocus(w) {
    if (!w.hasAttribute('tabindex')) { w.setAttribute('tabindex', '-1'); }
    try { w.focus({ preventScroll: true }); } catch (e) {}
  }
  function sheetBehind(on) {
    var tabs = el.root.querySelector('.va-tabs');
    if (on) {
      el.stack.setAttribute('inert', ''); el.stack.setAttribute('aria-hidden', 'true');
      if (tabs) { tabs.setAttribute('aria-hidden', 'true'); }
    } else {
      el.stack.removeAttribute('inert'); el.stack.removeAttribute('aria-hidden');
      if (tabs) { tabs.removeAttribute('aria-hidden'); }
    }
  }
  function sheetControls(w) {
    var all = w.querySelectorAll('a, button, input, select, textarea, [tabindex]'), out = [], k, n;
    for (k = 0; k < all.length; k++) {
      n = all[k];
      if (n === w || n.getAttribute('tabindex') === '-1' || !sheetUsable(n)) { continue; }
      if (n.tagName === 'A' && !n.hasAttribute('href')) { continue; }
      out.push(n);
    }
    return out;
  }
  function sheetTab(e) {
    if (!SHEET.open || e.key !== 'Tab') { return; }
    var sh = el.root.querySelector('[data-ask-sheet]'), w = sh && sh.querySelector('[role="dialog"]');
    if (!w) { return; }
    var c = sheetControls(w), i = c.indexOf(doc.activeElement);
    if (!c.length) { e.preventDefault(); sheetFocus(w); return; }
    if (e.shiftKey ? i <= 0 : (i < 0 || i === c.length - 1)) {
      e.preventDefault();
      try { (e.shiftKey ? c[c.length - 1] : c[0]).focus(); } catch (err) {}
    }
  }
  /* A repaint inside the open panel (a new answer, Start over) that took the
     focused control with it leaves focus on the panel, not on the page. */
  function sheetKeep() {
    var sh = el.root.querySelector('[data-ask-sheet]'), w = sh && sh.querySelector('[role="dialog"]');
    var a = doc.activeElement;
    if (SHEET.open && w && (!a || a === doc.body || !a.isConnected)) { sheetFocus(w); }
  }
  function sheetEnd() {
    var sh = el.root.querySelector('[data-ask-sheet]');
    if (!sh || !SHEET.open) { return false; }
    var g = SHEET.gen, a = doc.activeElement;
    var mine = !a || a === doc.body || sh.contains(a);
    SHEET.open = false;
    /* Out of reach the moment it starts to go, still painted for its slide:
       no Tab stop, screen-reader cursor or tap lands in a closing panel. */
    sh.setAttribute('inert', ''); sh.setAttribute('aria-hidden', 'true');
    sh.classList.remove('on');
    el.root.classList.remove('is-sheet');
    sheetBehind(false);
    after(sh, function () { if (SHEET.gen === g && !SHEET.open) { sh.hidden = true; sh.innerHTML = ''; } });
    if (mine) { sheetReturn(); }
    SHEET.opener = null; SHEET.ref = null;
    return true;
  }
  /* A tab tapped while a panel is open ends the panel first: the tab already
     showing as well, whose address does not change and so never reaches
     route(). A link tab then goes where it goes; the Ask cell closes her if
     she was the panel (a second tap), and otherwise opens her in its place. */
  function sheetTabTap(t, e) {
    var hit = t && t.closest ? t.closest('.va-tabs a, .va-tabs button') : null;
    if (!hit || !SHEET.open) { return false; }
    var wasAsk = ASK.on;
    if (wasAsk) { askClose(); } else { filtClose(); }
    if (wasAsk && hit.hasAttribute('data-asst-open')) { e.preventDefault(); return true; }
    return false;
  }
  function sheetReturn() {
    var top = stack[stack.length - 1], o = SHEET.opener, t;
    if (sheetUsable(o)) {
      try { o.focus({ preventScroll: true }); } catch (e) {}
      if (doc.activeElement === o) { return; }
    }
    if (!top) { return; }
    t = SHEET.at === spotKey() ? focusFind(top, SHEET.ref) : null;
    if (t) { focusTo(t, top, true); } else { focusLand(top); }
  }

  function askHtml() {
    var log = moLog(), r = roomNow();
    var h = '<div class="va-sheet-bd" data-sheet-close></div>' +
      '<div class="va-sheet-w" role="dialog" aria-modal="true" aria-label="Ask ' +
        esc(asstName()) + '">' +
      '<div class="va-sheet-h">' +
        '<button class="va-ico" data-sheet-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24"><path d="M6 9.5 12 15.5 18 9.5"/></svg></button>' +
        '<span class="va-sheet-t">' + ASKMARK + '<b>' + esc(asstName()) + '</b></span>' +
        (log.length ? '<button class="va-mini" data-mo-clear>Start over</button>'
                    : '<span class="va-sheet-sp"></span>') +
      '</div>' +
      '<div class="va-sheet-b" data-sheet-body>' + askBody() + '</div>' +
      composer() + '</div>';
    return h;
  }

  function askBody() {
    var log = moLog(), r = roomNow(), h = '';
    var bits = [];
    if (ASK.ctx) { bits.push('<b>' + esc(ASK.ctx.label) + '</b>'); }
    if (r && moContext()) { bits.push('<b>' + esc(r.name || 'your room') + '</b> — ' + esc(moContext())); }
    if (bits.length) {
      h += '<span class="va-room-chip">She has ' + bits.join(' · ') + '</span>';
    }
    h += '<div class="va-mo' + (log.length ? ' has' : '') + '" data-mo>';
    if (!log.length) {
      h += '<div class="va-mo-her"><div class="va-mo-n"><b>' + esc(asstName()) +
        '</b><i>knows</i></div>' +
        '<div class="va-mo-w"><p>I know what we sell — the specifications, the constraints, ' +
        'what fits where and what will not. ' +
        (r ? 'I have your room, so ask and I will answer against it.'
           : 'Tell me a room in Fit and I will answer against it instead of asking you twice.') +
        '</p><p>Where I do not know, I will say so and hand you to a person. For anything that ' +
        'matters, the manufacturer&rsquo;s sheet is the only figure to build to.</p></div></div>';
    } else {
      log.forEach(function (m) {
        h += m.w === 'you'
          ? '<div class="va-mo-you">' + esc(m.h) + '</div>'
          : '<div class="va-mo-her"><div class="va-mo-n"><b>' + esc(asstName()) + '</b></div>' +
            '<div class="va-mo-w">' + m.h + '</div></div>';
      });
    }
    h += '</div>';
    if (!log.length) {
      h += '<h2 class="va-fh">Start here</h2><div class="va-asks">' +
        starters().map(function (q) {
          return '<button class="va-ask" data-ask="' + esc(q) + '">' + esc(q) + '</button>';
        }).join('') + '</div>';
    }
    h += '<div class="va-mo-pad"></div>';
    return h;
  }

  /* The openers change with what you are standing on. Asked from a model page
     they are about that model; asked from nowhere in particular they are the
     four questions people actually arrive with. */
  function starters() {
    if (ASK.ctx && ASK.ctx.kind === 'model') {
      return ['Will this fit through my door?',
              'What circuit does this need?',
              'What is the difference between this and the next one up?',
              'How does this get delivered?'];
    }
    if (ASK.ctx && ASK.ctx.kind === 'cat') {
      return ['Which of these fits my room?',
              'What do I need to have ready before it arrives?',
              'What is the difference between these?',
              'What does room of choice delivery mean?'];
    }
    return STARTERS;
  }

  /* A deep link to her still works: it opens the sheet over the shop rather
     than becoming a screen, so there is exactly one of her in the app. */
  function scrAsst() {
    setTimeout(function () {
      if (!ASK.on && /^#\/?asst(\/|$)/.test(location.hash || '')) { askOpen(''); }
    }, 60);
    return scrShop();
  }
  function composer() {
    /* The microphone is only drawn on a device that has one to offer. A dead
       control is worse than a missing one. */
    var mic = canHear()
      ? '<button class="va-mic" data-mic aria-label="Ask out loud" aria-pressed="false">' +
        '<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3"/>' +
        '<path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg><i class="va-mic-r"></i></button>'
      : '';
    return '<div class="va-comp">' + mic +
      '<textarea data-mo-in rows="1" placeholder="Ask about a room or a circuit" ' +
        'autocapitalize="sentences" autocomplete="off"></textarea>' +
      '<button class="va-send" data-mo-send disabled aria-label="Send">' +
        '<svg viewBox="0 0 24 24"><path d="M12 19V5M5.5 11.5 12 5l6.5 6.5"/></svg></button></div>';
  }

  function moAsk(text) {
    text = String(text || '').trim();
    if (!text || MO.busy) { return; }
    MO.busy = true;
    moPush('you', text);
    moRender();

    var ctx = moContext(), q = text;
    /* Both facts ride along: the thing on the screen behind the sheet, and the
       room. She never has to ask "which one?" or "how wide is your door?" */
    if (ASK.ctx) { q += ' (about: ' + ASK.ctx.hint + ')'; }
    if (ctx) { q += ' (my room: ' + ctx + ')'; }

    moThinking(true);
    moReady().then(function () {
      var res = null;
      try { res = root.VB_BRAIN.ask(q); } catch (e) { res = null; }
      return res;
    }).then(function (res) {
      moThinking(false);
      var html = (res && res.html)
        ? res.html
        : '<p>I do not know that one, and I would rather say so than invent it. ' +
          'Call <b>1 (800) 982-7007</b> and a person will work it out with you.</p>';
      if (html.indexOf('<') !== 0 && html.indexOf('<p') === -1) { html = '<p>' + html + '</p>'; }
      moPush('her', html);
      MO.busy = false;
      moRender();
      /* Only speak when the question was spoken. Reading aloud an answer to
         something that was typed, in a quiet room, is an ambush. */
      if (VOICE.heard) { VOICE.heard = false; say(html); }
    }).catch(function () {
      moThinking(false);
      moPush('her', '<p>Something went wrong reaching what I know. Try again in a moment, or ' +
        'call <b>1 (800) 982-7007</b>.</p>');
      MO.busy = false;
      moRender();
    });
  }
  /* She answers from a file, not from a server, which is what makes her
     usable in a basement. The brain takes a URL, so a kept copy is handed back
     as an object URL rather than reaching for the network — the brain does not
     need to know the difference, and the site's copy of it stays untouched. */
  function moReady() {
    if (MO.ready) { return Promise.resolve(); }
    if (!root.VB_BRAIN || !root.VB_BRAIN.load) { return Promise.reject(); }
    var url = cfg('knowledge');
    return VAULT.get('knowledge').then(function (j) {
      if (!j) { return null; }
      try {
        return URL.createObjectURL(new Blob([JSON.stringify(j)], { type: 'application/json' }));
      } catch (e) { return null; }
    }).then(function (local) {
      return root.VB_BRAIN.load(local || url).then(function (kb) {
        if (!kb) { throw new Error('kb'); }
        MO.ready = true;
        if (local) { try { URL.revokeObjectURL(local); } catch (e) {} }
        /* Whether it came from the vault or the wire, make sure a copy is on
           the device for next time — and refresh a stale one when there is a
           signal to refresh it with. */
        if (!local && LIVE) { VAULT.put('knowledge', kb, 0); }
        else if (local && LIVE) { keepKnowledgeFresh(url); }
      });
    });
  }
  var KFRESH = false;
  function keepKnowledgeFresh(url) {
    if (KFRESH || !url) { return; }
    KFRESH = true;
    fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) { throw 0; } return r.json();
    }).then(function (j) { if (j) { VAULT.put('knowledge', j, 0); } }).catch(function () {});
  }
  function moThinking(on) {
    var wrap = el.root.querySelector('[data-mo]');
    if (!wrap) { return; }
    var old = wrap.querySelector('[data-mo-think]');
    if (old) { old.remove(); }
    if (!on) { return; }
    var d = doc.createElement('div');
    d.className = 'va-mo-her'; d.setAttribute('data-mo-think', '');
    d.innerHTML = '<div class="va-mo-n"><b>' + esc(asstName()) +
      '</b></div><div class="va-mo-w">' +
      '<span class="va-dots"><i></i><i></i><i></i></span></div>';
    wrap.appendChild(d);
    moScroll();
  }
  /* Repaint the conversation only — not the sheet's header, and never the
     screen underneath. Re-rendering the whole sheet on every turn threw away
     the composer mid-sentence and closed the keyboard on Android. */
  function moRender() {
    var b = el.root.querySelector('[data-sheet-body]');
    if (!b) { return; }
    b.innerHTML = askBody();
    var head = el.root.querySelector('.va-sheet-h');
    if (head) {
      var has = moLog().length, cur = head.querySelector('[data-mo-clear]');
      if (has && !cur) {
        var sp = head.querySelector('.va-sheet-sp');
        if (sp) { sp.outerHTML = '<button class="va-mini" data-mo-clear>Start over</button>'; }
      } else if (!has && cur) {
        cur.outerHTML = '<span class="va-sheet-sp"></span>';
      }
    }
    sheetKeep();
    moScroll();
  }
  function askScroll() { moScroll(); }
  function moScroll() {
    var b = el.root.querySelector('[data-sheet-body]');
    if (b) { requestAnimationFrame(function () { b.scrollTop = b.scrollHeight; }); }
  }

  /* ══════════════════════════════════════════════════════════════ routing ══
     Depth decides the transition: going deeper pushes, coming back pops,
     staying level replaces. That is all it takes for the Android back button
     and the iOS edge swipe to feel right, because both of them are just
     history moves and history is what drives this. */

  var DEPTH = { shop: 0, fit: 0, shelf: 0, you: 0, find: 1, gate: 1, c: 1, m: 2,
                room: 1, site: 1, cmp: 1, asst: 0 };
  var lastDepth = -1;

  function screenFor(p) {
    switch (p[0]) {
      case '': case 'shop': return scrShop();
      case 'c':    return scrCategory(p[1]);
      case 'm':    return scrModel(p[1], p[2]);
      case 'fit':  return scrFit();
      case 'room': return scrRoom(p[1]);
      case 'find': return scrFind(p[1]);
      case 'gate': return scrGate(p[1]);
      case 'shelf': return scrShelf();
      case 'cmp':  return scrCmp();
      case 'asst': return scrAsst();
      case 'you':  return scrYou();
      case 'site': return scrSite(p[1]);
      default:     return scrLost();
    }
  }
  function route() {
    /* A sheet is not a screen. If one is open when history moves, it takes the
       move — that is what the back gesture means while a sheet is up, on both
       platforms. */
    if (ASK.on) { askClose(); }
    else {
      var osh = el.root && el.root.querySelector('[data-ask-sheet]');
      if (osh && !osh.hidden) { filtClose(); }
    }
    hush(); if (VOICE.on) { voiceStop(); }
    var p = (location.hash || '#/shop').replace(/^#\/?/, '').split('/');
    var d = DEPTH[p[0]] == null ? 1 : DEPTH[p[0]];
    var how = lastDepth < 0 ? 'replace'
            : d > lastDepth ? 'push' : (d < lastDepth ? 'pop' : 'replace');
    lastDepth = d;
    var spec = screenFor(p);
    spec.depth = d;
    mount(spec, how);
    shelfBadge();
  }
  function go(h) { location.hash = h; }

  /* ══════════════════════════════════════════════════════ what a tap does ══
     One delegated listener on the root rather than a listener per element, so
     re-rendering a screen never leaks handlers and a long session does not
     slowly accumulate them. */
  function wire() {
    /* What a tap opened a panel from, to give focus back to on closing:
       Safari, and VoiceOver's double tap, activate a button without
       focusing it. Kept only for the tap's own turn. */
    el.root.addEventListener('click', function (e) {
      SHEET.tap = e.target && e.target.closest ? e.target.closest('a, button, input, select, textarea') : null;
      setTimeout(function () { SHEET.tap = null; }, 0);
    }, true);
    el.root.addEventListener('click', function (e) {
      var t = e.target, hit;

      if (sheetTabTap(t, e)) { return; }

      hit = t.closest('[data-back]');
      if (hit) { e.preventDefault(); history.back(); return; }

      hit = t.closest('[data-keep]');
      if (hit) {
        e.preventDefault();
        var parts = hit.getAttribute('data-keep').split('|');
        var rn = roomNow(), kU = undoLine(parts[0], parts[1]);
        var added = toggleShelf(parts[0], parts[1], rn ? rn.id : '');
        if (!added && kU) { undoSet(kU); }
        shelfBadge(); tap(added ? 10 : 6);
        hit.textContent = added ? 'On your shelf'
          : (rn ? 'Put aside for ' + (rn.name || 'this room') : 'Put aside');
        toast(added ? (rn ? 'Put aside for ' + (rn.name || 'this room') : 'Put aside')
                    : 'Taken off the shelf. Undo is at the top of the screen');
        /* The stepper only exists once there is something to step. Re-render
           the screen rather than hand-patching the DOM around the button. */
        mount(screenFor((location.hash || '').replace(/^#\/?/, '').split('/')), 'replace');
        return;
      }

      hit = t.closest('[data-sl-q]') || t.closest('[data-sl-drop]');
      if (hit) {
        e.preventDefault();
        var sDrop = hit.hasAttribute('data-sl-drop');
        var sAt = sDrop ? shelfFind(hit.closest('[data-sl]')) : null;
        var sU = sAt ? undoTake('line', sAt.x, sAt.i) : null;
        var sDid = shelfAct(hit.closest('[data-sl]'), sDrop ? 'drop' : 'q', hit.getAttribute('data-sl-q'));
        if (sDid && sU) { undoSet(sU); }
        if (sDid) { if (sDrop) { shelfBadge(); } else { tap(5); } }
        shelfRedraw();
        if (!sDid) { toast('Your list had changed, so nothing was edited'); }
        else if (sDrop) { heldToast('Taken off the shelf. Undo is at the top of the screen'); }
        return;
      }

      hit = t.closest('[data-drop]');
      if (hit) {
        e.preventDefault();
        var dp = hit.getAttribute('data-drop').split('|');
        var dU = undoLine(dp[0], dp[1]);
        if (!toggleShelf(dp[0], dp[1]) && dU) { undoSet(dU); }
        shelfBadge();
        mount(scrShelf(), 'replace');
        heldToast('Taken off the shelf');
        return;
      }

      hit = t.closest('[data-shelf-clear]');
      if (hit) {
        e.preventDefault();
        var cU = undoTake('clear');
        put({ shelf: [], cmp: [] });
        undoSet(cU);
        shelfBadge();
        mount(scrShelf(), 'replace');
        heldToast('Shelf cleared. Undo is at the top of the screen');
        return;
      }

      /* Put back the last thing taken off, only over what it left. */
      hit = t.closest('[data-undo-go]');
      if (hit) {
        e.preventDefault();
        var uDid = undoRun();
        shelfBadge();
        if (spotKey() === '#/shelf') { shelfRedraw(); }
        else { mount(screenFor((location.hash || '').replace(/^#\/?/, '').split('/')), 'replace'); }
        undoFocus(uDid);
        return;
      }

      hit = t.closest('[data-cmp-drop]');
      if (hit) {
        e.preventDefault();
        var cp = hit.getAttribute('data-cmp-drop').split('|');
        toggleCmp(cp[0], cp[1]);
        mount(scrCmp(), 'replace');
        return;
      }

      hit = t.closest('[data-cmp-clear]');
      if (hit) {
        e.preventDefault();
        put({ cmp: [] });
        mount(scrCmp(), 'replace');
        return;
      }

      hit = t.closest('[data-store-info]');
      if (hit) {
        e.preventDefault();
        toast(storeLine());
        return;
      }

      hit = t.closest('[data-wipe]');
      if (hit) {
        e.preventDefault();
        /* Two taps, because it is not undoable and there is no copy elsewhere.
           The second tap is the same button saying what it will do. */
        if (hit.getAttribute('data-armed')) {
          /* Erased is said only when the removal succeeded (30 Oct 2026). */
          if (!eraseSaved()) {
            mount(scrYou(), 'replace');
            toast('This browser didn’t let the app erase it, so it may still be on this device.');
            return;
          }
          FILT = { cat: null, sel: {}, fit: false, sort: '' };
          shelfBadge();
          mount(scrYou(), 'replace');
          toast('Erased from this device');
          return;
        }
        hit.setAttribute('data-armed', '1');
        hit.classList.add('is-armed');
        hit.querySelector('b').textContent = 'Tap again to erase it';
        setTimeout(function () {
          if (!hit.parentNode) { return; }
          hit.removeAttribute('data-armed');
          hit.classList.remove('is-armed');
          var b = hit.querySelector('b');
          if (b) { b.textContent = 'Erase everything on this device'; }
        }, 4000);
        return;
      }

      hit = t.closest('[data-jump-to]');
      if (hit) {
        e.preventDefault();
        var scr = stack[stack.length - 1];
        var body = scr && scr.querySelector('[data-body]');
        if (!body) { return; }
        var id = hit.getAttribute('data-jump-to');
        if (id === 'top') { body.scrollTo({ top: 0, behavior: 'smooth' }); }
        else {
          var tgt = body.querySelector('#' + id);
          if (tgt) {
            /* Offset by the bar and the jump strip, or the heading you asked
               for lands underneath the thing you tapped to get there. */
            body.scrollTo({ top: Math.max(0, tgt.offsetTop - 18), behavior: 'smooth' });
          }
        }
        tap(5);
        return;
      }

      hit = t.closest('[data-ask-here]');
      if (hit) {
        e.preventDefault();
        askOpen(hit.getAttribute('data-ask-here') || '');
        return;
      }

      hit = t.closest('[data-f-fit]');
      if (hit) {
        e.preventDefault(); FILT.fit = !FILT.fit; tap(6);
        mount(scrCategory(FILT.cat), 'replace'); return;
      }

      hit = t.closest('[data-f-sort]');
      if (hit) {
        e.preventDefault();
        FILT.sort = FILT.sort === '' ? 'up' : FILT.sort === 'up' ? 'down' : '';
        tap(6);
        mount(scrCategory(FILT.cat), 'replace'); return;
      }

      hit = t.closest('[data-f-open]');
      if (hit) { e.preventDefault(); filtSheet(hit.getAttribute('data-f-open')); return; }

      hit = t.closest('[data-f-pick]');
      if (hit) {
        e.preventDefault();
        var fp = hit.getAttribute('data-f-pick').split('|');
        FILT.sel[fp[0]] = fp[1] || '';
        tap(6);
        filtClose();
        mount(scrCategory(FILT.cat), 'replace');
        return;
      }

      hit = t.closest('[data-filt-close]');
      if (hit) { e.preventDefault(); filtClose(); return; }

      hit = t.closest('[data-f-clear]');
      if (hit) {
        e.preventDefault();
        FILT.sel = {}; FILT.fit = false; FILT.sort = '';
        mount(scrCategory(FILT.cat), 'replace'); return;
      }

      hit = t.closest('[data-seen-clear]');
      if (hit) {
        e.preventDefault();
        put({ seen: [] });
        mount(screenFor((location.hash || '').replace(/^#\/?/, '').split('/')), 'replace');
        heldToast('Cleared');
        return;
      }

      hit = t.closest('[data-asst-open]');
      if (hit) { e.preventDefault(); askOpen(''); return; }

      hit = t.closest('[data-sheet-close]');
      if (hit) { e.preventDefault(); askClose(); return; }

      hit = t.closest('[data-find-open]');
      if (hit) { e.preventDefault(); go('#/find'); return; }

      hit = t.closest('[data-find-clear]');
      if (hit) {
        e.preventDefault();
        var fin = el.root.querySelector('[data-find-in]');
        if (fin) { fin.value = ''; fin.focus(); }
        hit.hidden = true;
        findRender('');
        return;
      }

      hit = t.closest('[data-find-set]');
      if (hit) {
        e.preventDefault();
        var q1 = hit.getAttribute('data-find-set');
        var fin2 = el.root.querySelector('[data-find-in]');
        if (fin2) { fin2.value = q1; }
        var x1 = el.root.querySelector('[data-find-clear]');
        if (x1) { x1.hidden = false; }
        findRender(q1);
        return;
      }

      hit = t.closest('[data-find-drop]');
      if (hit) {
        e.preventDefault();
        var dq = hit.getAttribute('data-find-drop');
        put({ finds: recentFinds().filter(function (x) { return x !== dq; }) });
        var fi = el.root.querySelector('[data-find-in]');
        findRender(fi ? fi.value : '');
        return;
      }

      hit = t.closest('[data-finds-clear]');
      if (hit) {
        e.preventDefault();
        put({ finds: [] });
        findRender('');
        heldToast('Recent searches cleared');
        return;
      }

      hit = t.closest('[data-find-ask]');
      if (hit) {
        e.preventDefault();
        var qa = hit.getAttribute('data-find-ask');
        rememberFind(qa);
        askOpen(qa);
        return;
      }

      hit = t.closest('[data-find-voice]');
      if (hit) {
        e.preventDefault();
        /* From the field, dictation is a SEARCH, not a question — it fills the
           box and lets the person see what was heard before anything happens. */
        if (!el.root.querySelector('[data-find-in]')) { go('#/find'); }
        setTimeout(function () { voiceToggle('find'); }, 260);
        return;
      }

      hit = t.closest('[data-q]');
      if (hit) {
        e.preventDefault();
        var qp = hit.getAttribute('data-q').split('|');
        var cur = shelfAt(qp[0], qp[1]);
        var nq = Math.max(1, Math.min(99, qtyOf(cur) + Number(qp[2])));
        shelfSet(qp[0], qp[1], { q: nq }); tap(5);
        var outq = el.root.querySelector('[data-q-out]');
        if (outq) { outq.textContent = String(nq); }
        return;
      }

      hit = t.closest('[data-sheet-copy]');
      if (hit) {
        e.preventDefault();
        copySheet(hit.getAttribute('data-sheet-copy'));
        return;
      }

      hit = t.closest('[data-sheet-save]');
      if (hit) {
        e.preventDefault();
        saveSheet(hit.getAttribute('data-sheet-save'));
        return;
      }

      hit = t.closest('[data-sheet-print]');
      if (hit) {
        e.preventDefault();
        printSheet(hit.getAttribute('data-sheet-print'));
        return;
      }

      hit = t.closest('[data-jobsheet-done]');
      if (hit) { e.preventDefault(); sheetBoxClose(true); return; }

      hit = t.closest('[data-room-new]');
      if (hit) { e.preventDefault(); go('#/room/new'); return; }

      hit = t.closest('[data-room-edit]');
      if (hit) { e.preventDefault(); go('#/room/' + hit.getAttribute('data-room-edit')); return; }

      hit = t.closest('[data-room-open]');
      if (hit) { e.preventDefault(); put({ active: hit.getAttribute('data-room-open') }); go('#/fit'); return; }

      hit = t.closest('[data-room-pick]');
      if (hit) {
        e.preventDefault();
        put({ active: hit.getAttribute('data-room-pick') });
        mount(scrFit(), 'replace');
        return;
      }

      hit = t.closest('[data-room-drop]');
      if (hit) {
        e.preventDefault();
        dropRoom(hit.getAttribute('data-room-drop'));
        heldToast('Room removed');
        history.back();
        return;
      }

      hit = t.closest('[data-ask]');
      if (hit) { e.preventDefault(); moAsk(hit.getAttribute('data-ask')); return; }

      hit = t.closest('[data-mo-send]');
      if (hit) {
        e.preventDefault();
        var box = el.root.querySelector('[data-mo-in]');
        if (box) { var v = box.value; box.value = ''; box.style.height = ''; moAsk(v); }
        return;
      }

      hit = t.closest('[data-mo-clear]');
      if (hit) {
        e.preventDefault(); hush(); voiceStop();
        put({ chat: [] }); moRender(); return;
      }

      hit = t.closest('[data-install]');
      if (hit) {
        e.preventDefault();
        var ev = INSTALL.evt;
        if (!ev) { toast('Not available on this browser'); return; }
        INSTALL.evt = null;
        ev.prompt();
        ev.userChoice.then(function (c) {
          if (c && c.outcome === 'accepted') { put({ hideInstall: 1 }); }
          mount(scrYou(), 'replace');
        }).catch(function () {});
        return;
      }

      hit = t.closest('[data-install-no]');
      if (hit) {
        e.preventDefault();
        put({ hideInstall: 1 });
        mount(scrYou(), 'replace');
        return;
      }

      hit = t.closest('[data-mic]');
      if (hit) { e.preventDefault(); hush(); voiceToggle('ask'); return; }

      hit = t.closest('[data-speak]');
      if (hit) {
        e.preventDefault();
        var nowOn = !speakBack();
        put({ speak: nowOn });
        if (!nowOn) { hush(); }
        hit.setAttribute('aria-pressed', nowOn ? 'true' : 'false');
        hit.classList.toggle('on', nowOn);
        toast(nowOn ? 'She will read answers back' : 'She will stay quiet');
        return;
      }
    });

    el.root.addEventListener('change', function (e) {
      var sRoom = e.target.closest('[data-sl-room]'), sCmp = e.target.closest('[data-sl-cmp]');
      if (sRoom || sCmp) {
        var sRow = (sRoom || sCmp).closest('[data-sl]'), sAt = shelfFind(sRow);
        if (sRoom) {
          sAt = shelfAct(sRow, 'rm', sRoom.value);
          shelfRedraw();
          if (!sAt) { toast('Your list had changed, so nothing was edited'); return; }
          var sR = null;
          rooms().forEach(function (y) { if (y.id === roomOf(sAt.x)) { sR = y; } });
          toast(sR ? ('Specified for ' + (sR.name || 'that room')) : 'Left unassigned');
          return;
        }
        if (!sAt || !sAt.x || !itemOf(sAt.x)) {
          shelfRedraw(); toast('Your list had changed, so nothing was edited'); return;
        }
        var sAdded = toggleCmp(sAt.x.c, sAt.x.m);
        var sN = cmp().filter(function (y) { return !!itemOf(y); }).length;
        if (sAdded && sN > 1) { toast(sN + ' to compare'); }
        shelfRedraw();
        return;
      }
      var box = e.target.closest('[data-cmp]');
      if (box) {
        var cb = box.getAttribute('data-cmp').split('|');
        var added = toggleCmp(cb[0], cb[1]);
        box.checked = onCmp(cb[0], cb[1]);
        var n = cmp().length;
        if (added && n > 1) { toast(n + ' to compare'); }
        /* The Compare button at the foot of the shelf only exists once there
           are two, so the screen has to be redrawn when it crosses that line. */
        if (n === 2 || n === 1) {
          var top = stack[stack.length - 1];
          if (top && /shelf/.test(location.hash)) {
            var y = (top.querySelector('[data-body]') || {}).scrollTop || 0;
            mount(scrShelf(), 'replace');
            var nb = (stack[stack.length - 1] || {}).querySelector
              ? stack[stack.length - 1].querySelector('[data-body]') : null;
            if (nb) { nb.scrollTop = y; }
          }
        }
        return;
      }
      var sel = e.target.closest('[data-assign]');
      if (!sel) { return; }
      var ap = sel.getAttribute('data-assign').split('|');
      shelfSet(ap[0], ap[1], { rm: sel.value });
      var rr = null;
      rooms().forEach(function (x) { if (x.id === sel.value) { rr = x; } });
      toast(rr ? ('Specified for ' + (rr.name || 'that room')) : 'Left unassigned');
    });

    /* The composer grows with what is typed, up to a point, and the send button
       only lights when there is something to send. */
    el.root.addEventListener('input', function (e) {
      var fin = e.target.closest('[data-find-in]');
      if (fin) {
        var x = el.root.querySelector('[data-find-clear]');
        if (x) { x.hidden = !fin.value; }
        /* 90ms. Long enough that a fast typist is not ranking 765 models on
           every keystroke, short enough that it never feels like waiting. */
        clearTimeout(FT);
        var v = fin.value;
        FT = setTimeout(function () {
          findReady().then(function () { findRender(v); });
        }, 90);
        return;
      }
      var box = e.target.closest('[data-mo-in]');
      if (!box) { return; }
      box.style.height = 'auto';
      box.style.height = Math.min(120, box.scrollHeight) + 'px';
      var send = el.root.querySelector('[data-mo-send]');
      if (send) { send.disabled = !box.value.trim(); }
    });

    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') { return; }
      if (ASK.on) { e.preventDefault(); askClose(); }
      else if (SHEET.open) { e.preventDefault(); filtClose(); }
      else if (sheetBoxClose(true)) { e.preventDefault(); }
    });
    doc.addEventListener('keydown', sheetTab);

    el.root.addEventListener('keydown', function (e) {
      var fin = e.target.closest && e.target.closest('[data-find-in]');
      if (!fin || e.key !== 'Enter') { return; }
      e.preventDefault();
      var q = fin.value.trim();
      if (!q) { return; }
      rememberFind(q);
      /* Enter on something that reads as a question goes straight to her. That
         is the whole promise of one field that says "search, or ask". */
      if (looksAsked(q)) { fin.blur(); askOpen(q); return; }
      fin.blur();
      findReady().then(function () { findRender(q); });
    });

    /* Enter sends, shift-enter makes a line. On a phone the on-screen keyboard
       sends too, which is what people expect from every other app they use. */
    el.root.addEventListener('keydown', function (e) {
      var box = e.target.closest('[data-mo-in]');
      if (!box || e.key !== 'Enter' || e.shiftKey) { return; }
      e.preventDefault();
      var v = box.value; box.value = ''; box.style.height = '';
      var send = el.root.querySelector('[data-mo-send]');
      if (send) { send.disabled = true; }
      moAsk(v);
    });

    /* Saving a room. The figures are read with the site's own parser, so
       "8 ft 6", "8'6" and "102 in" all land on the same number. */
    el.root.addEventListener('submit', function (e) {
      var f = e.target.closest('[data-room-form]');
      if (!f) { return; }
      e.preventDefault();
      var v = {};
      Array.prototype.forEach.call(f.elements, function (x) { if (x.name) { v[x.name] = x.value; } });
      /* A slab that cannot be read stops the save and says so, rather than
         saving the room without it. */
      var was = rooms().filter(function (x) { return x.id === v.id; })[0];
      var sl = roomSlab(v.slab, was);
      if (sl.unread) {
        toast('The slab was not read. Type one thickness, like 4 in.');
        if (f.elements.slab && f.elements.slab.focus) { f.elements.slab.focus(); }
        return;
      }
      var r = roomFromForm(v, was, sl);
      saveRoom(r); tap(12);
      toast(HOLD ? 'Room kept on this page only, not saved on this device' : 'Room saved');
      go('#/fit');
    });

    /* Type "8 ft 6" and it says 8 ft 6 back to you before you leave the field.
       Reading a figure wrong is the one failure this app cannot afford, so it
       shows its working rather than asking to be trusted. */
    el.root.addEventListener('input', function (e) {
      var i = e.target.closest('[data-read]');
      if (!i) { return; }
      var out = i.parentNode.querySelector('[data-read-out]');
      if (!out) { return; }
      var n = i.getAttribute('name');
      if (n === 'name' || n === 'supply') { return; }
      var val = n === 'slab' ? readThick(i.value) : readLen(i.value);
      if (val == null && n === 'slab' && String(i.value).trim()) {
        out.textContent = 'Not read: type one thickness, like 4 in'; out.classList.add('on'); return;
      }
      if (val == null) { out.classList.remove('on'); out.textContent = ''; return; }
      out.textContent = n === 'ceiling' ? ('Read as ' + feet(val)) : ('Read as ' + val + ' in');
      out.classList.add('on');
    });
  }

  /* ══════════════════════════════════════════════════════════════════ boot ══ */
  function boot() {
    el.root = doc.getElementById('vba');
    if (!el.root) { return; }
    /* Before anything is drawn, so no screen is ever painted in one register
       and repainted in another. */
    el.root.setAttribute('data-acct', acct());
    el.root.innerHTML = chrome();
    store();
    heldSync();
    el.stack = el.root.querySelector('[data-stack]');
    el.toast = el.root.querySelector('[data-toast]');
    wire();
    root.addEventListener('hashchange', route);
    root.addEventListener('storage', undoWatch);
    route();

    var src = cfg('catalogue');
    if (!src) { return; }

    function take(j, why) {
      if (!j || !j.cats) { return false; }
      /* Do not repaint over a screen the person is already reading just because
         a background refresh came back identical. */
      if (CAT && CAT.v && j.v && CAT.v === j.v) { return false; }
      /* A new catalogue retires the index searched against the old one. */
      if (IX && IX.v !== j.v) { IX = null; IXWARM = false; }
      CAT = j; BYID = {};
      (j.cats || []).forEach(function (c) { BYID[c.id] = c; });
      A.CAT = CAT; A.BYID = BYID;
      if (why === 'kept' || !stack.length || stack.length === 1) {
        var seg = (location.hash || '#/shop').replace(/^#\/?/, '').split('/');
        var sp = screenFor(seg);
        sp.depth = DEPTH[seg[0]] == null ? 1 : DEPTH[seg[0]];
        mount(sp, 'replace');
      }
      return true;
    }

    /* Kept copy first — it paints in a few milliseconds and needs no network.
       The network then quietly confirms or replaces it. This is the reason the
       second open of the app feels instant and the first does not. */
    var painted = false;
    var fromVault = VAULT.get('catalogue').then(function (j) {
      if (j && !painted) { painted = take(j, 'kept'); }
    });

    fromVault.then(function () {
      return fetch(src, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) { throw new Error('cat ' + r.status); }
        return r.json();
      }).then(function (j) {
        setLive(true);
        painted = take(j, 'fresh') || painted;
        VAULT.put('catalogue', j, 0);
        /* Only now is there a catalogue to warm from. Doing this beside the
           idle callback below meant CAT was still null and the whole pass was
           a no-op — which the offline screenshot showed and the code did not. */
        idleRun(warmTiles);
      }).catch(function () {
        setLive(false);
        if (painted) { return; }
        mount({ tab: 'shop', title: '', html:
          '<div class="va-empty"><svg viewBox="0 0 24 24"><path d="M3 3l18 18M10.6 5.3A9 9 0 0 1 21 12"/>' +
          '<path d="M3 12a9 9 0 0 0 9 9"/></svg><h1 class="va-h1">Nothing on this device yet.</h1>' +
          '<p class="va-p">The catalogue has not reached this phone once, so there is nothing ' +
          'to work from offline. Open it on a signal once and it will not need one again.</p></div>'
        }, 'replace');
      });
    });

    shelfBadge();

    /* Pictures already seen, restored before anything asks for one. */
    shotsLoad();

    /* Her knowledge is a third of a megabyte. Pull it down quietly once,
       while there is a signal, so that the first time she is asked something
       in a basement she can answer it. Idle time only — it must never compete
       with the first paint. */
    idleRun(function () {
      VAULT.get('knowledge').then(function (j) {
        if (!j && LIVE) { keepKnowledgeFresh(cfg('knowledge')); }
      });
      /* Pull and stem the search index before anyone taps the field, so the
         first search is as fast as the tenth — and so it is on the device
         before the signal goes. */
      findReady();
    });

    wireInstall();
    wireKeyboard();
    root.addEventListener('online', function () { setLive(true); });
    root.addEventListener('offline', function () { setLive(false); });
    if (root.navigator && root.navigator.onLine === false) { setLive(false); }
  }

  A.boot = boot; A.route = route; A.esc = esc; A.money = money; A.asset = asset;
  A.fitRows = fitRows; A.verdict = verdict; A.readLen = readLen; A.feet = feet;
  A.store = store; A.rooms = rooms; A.roomNow = roomNow; A.saveRoom = saveRoom;
  A.shelf = shelf; A.toggleShelf = toggleShelf; A.go = go;
  A.siteNames = siteNames; A.roomsAt = roomsAt; A.specIn = specIn; A.sheetText = sheetText;
  root.VB_APP = A;

  if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }

})(typeof window !== 'undefined' ? window : globalThis,
   typeof document !== 'undefined' ? document : null);
